/**
 * Survey Session component for respondents taking surveys
 * Handles one-question-per-screen conversational flow
 */

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowRight, Check, MessageSquare, AlertCircle, RotateCcw } from 'lucide-react';
import Button from '../../components/Button';
import SurveyWelcomeScreen from './SurveyWelcomeScreen';
import { RESPONDENT_ID_PARAM, resolveRespondentId, shouldMintRespondentId } from './respondentId';
import ConversationalSurveyFlow from './ConversationalSurveyFlow';
import SurveyConsentScreen from '../../components/survey/SurveyConsentScreen';
import { SurveyTermination } from '../../components/survey/SurveyTermination';
import { SurveyAccessGate } from '../../components/survey/SurveyAccessGate';
import { TurnstileChallenge } from '../../components/survey/TurnstileChallenge';
import { collectClientSignals } from '../../utils/botDetection';
import ImageHotspotResponse from '../../components/survey/ImageHotspotResponse';
import InfoScreenDisplay from '../../components/survey/InfoScreenDisplay';
import SurveySectionStimulus from '../../components/survey/SurveySectionStimulus';
import PhotoUploadQuestion from '../../components/survey/PhotoUploadQuestion';
import QuestionText from '../../components/common/QuestionText';
import { HTTP_STATUS } from '../../config/api';
import {
  getPublicSurvey,
  startSurveySession,
  submitResponse,
  submitFollowupResponse,
  completeSurveySession,
  analyzeResponseForFollowup,
  shareIdToSurveyId,
  sendSessionHeartbeat,
  sendCompletionBeacon,
  getSessionById,
  startRapidFireBlock,
  closeRapidFireBlock,
  SurveyRequestError,
} from '../../utils/surveyApi';
import { SurveyFinishing } from './SurveyFinishing';
import RapidFireIntro from './RapidFireIntro';
import RapidFireBeat from './RapidFireBeat';
import RapidFireOutro from './RapidFireOutro';
import RapidFireTimer from './RapidFireTimer';
import { RapidFireVoiceRunner } from './RapidFireVoiceRunner';
import { transcribeRapidFireClip } from './rapidFireTranscribe';
import SurveyCountdown from './SurveyCountdown';
import TimerNudge from './TimerNudge';
import { useSurveyDeadline } from './useSurveyDeadline';
import {
  BEFORE_HARD_STOP_TIMEOUT_MS,
  isCreditedCompletion,
  isNudgeUrgent,
  isTimerExpiredWrite,
  nudgeSlotAvailable,
  timedOutCompletionPath,
  timerConfigFrom,
} from './surveyTimer';
import {
  rapidFireConfigFor,
  blockDisplayRange,
  remainingSeconds,
  shouldCloseRapidFire,
  answeredCountInBlock,
  unansweredAtCloseCount,
  rapidFireIntroText,
  rapidFireRules,
  silenceAdvanceMsFor,
  shouldFinishTheQuestionOnScreen,
  RAPID_FIRE_DEFAULT_DURATION_SECONDS,
  type RapidFireOverrun,
} from './rapidFire';
import { computeSurveyProgress, type SessionResponse } from './surveyProgress';
import {
  CHOICE_OPTION_MEDIA_KEY_PREFIX,
  MAX_DIFF_ITEM_MEDIA_KEY_PREFIX,
  keepMarksForQuestion,
} from './optionMediaMarks';
import { UI_TEXT, getRandomAnalyzingMessage } from '../../config/ui-text';
import { useDelayedFlag } from './useDelayedFlag';
import { useActiveQuestionTime } from './useActiveQuestionTime';

// How long a submit must be in flight before the "thinking…" overlay appears (fast submits skip it).
const THINKING_DELAY_MS = 450;
// `media_display_mode` default: media_urls render below the question text, not beside each choice.
const DEFAULT_MEDIA_DISPLAY_MODE = 'question';
// Types whose answer inputs sit INSIDE the media-gated <fieldset> (the two choice render sites
// below). Extend this in the same commit that gates another type — the answer clock waits for
// exactly the gates that lock these inputs, so the two must never drift (frontend#632).
const ANSWER_FIELDSET_TYPES = ['single_choice', 'multiple_choice'];
// How often the hard stop looks to see whether the answer already in flight has landed.
const SUBMIT_SETTLE_POLL_MS = 50;
// The style that hands the entire respondent body to the voice-moderator flow. Such a survey
// legitimately carries NO authored questions, so nothing about it can be keyed on having one.
const CONVERSATIONAL_SURVEY_STYLE = 'conversational';
import { ASYNC_ANALYSIS_CONFIG, ANALYSIS_TASK_STATES, VOICE_CAPTURE } from '../../config/constants';
import { generateAndPrepareSetsForUser, evaluateMaxDiffDesign } from './maxDiffUtils';
import { isBestWorstQuestion, parseBestWorstAnswer, resolveAnchors } from './bestWorst';
import {
  buildBestWorstFollowupContext,
  buildMaxDiffFollowupContext,
  chosenMaxDiffOptions,
} from './followupContext';
import { BestWorstCard } from './BestWorstCard';
import { isPhotoUploadResponseValid } from './photoUploadValidation';
import { makeAnswerSlotKey } from './answerSlot';
import { isValidAnswerFor, describeAnswerMismatch } from './answerTypes';
import { AiDynamicSectionRunner } from './AiDynamicSectionRunner';
import { contextTranscriptsOutstanding, dynamicSectionConfigFromRow } from './aiDynamicSection';
import { SurveyAnalyzingIndicator } from './SurveyAnalyzingIndicator';
import type { AiDynamicSectionQuestionWithVoiceFlags } from './AiDynamicSectionRunner';
import { VoiceOrTextInput } from './VoiceOrTextInput';
import {
  SELECTION_SHAPE,
  SELECTION_CAP_MESSAGE,
  SELECTION_CAP_REJECTION_CODE,
  selectionCap,
  isAddBlocked,
  isChoiceSelected,
  isChoiceLocked,
  rendersImageTiles,
  toggleMultiSelect,
  isWithinSelectionCap,
  selectionCounterText,
  questionTextWithCap,
} from './multiSelect';
import { useStimulusReady, isVideoUrl, STIMULUS_LOAD_TIMEOUT_MS } from './useStimulusReady';

const API_URL = import.meta.env.VITE_API_URL || '';

/** `?startFrom=N` deep-link index; anything unparseable, negative or absent means "the start". */
const parseStartFromIndex = (raw: string | null): number => {
  const parsed = raw === null ? NaN : Number.parseInt(raw, 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
};

/**
 * `?session_id=N` — the session a verified respondent's LinkedIn callback already created for them
 * (BE #840). Anything absent, unparseable or not a positive id means the URL named no session.
 */
const RESUME_SESSION_ID_PARAM = 'session_id';
const parseResumeSessionId = (raw: string | null): number | null => {
  const parsed = raw === null ? NaN : Number.parseInt(raw, 10);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
};

const clampQuestionIndex = (index: number, questionCount: number): number =>
  Math.min(Math.max(index, 0), Math.max(questionCount - 1, 0));

// Helper to build full URL for media (handles both uploaded files and external URLs)
const getFullMediaUrl = (url: string): string => {
  if (!url) return '';
  if (url.startsWith('http')) return url;
  if (url.startsWith('/api/')) return `${API_URL}${url}`;
  return url;
};

/**
 * Option-media image styling. Images render at their natural aspect ratio (object-contain,
 * never cropped) inside a self-sizing container capped to an adequate size — not a forced
 * aspect-ratio box that would crop the artwork.
 */
const OPTION_MEDIA_CONTAINER_CLASS =
  'relative rounded-lg overflow-hidden border border-gray-200 bg-gray-100 flex items-center justify-center min-h-[8rem]';
const OPTION_MEDIA_IMG_CLASS = 'w-full h-auto max-h-64 object-contain';

/**
 * THE PAINT-SLOT MODEL FOR MAXDIFF ITEM IMAGES.
 *
 * A paint slot is exactly ONE <img> DOM node, and every flag the gate and the tile display read —
 * "has this settled", "did it produce artwork" — is a fact about a node, never about an item. Four
 * coordinates name a slot, and the key must carry all four:
 *
 *   1. QUESTION — every MaxDiff question restarts its sets at 0 and indexes its own claims from 0,
 *      so without it q2's keys are q1's keys character for character.
 *   2. SET — a balanced design shows a claim in several of its sets, and each set mounts its own
 *      <img> and issues its own request.
 *   3. ITEM (index in `choices`, not display position) — tiles are keyed by position, so a slot
 *      identified by position follows the tile to the next item.
 *   4. COLUMN — the SAME claim is rendered in both columns, so one item owns TWO nodes making two
 *      independent requests that can settle differently: a 404 in one column and a cache hit or a
 *      successful retry in the other.
 *
 * (1)-(3) came first; (4) is what was missing, and it is the last one needed — `renderMaxDiffTile`
 * runs exactly once per (column, position), so (question, set, item, column) names one node and no
 * node-identity component is required.
 *
 * WHO MAY WRITE WHAT. Only the three writers bound to a node may write that node's slot: its `ref`
 * (which re-runs on every render — the arrow is inline, so React detaches and re-attaches it), its
 * `onLoad` and its `onError`. `optionMediaLoaded[slot]` latches once the fetch settles either way;
 * `optionMediaErrored[slot]` is raised by the broken branch of the ref and by `onError`, and
 * retracted by the painted branch and by `onLoad`. The retraction is the one non-idempotent writer
 * here, which is exactly why the column has to be in the key: while two nodes shared one key, a
 * broken node and a decoded node wrote opposite values through it on every render, each producing a
 * fresh state object, and React threw `Maximum update depth exceeded` — the survey page died
 * mid-response. Keyed per node the two directions can never contend.
 *
 * The namespace prefix keeps these apart from the choice paths' keys, which share the same tracker
 * and are built the same way (see CHOICE_OPTION_MEDIA_KEY_PREFIX). Both namespaces are declared in
 * `optionMediaMarks` and imported here, because the tracker's BOUND anchors on them — see
 * `isMarkOfQuestion` for what a second copy of either string would silently cost.
 */

/** The two MaxDiff columns differ only in colour; the tile itself is one piece of markup. */
const MAX_DIFF_COLUMN_STYLES = {
  most: {
    selected: 'border-green-500 bg-green-100 text-green-900',
    hover: 'hover:border-green-400 hover:bg-green-50',
    check: 'bg-green-500',
  },
  least: {
    selected: 'border-red-500 bg-red-100 text-red-900',
    hover: 'hover:border-red-400 hover:bg-red-50',
    check: 'bg-red-500',
  },
} as const;

type MaxDiffColumn = keyof typeof MAX_DIFF_COLUMN_STYLES;

/** Both columns of every set are rendered at once, so both are gated together. */
const MAX_DIFF_COLUMNS = Object.keys(MAX_DIFF_COLUMN_STYLES) as MaxDiffColumn[];

/** The paint-slot key for one tile <img>. See the model above for why each part is in it. */
const maxDiffItemMediaKey = (scopeKey: string, itemIndex: number, column: MaxDiffColumn): string =>
  `${MAX_DIFF_ITEM_MEDIA_KEY_PREFIX}${scopeKey}-${itemIndex}-${column}`;

/**
 * The same model for the single_choice / multiple_choice option images, which share the tracker.
 *
 * Their coordinates used to be `${optionIndex}-${mediaIndex}` alone — a name that repeats on every
 * question, while their gate counts the WHOLE tracker. So the frame in which a new question first
 * paints compared its own (empty) artwork against the previous question's satisfied count and
 * rendered the buttons live; they submit on click. The scope is the same one the MaxDiff tiles and
 * the fail-open backstop derive, so all three move in the commit the question moves in.
 */

/** Every choice key of one scope starts with this, which is what makes the count scopeable. */
const choiceOptionMediaScopePrefix = (scopeKey: string): string =>
  `${CHOICE_OPTION_MEDIA_KEY_PREFIX}${scopeKey}-`;

const choiceOptionMediaKey = (scopeKey: string, optionIndex: number, mediaIndex: number): string =>
  `${choiceOptionMediaScopePrefix(scopeKey)}${optionIndex}-${mediaIndex}`;

/**
 * WHERE the respondent is, and WHICH TIME THIS IS.
 *
 * Everything per-question in this component used to be keyed on the question alone, which is only
 * an identity while navigation is forward-only. It is not: `SurveyQuestionsPreview`'s skip-target
 * `<select>` offers EVERY question, `conditional_logic_service.get_next_question_index` returns the
 * target's index with no forward-only clamp, and the runner below applies whatever index comes back
 * verbatim. So a choice authored `skip_to` an EARLIER question sends the respondent back into a
 * question they have already answered — and a key that says only "q3" cannot tell that visit from
 * the first one. The set cursor resumed on the final set of an eight-set MaxDiff (one more answer
 * and the question ended, seven sets never re-shown), and the fail-open scope that had timed out
 * during the first visit was still latched, so the tiles were live before any artwork had painted.
 *
 * The answer clock is the third reader (frontend#425). `next_question_index` is a CANONICAL index
 * applied as a DISPLAY one, so a jump can even resolve to the position it started from; every other
 * term of `presentedQuestionKey` is then identical across it, and the once-per-unit stamp guard read
 * the second presentation as the first one still being on screen — so the re-answer was billed the
 * first answer's dwell plus everything in between.
 *
 * The visit count therefore lives in the SAME state as the index, so it is impossible to move
 * without it changing and it lands in the same commit the new question first paints in — the
 * distinction rounds 5-8 kept having to make about effects. Nothing else may set the index.
 */
type QuestionVisit = { index: number; visit: number };

/** The identity of "this question, this time" — the scope everything per-question hangs off. */
const makeQuestionVisitKey = (
  questionId: string | number | null | undefined,
  visit: number,
): string => `${questionId ?? ''}@${visit}`;

/**
 * How far through a MaxDiff question the respondent is, carrying the VISIT it belongs to.
 *
 * Scoped, not reset — the same shape (and the same reason) as the media paint keys above. The set
 * index is per-visit state, and the only thing that used to return it to 0 was the question-change
 * `useEffect`, which lands one render AFTER the new question has painted. So the next question's
 * first frame was built from the previous question's last set index: on a question with fewer sets,
 * `maxDiffSets[stale]` is `undefined`, the `|| []` fallback makes it a set with no claims, the
 * counter reads "Set 5 of 1", both columns are empty and the item-image gate is vacuously satisfied
 * because `[].every(...)` is true. Measured on a MutationObserver over a 5-set -> 1-set boundary:
 * the question text and the set TOTAL change in one commit, all four tiles are removed in that same
 * commit, and the set NUMBER only follows in the next one.
 *
 * Read back for the visit on screen and nothing else, so a question that has not advanced a set
 * reads 0 by construction, in the frame it first paints, with no effect in the path — and so does a
 * question being answered for the second time.
 */
type MaxDiffSetCursor = { visitKey: string | null; setIndex: number };

/** Belongs to no visit, so every question reads set 0 until it advances one of its own. */
const MAX_DIFF_SET_CURSOR_START: MaxDiffSetCursor = { visitKey: null, setIndex: 0 };

/**
 * What a settled <img> NODE says about itself, for images that resolved before React could attach
 * `onLoad`/`onError` — a cached fetch settles once and its event does not fire again, so the node
 * is the only witness left.
 *
 * `complete` alone cannot be trusted: per spec it is true for a fetch that settled BROKEN too, so a
 * cached 404 would be waved through as painted. The intrinsic size is the other thing a node
 * offers, and it is a ONE-WAY signal: a non-zero size proves something decoded, a zero size proves
 * nothing at all. An image sized only by an SVG `viewBox` reports 0x0 in Chrome (Firefox reports
 * 300x150) and renders perfectly.
 *
 * WHICH ERROR THIS DESIGN PREFERS, stated once rather than re-tuned per round: given a zero-size
 * node and no failure witness, prefer leaving a broken image UNLABELLED over covering a working
 * one. The failure panel is `absolute inset-0 z-20 bg-gray-100` — it does not label the artwork,
 * it COVERS it, and the respondent then ranks the claim from its text alone, which is the hole
 * this feature exists to close. The opposite error costs only the label: the browser still draws
 * its own broken-image glyph, exactly as it did before this feature existed.
 *
 * So a zero-size node is UNDECIDABLE, and nothing about the URL changes that. The verdict comes
 * only from a witness that actually knows: the `error` event, or `decode()` (see below). The
 * previous rule tested the url for a `.svg`/`.svgz` extension, which was a proxy for a question
 * the platform can answer directly — and it condemned every dimensionless image served without an
 * extension to read: a render endpoint (`/render?id=pack-a`), a signed CDN url, an `/api/…` proxy.
 */
const IMAGE_SETTLE = {
  PENDING: 'pending',
  PAINTED: 'painted',
  UNDECIDABLE: 'undecidable',
} as const;

type ImageSettleState = (typeof IMAGE_SETTLE)[keyof typeof IMAGE_SETTLE];

const readImageSettleState = (el: HTMLImageElement | null): ImageSettleState => {
  if (!el?.complete) return IMAGE_SETTLE.PENDING;
  if (el.naturalWidth > 0 || el.naturalHeight > 0) return IMAGE_SETTLE.PAINTED;
  return IMAGE_SETTLE.UNDECIDABLE;
};

/**
 * The src each node has already been asked about, so the probe below runs once per (node, src)
 * rather than on every render — the ref is an inline arrow and re-runs on all of them.
 */
const decodeProbedSrc = new WeakMap<HTMLImageElement, string>();

/**
 * Ask the node the question its size cannot answer. `HTMLImageElement.decode()` resolves when the
 * image is renderable and rejects when the fetch produced nothing, whatever the format or the url,
 * so it settles the UNDECIDABLE case correctly instead of guessing from a file extension.
 *
 * Absent on browsers too old to have it, in which case there is no verdict and the preference
 * above applies: the image stays unmarked. Writes only while the node is still mounted, so a set
 * advance or a question change cannot have a late promise reach a slot that has moved on.
 */
const probeImageDecode = (
  el: HTMLImageElement,
  onDecoded: () => void,
  onFailed: () => void,
): void => {
  if (typeof el.decode !== 'function') return;
  const src = el.getAttribute('src') ?? '';
  if (decodeProbedSrc.get(el) === src) return;
  decodeProbedSrc.set(el, src);
  el.decode().then(
    () => {
      if (el.isConnected) onDecoded();
    },
    () => {
      if (el.isConnected) onFailed();
    },
  );
};

const MAX_DIFF_TILE_LOCKED_CLASS = 'border-gray-200 bg-gray-50 text-gray-400 cursor-not-allowed';
const MAX_DIFF_TILE_TAKEN_CLASS = 'border-gray-200 bg-gray-100 text-gray-400 cursor-not-allowed';

/**
 * One picture inside a single_choice / multiple_choice option button.
 *
 * ONE component rather than the two identical copies the two branches carried: every round that
 * fixed this block fixed one of them, and the copy in the branch nobody was editing kept the
 * defect. Its own `key` is the media key, so the node's identity is "which picture is this" and
 * not "which slot in the grid" — the option buttons are keyed by LABEL and the question container
 * is not keyed at all, so a battery of questions sharing option labels reconciles this same <img>
 * into the next question.
 *
 * That is what made hiding a broken image imperatively (`style.display = 'none'`) unsafe here for
 * the same reason it was unsafe on the MaxDiff tiles: React does not manage a style it was never
 * given, so the write outlives the image it hid. One 404 on question 1 hid question 2's artwork
 * for the rest of the session while `onLoad` still opened the gate — and these buttons SUBMIT on
 * click, so the answer was recorded against a picture the respondent could not see, and nothing in
 * it said so. The failure is recorded against the paint slot and drawn as a panel instead.
 */
const ChoiceOptionMediaTile: React.FC<{
  url: string;
  alt: string;
  mediaKey: string;
  isLoaded: boolean;
  hasFailed: boolean;
  onPainted: (mediaKey: string) => void;
  onSettled: (mediaKey: string) => void;
  onFailed: (mediaKey: string) => void;
}> = ({ url, alt, mediaKey, isLoaded, hasFailed, onPainted, onSettled, onFailed }) => (
  <div className={OPTION_MEDIA_CONTAINER_CLASS}>
    {!isLoaded && !hasFailed && (
      <div className="absolute inset-0 flex items-center justify-center bg-gray-100 animate-pulse">
        <div className="w-6 h-6 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
      </div>
    )}
    {/* Says so, rather than leaving a blank panel that reads as "this option has no picture" —
        which is the impression the pack shot was added to correct. */}
    {hasFailed && (
      <div className="absolute inset-0 z-20 flex items-center justify-center bg-gray-100 p-2">
        <span className="text-xs text-gray-500 text-center">
          {UI_TEXT.HUMAN_SURVEY.SESSION.ITEM_IMAGE_FAILED}
        </span>
      </div>
    )}
    <img
      src={getFullMediaUrl(url)}
      alt={alt}
      className={`${OPTION_MEDIA_IMG_CLASS} transition-opacity duration-300 ${isLoaded ? 'opacity-100' : 'opacity-0'}`}
      // A cached image settles before React attaches onLoad/onError and neither event fires again,
      // so the gate has to read the node — and `complete` alone reads TRUE for a broken one.
      // See readImageSettleState: a settled node always resolves the gate, only one that reported
      // a size is painted outright, and one that reported none is put to decode().
      ref={(el) => {
        const settle = readImageSettleState(el);
        if (settle === IMAGE_SETTLE.PENDING) return;
        if (settle === IMAGE_SETTLE.PAINTED) {
          onPainted(mediaKey);
          return;
        }
        onSettled(mediaKey);
        probeImageDecode(
          el as HTMLImageElement,
          () => onPainted(mediaKey),
          () => onFailed(mediaKey),
        );
      }}
      onLoad={() => onPainted(mediaKey)}
      // Still resolves the gate, so a dead image fails open instead of locking the respondent out
      // of a question they can otherwise answer.
      onError={() => {
        onFailed(mediaKey);
        onSettled(mediaKey);
      }}
    />
  </div>
);
/**
 * How an option looks once the multi-select cap is reached (spec 36). Only UNSELECTED options get
 * this — a selected one stays fully interactive so the respondent can swap rather than be stuck.
 */
const CAPPED_OPTION_CLASS = 'border-gray-200 bg-gray-50 text-gray-400 cursor-not-allowed';

/** The value the "Other" entry is stored under in a choice answer. */
const OTHER_CHOICE_VALUE = 'Other';

/** What a respondent is told when a submission fails for a reason nothing can explain to them. */
const GENERIC_SUBMIT_ERROR = 'Failed to submit response. Please try again.';

/** Reason codes whose message is written FOR a respondent and is safe to show verbatim. */
const RESPONDENT_ACTIONABLE_CODES = new Set([SELECTION_CAP_REJECTION_CODE]);

/**
 * The message to show a respondent whose answer was refused.
 *
 * The status is necessary but nowhere near sufficient. `POST /sessions/{id}/responses` turns EVERY
 * refusal from `process_response` into a 400 carrying the raw text — session-state rejections
 * ("Session is not in progress"), allocation-shape validation, and a catch-all that passes
 * `str(e)` from an unhandled exception straight through. Gating on 400 alone put all of that in
 * front of a paid panellist mid-survey. Only a rule the respondent can actually act on gets
 * through; everything else keeps the generic retry text.
 *
 * The CODE decides wherever the server sent one — that is the whole point of tagging it. Matching
 * the message is the untagged fallback and nothing else: the two repos deploy separately, so a
 * browser can meet a server that predates `max_selections_exceeded`, and on that server the
 * wording is the only thing there is to recognise. Both the code and that wording are pinned in
 * the shared contract now (`submit_rejection`), so the server can no longer reword or rename
 * either without a red suite — which is what this fallback used to depend on informally.
 */
function respondentFacingSubmitError(error: unknown): string {
  if (!(error instanceof SurveyRequestError) || error.status !== HTTP_STATUS.BAD_REQUEST) {
    return GENERIC_SUBMIT_ERROR;
  }
  const reason = error.message.trim();
  const actionable = error.code
    ? RESPONDENT_ACTIONABLE_CODES.has(error.code)
    : SELECTION_CAP_MESSAGE.test(reason);
  return actionable && reason ? reason : GENERIC_SUBMIT_ERROR;
}

/** Backward-compat check: true if question should randomize media order. */
function shouldRandomizeMedia(q: any): boolean {
  if (q?.randomize_media_order != null) return q.randomize_media_order;
  return q?.image_position_mode === 'randomized';
}

/**
 * Build the conjoint profile set (balanced orthogonal design) for a question's attributes.
 * Shared by the render path (which shows the profiles) and the submit path (which recovers the
 * chosen profile's `profile_details`), so the stored details always match what the respondent saw.
 */
interface ConjointAttribute {
  name: string;
  levels: string[];
}
interface ConjointProfile {
  id: string;
  attributes: Record<string, string>;
}
function generateConjointProfiles(attributes: ConjointAttribute[]): ConjointProfile[] {
  const totalLevels = attributes.reduce((sum, attr) => sum + attr.levels.length, 0);
  const minProfilesNeeded = Math.max(totalLevels, attributes.length * 2, 12);

  const profiles: ConjointProfile[] = [];
  for (let i = 0; i < minProfilesNeeded; i++) {
    const profile: Record<string, string> = {};
    attributes.forEach((attr, attrIndex) => {
      const rotationMultiplier = attrIndex + 1;
      const levelIndex = Math.floor(i / rotationMultiplier) % attr.levels.length;
      profile[attr.name] = attr.levels[levelIndex];
    });
    profiles.push({ id: `profile_${i + 1}`, attributes: profile });
  }

  // Balance attribute levels across profiles for statistical validity.
  const balanced = [...profiles];
  attributes.forEach((attr) => {
    const levelCounts: Record<string, number> = {};
    attr.levels.forEach((level) => {
      levelCounts[level] = 0;
    });
    balanced.forEach((profile) => {
      levelCounts[profile.attributes[attr.name]]++;
    });

    const minCount = Math.min(...Object.values(levelCounts));
    const maxCount = Math.max(...Object.values(levelCounts));

    if (maxCount - minCount > 1) {
      const underrepresented = attr.levels.filter((level) => levelCounts[level] === minCount);
      let swapIndex = 0;
      for (let i = 0; i < balanced.length && underrepresented.length > 0; i++) {
        const currentLevel = balanced[i].attributes[attr.name];
        if (levelCounts[currentLevel] > minCount + 1) {
          balanced[i].attributes[attr.name] = underrepresented[swapIndex];
          levelCounts[currentLevel]--;
          levelCounts[underrepresented[swapIndex]]++;
          swapIndex = (swapIndex + 1) % underrepresented.length;
        }
      }
    }
  });
  return balanced;
}

interface AiInference {
  inferred_answer?: any;
  covered_by?: string[]; // source question id(s)
  citation?: string; // verbatim quote from an earlier answer
  confidence?: number; // >0.95 gate enforced server-side
  reason?: string;
}

interface QuestionResponse {
  question_id: string;
  question_type: string;
  response_data: any;
  state: string; // includes 'ai_skipped'
  created_at: string;
  ai_inference?: AiInference;
}

interface SessionData {
  id: number;
  state: string;
  current_question_index: number;
  survey_id: number;
  responses: QuestionResponse[];
  responses_count: number;
  question_order?: number[] | null; // Randomized question order indices
  // Adaptive-scoping narrowings the backend has staged for upcoming questions, keyed by question
  // id. Persisted on the session so the adapted text survives a resume / page reload.
  pending_adaptations?: Record<string, { adapted_question?: string }> | null;
}

const SurveySession: React.FC = () => {
  const { shareId } = useParams<{ shareId: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const isPreviewMode = searchParams.get('preview') === 'true';
  // `startFrom` is an authoring deep-link into the preview (SurveyQuestionsPreview builds
  // `?preview=true&startFrom=N`). On a public respondent link it must stay inert: a URL param is
  // never consent, so the welcome + consent gate always runs and progress comes from the session.
  const startFromParam = isPreviewMode ? searchParams.get('startFrom') : null;
  const startFromIndex = parseStartFromIndex(startFromParam);

  const [survey, setSurvey] = useState<any>(null);
  const [session, setSession] = useState<SessionData | null>(null);
  const previewResponsesRef = useRef<QuestionResponse[]>([]);
  const [showWelcomeScreen, setShowWelcomeScreen] = useState(!startFromParam);
  const [showConsentScreen, setShowConsentScreen] = useState(false);
  // The index and the visit count move together, always. See QuestionVisit: a bare index cannot
  // tell a second visit to a question from the first, and three things are read back against it —
  // the MaxDiff set cursor, the option-media fail-open scope, and the presentation clock.
  //
  // Seeded from `startFromIndex`, NOT a second parse of `startFromParam`: that param is
  // preview-only and is parsed once by `parseStartFromIndex` above. Two readers of one param is
  // the duplicate-state shape this cursor exists to remove.
  const [questionVisit, setQuestionVisit] = useState<QuestionVisit>(() => ({
    index: startFromIndex,
    visit: 0,
  }));
  const currentQuestionIndex = questionVisit.index;
  // The ONLY way to move. Every navigation is a new visit, including one that lands on the index it
  // started from — "the respondent is here again" is exactly what the scoped state has to see.
  const setCurrentQuestionIndex = useCallback(
    (next: number | ((previousIndex: number) => number)) =>
      setQuestionVisit((previous) => ({
        index: typeof next === 'function' ? next(previous.index) : next,
        visit: previous.visit + 1,
      })),
    [],
  );
  const [currentResponse, setCurrentResponse] = useState<any>('');
  // Adaptive question scoping: per-question_id overrides returned by submit-response when an
  // upcoming open-text question was narrowed to its still-uncovered rubric points. Keyed by id so
  // it survives advancing to that question (no reset needed); falls back to the authored text.
  const [adaptedQuestions, setAdaptedQuestions] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Synchronous re-entrancy latch. isSubmitting is state, so two taps in the same tick both read
  // the old `false` and both submit — each advancing the index off the same stale closure, which
  // skips a question. A ref flips immediately.
  const submitInFlightRef = useRef(false);
  const completeInFlightRef = useRef(false);
  // Set once a write comes back flagged `survey_timer_expired` — the server accepted it, recorded
  // nothing, and only IT knows the buzzer went (its deadline can be derived from the session's
  // own created_at, so it may be well ahead of ours). Handed to the clock, which then asks for the
  // hard stop instead of waiting for our interpolation to agree.
  const [timerExpiredByServer, setTimerExpiredByServer] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [completionPhase, setCompletionPhase] = useState<'idle' | 'finishing' | 'failed'>('idle');
  const completionPhaseRef = useRef(completionPhase);
  useEffect(() => {
    completionPhaseRef.current = completionPhase;
  }, [completionPhase]);
  // --- rapid-fire block state (spec 29) ---
  // Deadlines from a Start made in THIS tab. The session's own rapid_fire_state is the other
  // source (that is what makes a reload resume correctly) and the two are merged when read, so
  // there is no effect syncing server state into local state.
  const [rapidFireLocalEndsAt, setRapidFireLocalEndsAt] = useState<Record<string, string>>({});
  // Blocks already closed this session — stops a re-close when the timer fires mid-request.
  const rapidFireClosedRef = useRef<Record<string, boolean>>({});
  const [isStartingRapidFire, setIsStartingRapidFire] = useState(false);
  /**
   * The one answer of overrun each block has granted, keyed by block so a second rapid-fire block
   * starts with a clean slate. Written only by the tick — see `evaluateRapidFireClose`.
   */
  const rapidFireOverrunRef = useRef<Record<string, RapidFireOverrun>>({});
  /** The block whose 3-2-1 is running, or null. */
  const [rapidFireBeatFor, setRapidFireBeatFor] = useState<string | null>(null);
  /** A finished block, HELD before its advance; the count is a snapshot — ADR-0004 §3. */
  const [rapidFireOutro, setRapidFireOutro] = useState<{
    blockId: string;
    reason: 'expired' | 'cap';
    answered: number;
    notReached: number;
    nextIndex: number;
    label: string;
  } | null>(null);
  /** Spoken answers whose transcript has not resolved — ADR-0005. A COUNT per question, since a
   *  re-record puts two takes in flight and a Set cannot represent that. */
  const [outstandingTranscripts, setOutstandingTranscripts] = useState<ReadonlyMap<string, number>>(
    () => new Map(),
  );
  /** One ceiling timer per outstanding attempt, keyed BY attempt: two attempts must be bounded
   *  separately, and they do not come home in the order they were armed. */
  const transcriptCeilingTimersRef = useRef<Map<string, Map<number, number>>>(new Map());
  /** The question a spoken answer just advanced from, held for UNDO_WINDOW_MS. `armedAt` is not
   *  read: it makes every arming a NEW object, so re-recording restarts the window. */
  const [spokenUndo, setSpokenUndo] = useState<{ fromIndex: number; armedAt: number } | null>(null);
  useEffect(() => {
    if (!spokenUndo) return;
    const timer = window.setTimeout(() => setSpokenUndo(null), VOICE_CAPTURE.UNDO_WINDOW_MS);
    return () => window.clearTimeout(timer);
  }, [spokenUndo]);
  /** The counts as a ref: the follow-up gate reads them SYNCHRONOUSLY, in the same turn that
   *  armed the marker, where React state is still a render behind. State is written from this. */
  const outstandingTranscriptsRef = useRef<Map<string, number>>(new Map());
  /** Resolvers parked on a question's transcripts all settling — see `awaitSpokenTranscript`. */
  const transcriptWaitersRef = useRef<Map<string, ((transcript: string | null) => void)[]>>(
    new Map(),
  );
  /** What the NEWEST settled attempt came home with, for a waiter arriving after it — a fast
   *  transcript can land before the pending write's own round trip finishes. */
  const settledTranscriptsRef = useRef<Map<string, string | null>>(new Map());
  /** Identity for each take, and the newest one per question. A count says how many are in flight;
   *  only an id says WHICH, and the answer belongs to the take the respondent kept. */
  const transcriptAttemptSeqRef = useRef(0);
  const newestTranscriptAttemptRef = useRef<Map<string, number>>(new Map());

  const publishOutstandingTranscripts = useCallback(() => {
    setOutstandingTranscripts(new Map(outstandingTranscriptsRef.current));
  }, []);

  /** A take that has since been re-recorded. An unidentified delivery is never superseded: it
   *  cannot be placed, and refusing it would drop a real answer. */
  const transcriptAttemptSuperseded = useCallback(
    (questionId: string, attemptId?: number) =>
      attemptId !== undefined && newestTranscriptAttemptRef.current.get(questionId) !== attemptId,
    [],
  );

  /** Drops ONE outstanding attempt, idempotent past zero. Only the newest take's words are kept,
   *  and waiters get them once nothing is left in flight. */
  const releaseTranscript = useCallback(
    (questionId: string, attemptId?: number, transcript: string | null = null) => {
      const counts = outstandingTranscriptsRef.current;
      const inFlight = counts.get(questionId);
      if (inFlight === undefined) return;
      if (inFlight <= 1) counts.delete(questionId);
      else counts.set(questionId, inFlight - 1);
      publishOutstandingTranscripts();

      if (!transcriptAttemptSuperseded(questionId, attemptId)) {
        settledTranscriptsRef.current.set(questionId, transcript);
      }
      if (counts.has(questionId)) return;
      const settled = settledTranscriptsRef.current.get(questionId) ?? null;
      const waiters = transcriptWaitersRef.current.get(questionId);
      transcriptWaitersRef.current.delete(questionId);
      waiters?.forEach((resolve) => resolve(settled));
    },
    [publishOutstandingTranscripts, transcriptAttemptSuperseded],
  );

  /** This attempt's own ceiling — falling back to the oldest armed for an unidentified delivery,
   *  which is the arrival order the queue used to assume of everything. */
  const clearTranscriptCeiling = useCallback((questionId: string, attemptId?: number) => {
    const timers = transcriptCeilingTimersRef.current.get(questionId);
    if (!timers) return;
    const armed = attemptId ?? timers.keys().next().value;
    if (armed === undefined) return;
    const timer = timers.get(armed);
    if (timer !== undefined) window.clearTimeout(timer);
    timers.delete(armed);
  }, []);

  const resolveTranscript = useCallback(
    (questionId: string, attemptId?: number, transcript: string | null = null) => {
      clearTranscriptCeiling(questionId, attemptId);
      releaseTranscript(questionId, attemptId, transcript);
    },
    [clearTranscriptCeiling, releaseTranscript],
  );

  /** Resolves once nothing is outstanding for `questionId`. Bounded by each attempt's ceiling,
   *  which is what makes it safe to await inside a submit holding `submitInFlightRef`. */
  const awaitSpokenTranscript = useCallback((questionId: string): Promise<string | null> => {
    if (!outstandingTranscriptsRef.current.get(questionId)) {
      return Promise.resolve(settledTranscriptsRef.current.get(questionId) ?? null);
    }
    return new Promise<string | null>((resolve) => {
      const waiters = transcriptWaitersRef.current.get(questionId) ?? [];
      waiters.push(resolve);
      transcriptWaitersRef.current.set(questionId, waiters);
    });
  }, []);

  /** Marks one spoken answer outstanding and bounds the wait it creates. ADR-0005 §1 rejected a
   *  clock for the OUTCOME; this bounds the PROMISE, and releases the GATE only. The attempt id it
   *  returns is what the delivery must carry back. */
  const markTranscriptOutstanding = useCallback(
    (questionId: string): number => {
      const attemptId = ++transcriptAttemptSeqRef.current;
      const counts = outstandingTranscriptsRef.current;
      counts.set(questionId, (counts.get(questionId) ?? 0) + 1);
      newestTranscriptAttemptRef.current.set(questionId, attemptId);
      // A new take invalidates whatever the previous one said: a late waiter must not be handed
      // the words of a recording this attempt is about to replace.
      settledTranscriptsRef.current.delete(questionId);
      publishOutstandingTranscripts();

      const timers =
        transcriptCeilingTimersRef.current.get(questionId) ?? new Map<number, number>();
      timers.set(
        attemptId,
        window.setTimeout(() => {
          transcriptCeilingTimersRef.current.get(questionId)?.delete(attemptId);
          releaseTranscript(questionId, attemptId);
        }, VOICE_CAPTURE.TRANSCRIPT_WAIT_CEILING_MS),
      );
      transcriptCeilingTimersRef.current.set(questionId, timers);
      return attemptId;
    },
    [publishOutstandingTranscripts, releaseTranscript],
  );

  useEffect(() => {
    const timers = transcriptCeilingTimersRef.current;
    const waiters = transcriptWaitersRef.current;
    return () => {
      timers.forEach((ids) => ids.forEach((id) => window.clearTimeout(id)));
      timers.clear();
      // Nothing will ever settle these now, and a waiter that never resolves is an awaited submit
      // that never returns.
      waiters.forEach((resolvers) => resolvers.forEach((resolve) => resolve(null)));
      waiters.clear();
    };
  }, []);
  const [rapidFireNow, setRapidFireNow] = useState<number>(() => Date.now());
  const [isRecording, setIsRecording] = useState(false);
  // The same thing for the conversational runtime, which owns its own microphone and whose
  // recording state `isRecording` above never sees. Kept separate rather than shared: only one of
  // the two bodies is ever on screen, and a single flag written from both would leave whichever
  // ran last deciding for a flow that is not even mounted.
  const [isRecordingConversational, setIsRecordingConversational] = useState(false);
  const [showTextInput, setShowTextInput] = useState(false);
  // Mirror of the channel toggle for reads inside the submit callbacks, for the same reason as
  // questionStartTimeRef: the toggle can flip after React last rebuilt them, and `input_mode`
  // has to describe how the answer on screen was actually given.
  const showTextInputRef = useRef(showTextInput);
  // Sticky for the rest of the session once transcription fails. Voice is not offered again —
  // re-offering a service we know is down just loses more answers.
  const [transcriptionUnavailable, setTranscriptionUnavailable] = useState(false);
  const [followupQuestion, setFollowupQuestion] = useState<string | null>(null);
  // Count of follow-ups the respondent has answered this session — feeds the progress estimate
  // (each follow-up is an extra question not present in the authored array).
  const [followupsAnswered, setFollowupsAnswered] = useState(0);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  // Brief "checking your answers" state during the synchronous AI redundancy look-ahead.
  const [isCheckingSkip, setIsCheckingSkip] = useState(false);
  // "Thinking…" overlay message while a submit is in flight and the backend is doing its adaptive
  // work (scoping the next question / coverage look-ahead). The delayed flag itself comes from
  // useDelayedFlag so quick submits never flash it.
  const [thinkingMessage, setThinkingMessage] = useState('');
  const [_analysisProgress, setAnalysisProgress] = useState<string>('');
  const [originalResponse, setOriginalResponse] = useState<any>(null);
  /**
   * When the CURRENT question was put in front of the respondent (ISO string). This is what the
   * backend turns into `response_time_seconds`.
   *
   * A ref, not state: it is never rendered, and it is read from submit callbacks whose identity
   * React rebuilds only when their dependencies change. Held as state it was read through
   * whatever closure happened to be bound to the control that was clicked, so an answer could be
   * stamped with the PREVIOUS question's start (frontend#425) — and adding it to the dependency
   * arrays would only trade that for rebuilding those callbacks on every question. A ref is read
   * at the moment of the click, so no dependency array can get this wrong again.
   */
  const questionStartTimeRef = useRef<string>('');
  // True active-time tracking for the current question (pauses on tab-hidden / blur / idle).
  // Mirrors the heartbeat's preview guard: never tracks in preview mode.
  const {
    getTimings: getActiveTimings,
    collectDelta: collectActiveTimeDelta,
    resetForQuestion: resetActiveTime,
  } = useActiveQuestionTime(!isPreviewMode);
  // Which MaxDiff set the respondent is on, stored WITH the question it was counted for. Read
  // back through `currentMaxDiffSetIndex` below, never directly.
  const [maxDiffSetCursor, setMaxDiffSetCursor] =
    useState<MaxDiffSetCursor>(MAX_DIFF_SET_CURSOR_START);

  // Track option media loading for choice questions
  const [optionMediaLoaded, setOptionMediaLoaded] = useState<Record<string, boolean>>({});
  // Option images that failed to load, keyed the same way as the paint tracker — for MaxDiff by
  // PAINT SLOT (question, set, item, column: see MAX_DIFF_ITEM_MEDIA_KEY_PREFIX above), never by
  // where the tile happens to sit on screen. Hiding a broken image imperatively
  // (`style.display = 'none'`) cannot work for MaxDiff: its tiles are keyed by display position,
  // so React hands the SAME <img> node to a different item on the next set and the inline style
  // outlives the item it was hiding. That later image then loads, opens the gate, enables the
  // tile — and stays invisible, so the respondent ranks packaging they never saw and the saved
  // response looks perfectly normal.
  //
  // A paint retracts the flag: every node issues its own request, so a failure that only ever
  // latched would cover a picture that IS being served on a retry with an opaque panel the
  // respondent cannot see past. Latching is not conservative here — it is its own way of hiding
  // the stimulus.
  const [optionMediaErrored, setOptionMediaErrored] = useState<Record<string, boolean>>({});
  // Fail-open backstop for option media, mirroring useStimulusReady: an option image that never
  // resolves must not leave the choices disabled forever.
  //
  // Stored as the SCOPE that timed out rather than a bare boolean, and compared against the scope
  // on screen. A MaxDiff set advance keeps the same question id, so a latched boolean would stay
  // true from the first set onward — a respondent comparing four packs takes longer than the
  // timeout, and every later set would then render unlocked before its artwork painted, which is
  // the exact hole this gate exists to close. Deriving it also means the new set is gated on its
  // FIRST render; re-arming it from an effect would leave one unlocked frame.
  const [timedOutOptionMediaScope, setTimedOutOptionMediaScope] = useState<string | null>(null);
  // Per-image paint tracking for question-level media (info-screen-style grid). Keyed by
  // question id + url so a new question starts fully unloaded — this is what lets us hide the
  // previous question's images behind a loading skeleton instead of flashing them until the
  // new src decodes into the reused <img> node.
  const [questionMediaLoaded, setQuestionMediaLoaded] = useState<Record<string, boolean>>({});
  const markQuestionMediaLoaded = useCallback((key: string) => {
    setQuestionMediaLoaded((prev) => (prev[key] ? prev : { ...prev, [key]: true }));
  }, []);
  // Media that failed to load. Without this a 404 stimulus just shows the browser's broken
  // -image glyph with no explanation; we render a readable fallback instead.
  const [questionMediaErrored, setQuestionMediaErrored] = useState<Record<string, boolean>>({});
  const markQuestionMediaErrored = useCallback((key: string) => {
    setQuestionMediaErrored((prev) => (prev[key] ? prev : { ...prev, [key]: true }));
  }, []);
  const [currentConjointScenarioIndex, setCurrentConjointScenarioIndex] = useState(0);
  const [maxDiffResponses, setMaxDiffResponses] = useState<
    Record<number, { most: string; least: string }>
  >({});
  const [conjointResponses, setConjointResponses] = useState<Record<number, string>>({});
  const [isTerminated, setIsTerminated] = useState(false);
  const [terminationReason, setTerminationReason] = useState<string | null>(null);
  const [terminationRedirectUrl, setTerminationRedirectUrl] = useState<string | null>(null);
  // Respondent fraud gate: non-US soft gate (retryable) and borderline-bot challenge.
  const [geoGateMessage, setGeoGateMessage] = useState<string | null>(null);
  const [showChallenge, setShowChallenge] = useState(false);
  const [isRetryingAccess, setIsRetryingAccess] = useState(false);
  const sessionStartCtxRef = useRef<{ surveyId: number; trackingId?: string } | null>(null);
  // Which shareId the load effect has already run for. `searchParams` is one of its dependencies,
  // so writing the respondent id into the URL re-fires it; `session` is set asynchronously and
  // cannot be relied on to have landed, which would let two sessions be created for one respondent.
  const startedForShareIdRef = useRef<string | null>(null);
  const [pendingSkipTarget, setPendingSkipTarget] = useState<number | null>(null);
  const [isSkippingConditional, setIsSkippingConditional] = useState(false);
  // Brief acknowledgement when the AI auto-skipped questions the respondent already answered.
  const [aiSkipNotice, setAiSkipNotice] = useState<{ count: number } | null>(null);

  // Memoize analyzing message to prevent flickering when component re-renders during analysis
  const analyzingMessage = useMemo(() => {
    return isAnalyzing ? getRandomAnalyzingMessage() : '';
  }, [isAnalyzing]);

  // Surface a "thinking…" overlay once a submit has been in flight long enough that the wait would
  // otherwise read as lag (the backend is scoping the next question / running coverage). The delay
  // keeps fast submits instant; the message is picked once it turns on so it doesn't flicker.
  const showThinking = useDelayedFlag(isSubmitting, THINKING_DELAY_MS);
  // Pick the message when the submit STARTS (not when the overlay flips on) so it's already in
  // state by the time the delay elapses — otherwise the heading paints empty for one frame.
  useEffect(() => {
    if (isSubmitting) setThinkingMessage(getRandomAnalyzingMessage());
  }, [isSubmitting]);

  // Session persistence helpers - prevent losing progress on page reload
  const getStorageKey = useCallback((surveyId: number) => `survey_session_${surveyId}`, []);

  const saveSessionToStorage = useCallback(
    (surveyId: number, sessionId: number) => {
      try {
        localStorage.setItem(
          getStorageKey(surveyId),
          JSON.stringify({
            sessionId,
            savedAt: Date.now(),
          }),
        );
        console.log(`[SessionPersist] Saved session ${sessionId} for survey ${surveyId}`);
      } catch (e) {
        console.warn('[SessionPersist] Failed to save session to localStorage:', e);
      }
    },
    [getStorageKey],
  );

  const getSavedSession = useCallback(
    (surveyId: number): { sessionId: number; savedAt: number } | null => {
      try {
        const saved = localStorage.getItem(getStorageKey(surveyId));
        if (saved) {
          const parsed = JSON.parse(saved);
          // Only use sessions saved within the last 24 hours
          if (
            parsed.sessionId &&
            parsed.savedAt &&
            Date.now() - parsed.savedAt < 24 * 60 * 60 * 1000
          ) {
            return parsed;
          }
        }
      } catch (e) {
        console.warn('[SessionPersist] Failed to read session from localStorage:', e);
      }
      return null;
    },
    [getStorageKey],
  );

  const clearSavedSession = useCallback(
    (surveyId: number) => {
      try {
        localStorage.removeItem(getStorageKey(surveyId));
        console.log(`[SessionPersist] Cleared saved session for survey ${surveyId}`);
      } catch (e) {
        console.warn('[SessionPersist] Failed to clear session from localStorage:', e);
      }
    },
    [getStorageKey],
  );

  // Function to randomize choices while excluding the "Other" option if it exists
  const randomizeChoices = useCallback(
    (choices: string[], hasOtherOption: boolean = false): string[] => {
      if (!choices || choices.length <= 1) return choices;

      // If there's an "Other" option and it should be excluded from randomization
      const choicesToRandomize = hasOtherOption ? choices.slice(0, -1) : choices;
      const otherOption = hasOtherOption && choices.length > 0 ? choices[choices.length - 1] : null;

      // Fisher-Yates shuffle algorithm for randomization
      const randomized = [...choicesToRandomize];
      for (let i = randomized.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [randomized[i], randomized[j]] = [randomized[j], randomized[i]];
      }

      // Add back the "Other" option at the end if it exists
      return otherOption ? [...randomized, otherOption] : randomized;
    },
    [],
  );

  // Function to calculate MaxDiff scores and get top and bottom claims
  const calculateMaxDiffRankedClaims = useCallback(
    (
      responses: Record<
        number,
        { most?: string; least?: string; most_appealing?: string; least_appealing?: string }
      >,
      count: number = 3,
    ): { topClaims: string[]; bottomClaims: string[] } => {
      // Count most and least selections
      const mostCounts: Record<string, number> = {};
      const leastCounts: Record<string, number> = {};

      // Iterate through all set responses
      Object.values(responses).forEach((response) => {
        // Handle both 'most'/'least' and 'most_appealing'/'least_appealing' keys
        const most = response.most_appealing || response.most;
        const least = response.least_appealing || response.least;

        if (most) {
          mostCounts[most] = (mostCounts[most] || 0) + 1;
        }
        if (least) {
          leastCounts[least] = (leastCounts[least] || 0) + 1;
        }
      });

      // Calculate net scores (most = +1, least = -1)
      const allItems = new Set([...Object.keys(mostCounts), ...Object.keys(leastCounts)]);
      const netScores: Record<string, number> = {};

      allItems.forEach((item) => {
        const mostScore = (mostCounts[item] || 0) * 1; // +1 per "most" selection
        const leastScore = (leastCounts[item] || 0) * -1; // -1 per "least" selection
        netScores[item] = mostScore + leastScore;
      });

      // Sort by score (descending), then alphabetically for ties
      const sortedItems = Object.entries(netScores).sort(([itemA, scoreA], [itemB, scoreB]) => {
        if (scoreB !== scoreA) return scoreB - scoreA; // Higher score first
        return itemA.localeCompare(itemB); // Alphabetical for ties
      });

      // Top N = highest scores, Bottom N = lowest scores
      const topClaims = sortedItems.slice(0, count).map(([item]) => item);
      const bottomClaims = sortedItems
        .slice(-count)
        .reverse()
        .map(([item]) => item);

      return { topClaims, bottomClaims };
    },
    [],
  );

  // Get the actual question index using question_order if available
  // This maps display position (currentQuestionIndex) to actual question index in survey.questions
  const getActualQuestionIndex = useCallback(
    (displayIndex: number): number => {
      if (session?.question_order && session.question_order.length > displayIndex) {
        return session.question_order[displayIndex];
      }
      return displayIndex;
    },
    [session?.question_order],
  );

  // Memoized current question that respects question_order
  const currentQuestion = useMemo(() => {
    if (!survey || !survey.questions || currentQuestionIndex >= survey.questions.length) {
      return null;
    }
    const actualIndex = getActualQuestionIndex(currentQuestionIndex);
    return survey.questions[actualIndex];
  }, [survey, currentQuestionIndex, getActualQuestionIndex]);

  // Whether the on-screen max_diff question uses the one-screen best/worst mode (spec 36-02)
  // rather than today's multi-set MaxDiff. Read once here and reused everywhere the mode has to
  // be checked, so it is never re-derived — or worse, re-inferred from the response shape, which
  // decisions-02 explicitly forbids: the mode must be checked explicitly.
  const isBestWorst = isBestWorstQuestion(currentQuestion);

  // Which question, and which time through it. Everything scoped per-question hangs off this
  // rather than off the id alone, because a backward `skip_to` makes those two different things.
  const questionVisitKey = makeQuestionVisitKey(currentQuestion?.id, questionVisit.visit);

  // The set index of the visit ON SCREEN. See MaxDiffSetCursor for why this is derived from a
  // scoped cursor rather than reset by the question-change effect.
  const currentMaxDiffSetIndex =
    currentQuestion && maxDiffSetCursor.visitKey === questionVisitKey
      ? maxDiffSetCursor.setIndex
      : 0;

  // Identifies which answer a voice recording belongs to (`VoiceOrTextInput`'s `slotKey` prop) — a
  // question and its AI follow-up are distinct slots, and a MaxDiff/conjoint scenario advance
  // inside the same question is too. See `makeAnswerSlotKey`'s docblock.
  const answerSlotKey = useMemo(
    () =>
      makeAnswerSlotKey(
        currentQuestion?.id,
        currentQuestionIndex,
        Boolean(followupQuestion),
        followupQuestion
          ? followupsAnswered
          : currentMaxDiffSetIndex + currentConjointScenarioIndex,
      ),
    [
      currentQuestion,
      currentQuestionIndex,
      followupQuestion,
      followupsAnswered,
      currentMaxDiffSetIndex,
      currentConjointScenarioIndex,
    ],
  );

  // Progress as a PERCENTAGE of the questions this respondent will actually face (estimated from
  // conditional logic — longest branch until decided, then narrowed), not the raw array length. A
  // 92-question survey that branches to ~37 should never read "5 / 92".
  // Survey-wide countdown (spec 35). ONE hook, ONE snapshot of what the server last said; the
  // number on screen is derived at render time. There is deliberately no local seconds counter —
  // a second ticking value would drift from the server on every reload, pause and hiccup.
  // The hook itself is wired up below the rapid-fire section, whose state it needs.
  const surveyTimerConfig = useMemo(() => timerConfigFrom(survey), [survey]);

  const handleCompleteSession = useCallback(async () => {
    if (!session) return;
    if (completeInFlightRef.current) return;
    completeInFlightRef.current = true;

    try {
      setIsSubmitting(true);
      setCompletionPhase('finishing');
      const result = await completeSurveySession(session.id);

      // Clear saved session from localStorage on successful completion
      clearSavedSession(session.survey_id);

      // Check if panel provider redirect URL was provided
      if (result.redirect_url) {
        console.log(`Redirecting to panel provider: ${result.redirect_url}`);
        // Redirect to external panel provider URL
        window.location.href = result.redirect_url;
      } else {
        // No redirect configured, navigate to internal completion page
        navigate(`/survey-complete/${session.id}`);
      }
    } catch (error) {
      console.error('Failed to complete session:', error);
      // Not setError: that card offers no way to finish. The finishing screen's retry re-runs this.
      setCompletionPhase('failed');
    } finally {
      setIsSubmitting(false);
      completeInFlightRef.current = false;
    }
  }, [session, navigate, clearSavedSession]);

  // ------------------------------------------------------------------ rapid-fire (spec 29)

  /**
   * The rapid-fire block the respondent is currently inside, with everything the runner needs.
   *
   * The deadline is READ from two places and merged rather than synced: a Start in this tab, or
   * the session's own rapid_fire_state. That is what makes a reload resume on the real remaining
   * time — and it means "has this block been started" needs no separate flag, because having a
   * deadline IS having been started.
   */
  const rapidFire = useMemo(() => {
    const blockId = currentQuestion?.block_id ? String(currentQuestion.block_id) : null;
    const config = rapidFireConfigFor(survey?.blocks, blockId);
    if (!config || !blockId || !survey?.questions) return null;

    const range = blockDisplayRange(survey.questions, session?.question_order, blockId);
    if (!range) return null; // orphaned config — behave like an ordinary block

    const serverState = (
      session as { rapid_fire_state?: Record<string, { ends_at?: string }> } | null
    )?.rapid_fire_state?.[blockId];
    const endsAt = rapidFireLocalEndsAt[blockId] ?? serverState?.ends_at ?? null;

    return {
      blockId,
      config,
      range,
      endsAt,
      started: !!endsAt,
      remaining: remainingSeconds(endsAt, rapidFireNow),
      answeredInBlock: answeredCountInBlock(survey.questions, blockId, session?.responses),
      durationSeconds: config.duration_seconds ?? RAPID_FIRE_DEFAULT_DURATION_SECONDS,
    };
  }, [currentQuestion, survey, session, rapidFireLocalEndsAt, rapidFireNow]);

  /** Intro gate: inside a rapid-fire block that hasn't been Started yet. */
  // Holds the block id, not a boolean, so a stale beat from another block can never show.
  const showRapidFireBeat =
    !!rapidFire && !rapidFire.started && !isPreviewMode && rapidFireBeatFor === rapidFire.blockId;
  const showRapidFireIntro =
    !!rapidFire && !rapidFire.started && !isPreviewMode && !showRapidFireBeat;
  const rapidFireActive = !!rapidFire?.started;
  /** One lookup for all four rapid-fire screens, so they cannot disagree — ADR-0004 §4. */
  const rapidFireLabel =
    (survey?.blocks || []).find(
      (b: { id?: string | number; label?: string }) => String(b.id) === rapidFire?.blockId,
    )?.label || 'Quick-fire round';

  /** The tap only opens the beat; `handleRapidFireBeatDone` asks for the deadline — ADR-0004 §2. */
  const handleStartRapidFire = useCallback(() => {
    if (!rapidFire || !session) return;
    const { blockId } = rapidFire;

    if (isPreviewMode) {
      // No session to stamp against, so fake a local deadline of the configured length.
      const ends = new Date(Date.now() + rapidFire.durationSeconds * 1000).toISOString();
      setRapidFireLocalEndsAt((prev) => ({ ...prev, [blockId]: ends }));
      setRapidFireNow(Date.now());
      return;
    }

    setRapidFireBeatFor(blockId);
  }, [rapidFire, session, isPreviewMode]);

  const handleRapidFireBeatDone = useCallback(async () => {
    if (!rapidFire || !session) return;
    const { blockId } = rapidFire;

    setIsStartingRapidFire(true);
    try {
      const result = await startRapidFireBlock(session.id, blockId);
      setRapidFireLocalEndsAt((prev) => ({ ...prev, [blockId]: result.ends_at }));
      setRapidFireNow(Date.now());
    } catch (e) {
      console.error('Failed to start rapid-fire block:', e);
      setError('Could not start this section. Please try again.');
      setRapidFireBeatFor(null); // back to pre-flight; never a question with no deadline
    } finally {
      setIsStartingRapidFire(false);
    }
  }, [rapidFire, session]);

  const handleCloseRapidFire = useCallback(
    async (reason: 'expired' | 'cap') => {
      if (!rapidFire || !session || !survey?.questions) return;
      const { blockId, range } = rapidFire;
      if (rapidFireClosedRef.current[blockId]) return;
      rapidFireClosedRef.current[blockId] = true;

      if (isPreviewMode) {
        if (range.end + 1 >= survey.questions.length) {
          setIsTerminated(true);
          setTerminationReason('completed');
        } else {
          setCurrentResponse('');
          setCurrentQuestionIndex(range.end + 1);
        }
        return;
      }

      // The runtime-wide "a write is in the air" flag, taken here for the same reason the three
      // answer-submit paths take it: `beforeHardStop`'s `submitToSettle` polls it and nothing else,
      // so a close it cannot see is a close the hard stop completes the session underneath. That
      // landed two ways, both the researcher's bookkeeping rather than the respondent's answers —
      // this request then failed against a completed session and put "Could not finish this
      // section. Please try again." on the completion page, inviting a retry that cannot succeed;
      // or it landed and wrote block state over a session already `timed_out`. The block's own tick
      // has held the mirror image of this since it was written, declining to close while a submit
      // is in flight.
      //
      // SAVED AND RESTORED rather than cleared, because this is the one write path that also runs
      // NESTED: `maybeCloseRapidFireOnCap` calls it from inside `handleSubmitResponse`, which is
      // already holding the flag. A bare `finally` clearing it would give it back on that caller's
      // behalf while the caller is still going — the very fault the conditional-skip chain records.
      //
      // And deliberately no `if (submitInFlightRef.current) return` guard beside it. The three
      // submit paths have one because a second tap must not start a second write; here
      // `rapidFireClosedRef` above already is that guard, and adding the submit one would make the
      // cap close refuse itself under the submit that asked for it.
      const heldByCaller = submitInFlightRef.current;
      submitInFlightRef.current = true;
      try {
        const result = await closeRapidFireBlock(session.id, blockId, reason);
        setSession(result.session_data);
        setCurrentResponse('');
        // Counted from the session the CLOSE returned, never from `rapidFire.answeredInBlock`.
        // On the cap path this function is the pre-submit render's closure — the answer that
        // tripped the cap is already on the server and still missing from that snapshot, so the
        // card read one below the header directly above it. Deterministic, not a race.
        const answeredAtClose = answeredCountInBlock(
          survey.questions,
          blockId,
          result.session_data?.responses,
        );
        // HELD, not advanced; `handleRapidFireContinue` does what this used to (ADR-0004 §3).
        setRapidFireOutro({
          blockId,
          reason,
          answered: answeredAtClose,
          // The server's record, not `size - answered`: a skip is neither (ADR-0004 §3). Both
          // halves of that record, though — the question the clock stopped on is reported apart
          // from the ones never served, and leaving it out let the card claim a finished round.
          notReached: unansweredAtCloseCount({
            notReachedQuestionIds: result.not_reached_question_ids,
            expiredQuestionId: result.expired_question_id,
            blockSize: range.size,
            answeredInBlock: answeredAtClose,
          }),
          nextIndex: result.next_question_index,
          label: rapidFireLabel,
        });
      } catch (e) {
        console.error('Failed to close rapid-fire block:', e);
        // Let them retry rather than stranding them on a dead timer.
        rapidFireClosedRef.current[blockId] = false;
        setError('Could not finish this section. Please try again.');
      } finally {
        // After the `catch` has released `rapidFireClosedRef`, so the next tick — which is gated on
        // this flag — finds the retry armed rather than a close that suppressed its own way back.
        submitInFlightRef.current = heldByCaller;
      }
    },
    [rapidFire, session, survey, isPreviewMode, rapidFireLabel, setCurrentQuestionIndex],
  );

  /** Performs the advance the close used to do in the same breath. */
  const handleRapidFireContinue = useCallback(async () => {
    const outro = rapidFireOutro;
    if (!outro || !survey?.questions) return;
    setRapidFireOutro(null);
    if (outro.nextIndex >= survey.questions.length) {
      await handleCompleteSession();
    } else {
      setCurrentQuestionIndex(outro.nextIndex);
    }
  }, [rapidFireOutro, survey, handleCompleteSession, setCurrentQuestionIndex]);

  // Latest rapid-fire snapshot for the interval callback, which would otherwise close over a
  // stale one. Assigned in an effect, never during render.
  const rapidFireRef = useRef(rapidFire);
  useEffect(() => {
    rapidFireRef.current = rapidFire;
  }, [rapidFire]);

  /** A spoken answer in THIS block still awaiting its words. */
  const rapidFireTranscriptOutstanding = useMemo(() => {
    const blockId = rapidFire?.blockId;
    if (!blockId || !survey?.questions || outstandingTranscripts.size === 0) return false;
    return survey.questions.some(
      (q: { id: string; block_id?: string | null }) =>
        String(q.block_id ?? '') === blockId && outstandingTranscripts.has(String(q.id)),
    );
  }, [rapidFire?.blockId, survey, outstandingTranscripts]);

  /** What the tick needs about the answer on screen, in one ref so it cannot close over stale
   *  values. No "mid-answer": since ADR-0006 the question always stays. */
  const answerProgressRef = useRef({
    transcriptOutstanding: false,
    questionId: null as string | null,
  });
  useEffect(() => {
    answerProgressRef.current = {
      transcriptOutstanding: rapidFireTranscriptOutstanding,
      questionId: currentQuestion?.id ? String(currentQuestion.id) : null,
    };
  }, [rapidFireTranscriptOutstanding, currentQuestion]);

  /**
   * Has this block ended? Driven from the timer tick rather than a render effect, so the check
   * runs when time actually passes instead of on every re-render.
   *
   * Deliberately does NOT fire while a submit is in flight: the in-flight answer has to land
   * first, or close would record the question the respondent just answered as never completed.
   *
   * And on EXPIRY it also declines while the question on screen is still unanswered — a different
   * moment from the in-flight one above, and the one the buzzer used to cut through. Only expiry:
   * the cap is reached BY an answer landing, so there is never a question owed an answer, and
   * holding it would leave a respondent answering a round that has already had every answer it
   * asked for.
   */
  const evaluateRapidFireClose = useCallback(() => {
    const rf = rapidFireRef.current;
    if (!rf?.started || submitInFlightRef.current) return;
    const { close, reason } = shouldCloseRapidFire({
      remaining: rf.remaining,
      answeredInBlock: rf.answeredInBlock,
      blockSize: rf.range.size,
      maxQuestions: rf.config.max_questions,
    });
    if (!close || !reason) return;

    if (reason === 'expired') {
      const { wait, overrun } = shouldFinishTheQuestionOnScreen({
        nowMs: Date.now(),
        overrun: rapidFireOverrunRef.current[rf.blockId] ?? null,
        currentQuestionId: answerProgressRef.current.questionId,
        transcriptOutstanding: answerProgressRef.current.transcriptOutstanding,
      });
      // Stored before the decision is acted on, so the grace is stamped once for the block rather
      // than re-stamped by every tick that finds them still typing.
      if (overrun) rapidFireOverrunRef.current[rf.blockId] = overrun;
      if (wait) return;
    }

    handleCloseRapidFire(reason);
  }, [handleCloseRapidFire]);

  // Tick once a second while a rapid-fire block is live. Only the tick is local — remaining time
  // is always recomputed from the server deadline, so a throttled background tab catches up on
  // its next tick instead of drifting. The timeout(0) covers resuming into an already-expired
  // block: close promptly rather than showing a dead timer for a second.
  useEffect(() => {
    // Stops at the closing card too: the round is over, so a clock that keeps recomputing it is
    // re-rendering the whole session once a second to move a number nobody can see.
    if (!rapidFireActive || !rapidFire?.endsAt || rapidFireOutro) return;
    const immediate = setTimeout(evaluateRapidFireClose, 0);
    const t = setInterval(() => {
      setRapidFireNow(Date.now());
      evaluateRapidFireClose();
    }, 1000);
    return () => {
      clearTimeout(immediate);
      clearInterval(t);
    };
  }, [rapidFireActive, rapidFire?.endsAt, rapidFireOutro, evaluateRapidFireClose]);

  const progress = useMemo(
    () =>
      computeSurveyProgress({
        questions: survey?.questions ?? [],
        blocks: survey?.blocks ?? null,
        responses: (session?.responses ?? []) as unknown as SessionResponse[],
        followupsAnswered,
        followupActive: !!followupQuestion,
      }),
    [survey, session, followupsAnswered, followupQuestion],
  );

  // Tracks whether the CURRENT showTextInput value was forced by a question's author (via a
  // disable_* flag) rather than chosen by the respondent. Lets us clear an author-forced channel
  // when the next question has no restriction, without clobbering a respondent's own toggle.
  const inputChannelAuthorForcedRef = useRef(false);

  // Force the input channel when the author disabled one for this question. When neither flag is
  // set, return to the default IF the prior value was author-forced; otherwise leave it as-is so a
  // respondent's own text/voice toggle carries over (preserving their prior choice).
  useEffect(() => {
    if (!currentQuestion) return;
    // Overrides the author's channel choice, including disable_text_input: if transcription is
    // down, typing is the only way left to answer, so blocking it would strand the respondent.
    if (transcriptionUnavailable) {
      setShowTextInput(true);
      return;
    }
    if (currentQuestion.disable_voice_input) {
      setShowTextInput(true); // voice disabled -> force text
      inputChannelAuthorForcedRef.current = true;
    } else if (currentQuestion.disable_text_input) {
      setShowTextInput(false); // text disabled -> force voice
      inputChannelAuthorForcedRef.current = true;
    } else if (inputChannelAuthorForcedRef.current) {
      // Previous question author-forced a channel; this one imposes no restriction, so revert to
      // the voice-first default instead of carrying the forced value over.
      setShowTextInput(false);
      inputChannelAuthorForcedRef.current = false;
    }
    // else: no restriction now and none carried over -> keep the respondent's current choice.
  }, [currentQuestion, transcriptionUnavailable]);

  // Memoized randomized choices for the current question to prevent re-shuffling on re-renders
  const randomizedChoices = useMemo(() => {
    if (!currentQuestion) {
      return [];
    }

    const originalChoices = currentQuestion.choices || [];
    const shouldRandomize = currentQuestion.randomize_choices;
    const hasOther = currentQuestion.allow_other;

    if (!shouldRandomize || originalChoices.length <= 1) {
      return originalChoices;
    }

    return randomizeChoices(originalChoices, hasOther);
  }, [currentQuestion, randomizeChoices]);

  /**
   * The multi-select ceiling for the current question (spec 36), or null for "no limit".
   * Computed once here because three separate scopes need the same answer: the option renderers,
   * the Next-button gate, and the question text the respondent reads.
   *
   * UNCONDITIONAL, by decision, and the mitigation for that lives in the data rather than here.
   *
   * The risk is real and measured: 19 capped multiple_choice questions across 13 already-fielding
   * PROD surveys, 12 of them with no textual support at all, 451 of 1,543 stored answers (29.2%)
   * already over their cap. Enforcing changes the live question text, adds a counter and greys out
   * options mid-study, splitting one fielded question into two response regimes.
   *
   * Three ways to gate it were weighed here — an author-confirmed marker, "no responses yet", and
   * a dark flag — and all three are rejected for the same reason: the SERVER enforces with no gate
   * whatsoever. `_enforces_selection_cap` (services_fastapi/survey_service.py) asks only whether
   * the question is a multiple_choice carrying a positive integer cap, and rejects an over-cap
   * submission with a 400. A browser rule narrower than that does not soften the change, it hides
   * it: the options stay clickable, the respondent picks five, and the refusal arrives from the
   * server mid-survey with nothing on screen that could have prevented it. That is precisely the
   * failure the PR's deploy order (frontend first, backend second) exists to prevent, so a
   * narrower browser rule is strictly the worse outcome, not the safer one.
   *
   * The gate that IS safe is the absence of a cap. backend#691 clears the caps no author ever
   * confirmed on surveys that are already collecting; once cleared, `selectionCap` returns null
   * here and the server has nothing to enforce, with no flag and no divergence between the two.
   * That makes #691 a merge blocker for this PR, which the PR body states.
   *
   * `src/__tests__/fixtures/selectionCapContract.json` pins the browser predicate to the
   * server's, case by case, so "never narrower than the server" is checkable rather than merely
   * intended.
   */
  const multiSelectCap = useMemo(() => selectionCap(currentQuestion), [currentQuestion]);

  /** Question-level media_urls in display order; originalIndices[i] is the storage index for slot i (for choice-image mode). */
  const questionMediaDisplay = useMemo(() => {
    if (!currentQuestion?.media_urls?.length) {
      return { orderedUrls: [] as string[], originalIndices: [] as number[] };
    }
    const urls = currentQuestion.media_urls;
    const n = urls.length;
    if (n <= 1) {
      return { orderedUrls: [...urls], originalIndices: n === 1 ? [0] : [] };
    }
    const identity = Array.from({ length: n }, (_, i) => i);
    if (shouldRandomizeMedia(currentQuestion)) {
      const pairs: { url: string; orig: number }[] = urls.map((url: string, orig: number) => ({
        url,
        orig,
      }));
      for (let i = n - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [pairs[i], pairs[j]] = [pairs[j], pairs[i]];
      }
      return {
        orderedUrls: pairs.map((p) => p.url),
        originalIndices: pairs.map((p) => p.orig),
      };
    }
    return { orderedUrls: [...urls], originalIndices: identity };
  }, [
    currentQuestion?.id,
    JSON.stringify(currentQuestion?.media_urls),
    currentQuestion?.randomize_media_order,
    currentQuestion?.image_position_mode,
  ]);

  const orderedQuestionMediaUrls = questionMediaDisplay.orderedUrls;
  const questionMediaOriginalIndices = questionMediaDisplay.originalIndices;

  // Don't let a respondent answer a stimulus they haven't seen: choice buttons submit on click,
  // so while the concept image is still in flight a click records a rating of packaging that
  // never rendered. Fails open (see useStimulusReady).
  // Scoped to the VISIT, not the question: a backward `skip_to` re-enters a question whose stimulus
  // urls are the ones already resolved (and whose fail-open may already have fired), so keyed on
  // the id alone the gate is open on the landing frame over an <img> that has just been remounted.
  // There is no second term to save it here — `max_diff` is in MEDIA_SUPPORTED_TYPES, so a question
  // whose pack shots are its OWN `media_urls` has no per-item gate, and single_choice /
  // multiple_choice / Next never had one.
  const { ready: stimulusReady, markResolved: markStimulusResolved } = useStimulusReady(
    questionVisitKey,
    orderedQuestionMediaUrls,
  );

  // Memoized randomized matrix rows for matrix questions
  const randomizedMatrixRows = useMemo(() => {
    if (!currentQuestion || currentQuestion.type !== 'matrix_rating') {
      return [];
    }

    const originalRows = currentQuestion.matrix_rows || currentQuestion.choices || [];
    const shouldRandomize = currentQuestion.randomize_choices;

    if (!shouldRandomize || originalRows.length <= 1) {
      return originalRows;
    }

    // For matrix questions, randomize rows without excluding any items (no "Other" option)
    return randomizeChoices(originalRows, false);
  }, [currentQuestion, randomizeChoices]);

  // The ONLY thing the MaxDiff design is keyed on, pulled out so the memo below can depend on it
  // rather than on the session object that carries it.
  const sessionId = session?.id;

  // Memoized MaxDiff sets for the current question.
  //
  // Depends on `sessionId`, NOT on `session`. `setSession` installs a freshly parsed object on
  // every submit — and a MaxDiff submits once per SET, so keying on the object rebuilt the BIBD
  // design and re-ran `evaluateMaxDiffDesign` and re-serialised its multi-KB result to the console
  // once per set: the exact per-render cost this memo exists to remove, reintroduced through its
  // own dependency list. Measured at 5 builds across two set advances where the contract is 1.
  // The id is the only field read here and cannot change within a session, so nothing else about
  // the session can legitimately change the design.
  const maxDiffSets = useMemo(() => {
    // Best/worst is ONE screen with every option — no sets to build. Skipping here (rather than
    // after the fact) also skips the BIBD heuristic and its console evaluation log, which this
    // mode has no use for.
    if (
      !currentQuestion ||
      currentQuestion.type !== 'max_diff' ||
      !currentQuestion.choices ||
      sessionId == null ||
      isBestWorstQuestion(currentQuestion)
    ) {
      return [];
    }

    // Use session ID as unique identifier for reproducible randomization per user
    const sets = generateAndPrepareSetsForUser(currentQuestion.choices, sessionId.toString(), 4, 4);

    // Evaluate and log the design quality for this session
    const evaluation = evaluateMaxDiffDesign(sets, currentQuestion.choices);
    console.log(`[MaxDiff Evaluation - Session ${sessionId}]`, evaluation);

    return sets;
  }, [currentQuestion, sessionId]);

  // Reset state when question changes
  // Fold any server-staged adaptive narrowings into the display map whenever the session loads or
  // updates. The submit response delivers the next question's adaptation inline, but a resume /
  // page reload only carries it on session.pending_adaptations — without this, a respondent who
  // reloads on an adapted question would see the ORIGINAL authored text.
  useEffect(() => {
    const pending = session?.pending_adaptations;
    if (!pending || typeof pending !== 'object') return;
    setAdaptedQuestions((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const [qid, ad] of Object.entries(pending)) {
        const text = ad?.adapted_question;
        if (text && next[qid] !== text) {
          next[qid] = text;
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [session]);

  // Keyed on `questionVisit`, not `currentQuestionIndex`: the index is exactly what a same-index
  // re-presentation leaves untouched, and that jump is the case `questionVisit` was introduced for
  // (see QuestionVisit). Everything reset below is per-PRESENTATION state — the MaxDiff set the
  // respondent is on, the answers they have given to it, the conjoint scenario, the question's
  // paint maps — so a navigation the effect cannot see leaves the respondent mid-task on a task
  // that was supposed to restart. `currentQuestion` stays in the list so a question object arriving
  // or changing under a stationary visit still resets; `questionVisit` strictly widens when this
  // fires, since `currentQuestionIndex` is read off it.
  useEffect(() => {
    // No MaxDiff set-index reset here: `currentMaxDiffSetIndex` is DERIVED from a cursor that
    // carries its VISIT (see MaxDiffSetCursor), so it is already 0 in the frame the new question
    // first paints — and in the frame a backward `skip_to` re-enters an answered one. A reset here
    // would land one render too late to be the answer, and would not fire at all for a re-entry
    // that landed on the index it came from.
    setCurrentConjointScenarioIndex(0);
    setMaxDiffResponses({});
    setConjointResponses({});
    setCurrentResponse('');
    setFollowupQuestion(null);

    // Drop the option-media paint marks. NOT what makes the gate correct across a boundary — every
    // key it can read is scoped to the visit and the set (see optionMediaScopeKey), so the new
    // question is gated in the frame it first paints and this reset could only ever land after it.
    // What it buys is a bounded tracker: without it the marks of every question the respondent has
    // answered stay in the map for the length of the session.
    //
    // The earlier claim here — that the CHOICE types depend on it, because their keys repeat and
    // their gate is a count over the whole tracker — was measured and is false. It was true that
    // their keys repeated; it was not true that resetting them was a fix. `disabled` was ADDED to
    // both of question 2's buttons after they had already been committed live over question 1's
    // artwork, which is a paintable frame, and `isSubmitting` was not covering it either
    // (`setIsSubmitting(false)` shares a batch with the advance). The keys now carry the scope and
    // the count is taken within it, so both terms of that gate move in the same commit as the
    // question. See SurveySession.optionMediaQuestionBoundary.test.tsx.
    //
    // PRUNED, not cleared — a bound that cannot reach the question it is running for.
    //
    // The ordering was measured by instrumenting both sides across a question boundary with
    // question 2 served warm: this is a passive effect, so the commit that first paints the new
    // question lands BEFORE it and the tile refs resolve in that commit's layout phase. The
    // reset's own input was observed holding all four marks — question 1's AND question 2's — so
    // `{}` here provably discards marks belonging to the question now ON SCREEN.
    //
    // On an idle machine that is survivable, and the reason is worth knowing: the tile `ref` is an
    // inline arrow, so React re-attaches it on every render and the re-render the reset itself
    // causes re-reports the node, restoring the mark. `{}` is therefore also a guaranteed EXTRA
    // commit on every question change, where the prune returns the same object and lets React bail
    // out. Under load that extra commit is not free — it is the flake:
    //
    //     {} clear   4 failing runs / 16     (2/6 at 2d7dc71, 2/10 with this line reverted)
    //     prune      0 failing runs / 32
    //
    // measured with `vitest run src/__tests__/SurveySession*.test.tsx` against 14 busy cores. One
    // of those runs took 32 tests down together. The suspected test-harness causes (an unstable
    // `useSearchParams` mock, a dirty localStorage) were reverted one at a time and together and
    // did not reproduce it — 0/26 across those three arms. This line is the fix; see
    // SurveySession.optionMediaMarkSurvivesReset.test.tsx for the behaviour it has to preserve.
    const currentId = String(currentQuestion?.id ?? '');
    setOptionMediaLoaded((prev) => keepMarksForQuestion(prev, currentId));
    setOptionMediaErrored((prev) => keepMarksForQuestion(prev, currentId));
    // The question-level trackers are still CLEARED, and cannot be pruned the same way: their keys
    // are `${questionId}-media-${index}` with no visit in them, so "this question's marks" includes
    // the marks of a PREVIOUS visit to it. Keeping those unlocks a backward `skip_to` on arrival,
    // over an <img> that has just been remounted — the exact hole the visit scope closes for the
    // option media. Giving them the visit too is the fix, and is a wider change than this round.
    setQuestionMediaLoaded({});
    setQuestionMediaErrored({});
    // No clock reset here. `question_start_time` and the active-time accumulator are stamped from
    // `presentedQuestionKey` below, which is the moment the question is actually PRESENTED — this
    // effect fires for a question the render chain may still be covering with a spinner, and fires
    // once for a unit that has two clocks (a MaxDiff set, a conjoint scenario).
  }, [questionVisit, currentQuestion]);

  // Keep the submit-time mirror in step with the rendered toggle (post-commit, so a click can
  // never observe an older value than the UI it was aimed at).
  useEffect(() => {
    showTextInputRef.current = showTextInput;
  }, [showTextInput]);

  // Marks one option image resolved (loaded OR errored — an image that 404s is resolved, it just
  // isn't visible). Idempotent, so a repeated onLoad cannot inflate the count.
  const markOptionMediaLoaded = useCallback((mediaKey: string) => {
    setOptionMediaLoaded((prev) => (prev[mediaKey] ? prev : { ...prev, [mediaKey]: true }));
  }, []);

  // Records the failure against the PAINT SLOT's media key. See the state declaration above.
  const markOptionMediaErrored = useCallback((mediaKey: string) => {
    setOptionMediaErrored((prev) => (prev[mediaKey] ? prev : { ...prev, [mediaKey]: true }));
  }, []);

  // A genuine paint also RETRACTS any failure recorded for that slot. A 404 is often transient (a
  // CDN cold miss, a deploy mid-flight), so a flag that only ever latched would go on covering a
  // picture the respondent CAN now be served with an opaque "unavailable" panel they cannot see
  // past. Not folded into markOptionMediaLoaded: the error path calls that one too (to fail the
  // gate open), and the `complete` ref check reads true for a broken image as well.
  //
  // This is the only writer here that is not monotonic, so it is only safe while the key names ONE
  // <img> node. Keyed by item, the two columns' nodes wrote opposite values through the same key on
  // every render and React threw `Maximum update depth exceeded`.
  const markOptionMediaPainted = useCallback(
    (mediaKey: string) => {
      markOptionMediaLoaded(mediaKey);
      setOptionMediaErrored((prev) => (prev[mediaKey] ? { ...prev, [mediaKey]: false } : prev));
    },
    [markOptionMediaLoaded],
  );

  // What the option-media gate is currently waiting on. The MaxDiff set index is part of it
  // because a set advance shows four DIFFERENT items' images without changing the question; the
  // VISIT is part of it because `timedOutOptionMediaScope` is never cleared, so a scope that timed
  // out while the respondent compared four packs would still be latched when a backward `skip_to`
  // brought them back to that same question and set — unlocking the tiles over empty image boxes.
  const optionMediaScopeKey = `${questionVisitKey}#${currentMaxDiffSetIndex}`;
  const optionMediaTimedOut = timedOutOptionMediaScope === optionMediaScopeKey;

  /**
   * The option images the choice types are actually going to mount, DERIVED from the question on
   * screen. It used to be state written by the question-change effect, which is one commit late:
   * the boundary frame compared the new question's marks against the PREVIOUS question's total.
   *
   * Counted the way the render counts them — one per `${choices.indexOf(choice)}-${mediaIndex}` —
   * rather than as every url in `option_media`, because those two differ and only the first is
   * REACHABLE. An author who deletes a choice leaves its media behind, and two choices carrying
   * the same string collapse onto one key; either way the numerator can never catch a denominator
   * taken over the raw map, so the tiles — and, since `answerControlsUnlocked` waits on them, the
   * answer clock — sat out the full 8s fail-open on every presentation of that question.
   *
   * `coversEveryChoice` is the same walk's other product, read only by the answer clock
   * (`inputsGatedByOptionMedia`): the tiles lock INDIVIDUALLY, so a choice with no artwork of its
   * own — or an "Other" box — leaves the question answerable while the gate is still shut, and
   * restarting the clock when it opens would store the whole dwell as the moment after the last
   * image landed.
   *
   * MaxDiff does not read either: a set shows 4 of N items and shows each of them twice, so it
   * gates per paint slot instead (see maxDiffItemImagesReady).
   */
  const optionMediaPlan = useMemo(() => {
    const media = currentQuestion?.option_media;
    const choices: string[] = currentQuestion?.choices || [];
    if (!media || choices.length === 0) return { mountedImageCount: 0, coversEveryChoice: false };

    let mountedImageCount = 0;
    const seenChoiceIndices = new Set<number>();
    // An "Other" box is always answerable, so an option-media gate never covers the question.
    let coversEveryChoice = !currentQuestion?.allow_other;
    for (const choice of choices) {
      const originalIndex = choices.indexOf(choice);
      if (seenChoiceIndices.has(originalIndex)) continue;
      seenChoiceIndices.add(originalIndex);
      const urls: string[] = media[String(originalIndex)] || media[originalIndex] || [];
      if (urls.length === 0) coversEveryChoice = false;
      mountedImageCount += urls.length;
    }
    return { mountedImageCount, coversEveryChoice };
  }, [currentQuestion]);
  const totalOptionMedia = optionMediaPlan.mountedImageCount;
  const optionMediaCoversEveryChoice = optionMediaPlan.coversEveryChoice;

  // ...and how many of THIS visit's have painted. Counted within the scope rather than over the
  // whole tracker, for the other half of the same reason: the previous question's marks are still
  // in the map in the frame the new question first paints, and `${choiceIndex}-${mediaIndex}`
  // repeats on every question, so an unscoped count reads them as this question's.
  //
  // Derived, not a counter: marking is keyed by mediaKey and therefore idempotent. A bare
  // `count + 1` per onLoad could be double-fired for one image and tip the gate open while a
  // different image was still in flight.
  const loadedOptionMediaCount = useMemo(() => {
    const prefix = choiceOptionMediaScopePrefix(optionMediaScopeKey);
    return Object.keys(optionMediaLoaded).filter((key) => key.startsWith(prefix)).length;
  }, [optionMediaLoaded, optionMediaScopeKey]);

  // Fail open: if the option images never resolve (cached image that fired onLoad before React
  // attached it, dead CDN, ...), unlock rather than strand the respondent on a dead question.
  useEffect(() => {
    if (totalOptionMedia === 0) return;
    const timer = setTimeout(
      () => setTimedOutOptionMediaScope(optionMediaScopeKey),
      STIMULUS_LOAD_TIMEOUT_MS,
    );
    return () => clearTimeout(timer);
  }, [optionMediaScopeKey, totalOptionMedia]);

  // Helper function to evaluate percentage allocation conditions
  const evaluatePercentageCondition = useCallback(
    (responseData: any[], condition: any): boolean => {
      // Extract the dict value from original_response
      const originalResponse = responseData?.find((item: any) => item.type === 'original_response');
      if (!originalResponse?.value || typeof originalResponse.value !== 'object') return true;

      const allocations = originalResponse.value as Record<string, number>;
      const optionValue = allocations[condition.option_key];

      if (optionValue === undefined) return true; // Option not found, show by default

      const threshold = condition.threshold ?? 0;

      switch (condition.operator) {
        case '>':
          return optionValue > threshold;
        case '>=':
          return optionValue >= threshold;
        case '=':
          return optionValue === threshold;
        case '<':
          return optionValue < threshold;
        case '<=':
          return optionValue <= threshold;
        default:
          return true;
      }
    },
    [],
  );

  // Helper function to evaluate choice-based conditions
  const evaluateChoiceCondition = useCallback((responseData: any[], condition: any): boolean => {
    const extractSelectedValues = (data: any[]): string[] => {
      if (!data || !Array.isArray(data)) return [];
      const values: string[] = [];
      // Handle all choice-based question types that can be used as display condition sources
      const choiceTypes = [
        'single_choice',
        'multiple_choice',
        'yes_no',
        'dropdown',
        'original_response',
      ];
      for (const item of data) {
        if (choiceTypes.includes(item.type)) {
          values.push(String(item.value));
        }
      }
      return values;
    };

    const selectedValues = extractSelectedValues(responseData);

    if (condition.operator === 'selected') {
      return selectedValues.includes(condition.value);
    } else if (condition.operator === 'not_selected') {
      return !selectedValues.includes(condition.value);
    }
    return true;
  }, []);

  const evaluateSingleCondition = useCallback(
    (condition: any, responses: any[]): boolean => {
      const sourceResponse = responses?.find(
        (r: any) => r.question_id === condition.source_question_id,
      );
      // A source question that was skipped (conditionally or by AI) was never genuinely
      // answered, so a downstream condition referencing it is undecided. Treat undecided as
      // "show" (never hide a question by accident — that would silently drop data), matching
      // the backend / surveyProgress 'undecided' handling instead of evaluating a stale/empty
      // skipped value.
      const SKIPPED_SOURCE_STATES = ['conditionally_skipped', 'ai_skipped', 'skipped'];
      if (sourceResponse && SKIPPED_SOURCE_STATES.includes(sourceResponse.state)) return true;
      if (!sourceResponse?.response_data) return true;

      const isPercentageCondition = ['>', '>=', '=', '<', '<='].includes(condition.operator);
      if (isPercentageCondition) {
        return evaluatePercentageCondition(sourceResponse.response_data, condition);
      } else {
        return evaluateChoiceCondition(sourceResponse.response_data, condition);
      }
    },
    [evaluatePercentageCondition, evaluateChoiceCondition],
  );

  // Evaluate display conditions -- supports compound AND/OR conditions
  const evaluateDisplayCondition = useCallback(
    (question: any, responses: any[]): boolean => {
      if (!question?.display_condition) return true;

      const dc = question.display_condition;

      // New compound format: { logic, conditions[] }
      if (dc.conditions && Array.isArray(dc.conditions) && dc.conditions.length > 0) {
        const logic = dc.logic || 'and';
        if (logic === 'or') {
          return dc.conditions.some((c: any) => evaluateSingleCondition(c, responses));
        }
        return dc.conditions.every((c: any) => evaluateSingleCondition(c, responses));
      }

      // Legacy flat format: { source_question_id, operator, value, ... }
      if (dc.source_question_id) {
        return evaluateSingleCondition(dc, responses);
      }

      return true;
    },
    [evaluateSingleCondition],
  );

  // Evaluate block display condition - checks if a block should be shown based on its condition
  const evaluateBlockDisplayCondition = useCallback(
    (blockId: string | undefined, responses: any[]): boolean => {
      if (!blockId || !survey?.blocks) return true;

      const block = survey.blocks.find((b: any) => b.id === blockId);
      if (!block?.display_condition) return true;

      const dc = block.display_condition;

      // Compound format: { logic, conditions[] }
      if (dc.conditions && Array.isArray(dc.conditions) && dc.conditions.length > 0) {
        const logic = dc.logic || 'and';
        if (logic === 'or') {
          return dc.conditions.some((c: any) => evaluateSingleCondition(c, responses));
        }
        return dc.conditions.every((c: any) => evaluateSingleCondition(c, responses));
      }

      // Legacy flat format: { source_question_id, operator, value, ... }
      // Mirror the question-level evaluator so legacy-flat block conditions are honored.
      if (dc.source_question_id) {
        return evaluateSingleCondition(dc, responses);
      }

      return true;
    },
    [survey, evaluateSingleCondition],
  );

  /**
   * Check if current question should be skipped (memoized to prevent flashing).
   *
   * Gated on the pre-question screens because being true both SUBMITS a response (the effect
   * below) and paints a spinner over whatever is on screen (the early return in the render).
   * Consent especially: it is returned later in the render chain than the skip spinner, so
   * without this gate a respondent resuming onto a question that fails its display condition
   * had a response written for them and never saw the consent terms at all.
   */
  const shouldSkipCurrentQuestion = useMemo(() => {
    if (!session || !survey || !currentQuestion) return false;
    if (showWelcomeScreen || showConsentScreen) return false;

    // Check block-level condition first
    if (
      currentQuestion.block_id &&
      !evaluateBlockDisplayCondition(currentQuestion.block_id, session.responses || [])
    ) {
      return true;
    }

    // Then check question-level condition
    if (!currentQuestion.display_condition) return false;
    return !evaluateDisplayCondition(currentQuestion, session.responses || []);
  }, [
    session,
    survey,
    currentQuestion,
    showWelcomeScreen,
    showConsentScreen,
    evaluateDisplayCondition,
    evaluateBlockDisplayCondition,
  ]);

  /**
   * Is the question itself what the respondent is looking at right now?
   *
   * This is the negation of the render's early-return chain, term for term and in the same order:
   * loading spinner, termination screen, conditional-skip spinner, bot challenge, geo gate,
   * no-session/no-survey, welcome, consent, the conversational takeover, no-question, and the
   * three flags that replace the question with a spinner (`:4916`). Nothing may be added to that
   * chain without a term here — the point of the predicate is that it is a TOTAL description of
   * "the question is on screen", not a partial one.
   *
   * The spinner flags are what a multi-step question needs most: `advanceMultiStep?.()` commits
   * the next scenario index and `setIsAnalyzing(true)` lands in the same batch, both BEFORE
   * `await analyzeResponseForFollowup` — so without them the next scenario's clock started while
   * the analysis spinner was up and the whole 5-20s AI wait was billed to a screen nobody had
   * seen yet.
   */
  /**
   * Whether the voice-moderator flow is the thing on screen.
   *
   * Mirrors the render branch below exactly. Read here because that flow supplies its OWN nudge
   * slot and needs no `currentQuestion` behind it — which matters because `survey.questions` is
   * legitimately `[]` on such a survey (the researcher UI treats `[] + conversational` as valid).
   */
  const isConversationalRuntime =
    survey?.survey_style === CONVERSATIONAL_SURVEY_STYLE &&
    Boolean(session) &&
    (session?.id ?? 0) > 0;

  // Excludes the conversational runtime: `questions` is legitimately [] there, so 0 >= 0 would read as finished.
  const isQuestionnaireFinished =
    !isConversationalRuntime &&
    !!survey?.questions &&
    currentQuestionIndex >= survey.questions.length;

  // A session resumed past the last question has nothing to render — otherwise "Loading question..." forever.
  const resumePastEndCompletionRef = useRef(false);
  useEffect(() => {
    if (!session || isTerminated || !isQuestionnaireFinished) return;
    if (resumePastEndCompletionRef.current) return;
    resumePastEndCompletionRef.current = true;
    void handleCompleteSession();
  }, [session, isTerminated, isQuestionnaireFinished, handleCompleteSession]);

  const questionIsPresented =
    !isLoading &&
    !isTerminated &&
    !shouldSkipCurrentQuestion &&
    !isSkippingConditional &&
    !showChallenge &&
    geoGateMessage === null &&
    !!session &&
    !!survey &&
    !!currentQuestion &&
    !showWelcomeScreen &&
    !showConsentScreen &&
    !isConversationalRuntime &&
    !showRapidFireIntro &&
    !showRapidFireBeat &&
    !rapidFireOutro &&
    !isAnalyzing &&
    !isCheckingSkip &&
    !showThinking;

  /**
   * Is the question ANSWERABLE yet, or is it on screen with its answer inputs still locked?
   *
   * Two media gates disable answer controls while the question already renders, and both
   * intentionally: a respondent must not rate packaging they have not seen. `stimulusReady` is the
   * question-level concept image (`useStimulusReady`, 8s fail-open); `optionMediaSettled` is the
   * per-option pack shots.
   *
   * The clock may only wait for a gate that actually LOCKED this question's INPUTS. Waiting on one
   * that did not is the same fault in the other direction, and the direction that under-reports:
   * `yes_no`, `rating_scale`, `likert_scale`, `dropdown` and `nps` auto-submit from a button
   * carrying only `disabled={isSubmitting}` (frontend#632), and `long_text` / `matrix_rating` hold
   * only their Next while every cell and the textarea stay live — so the respondent does all the
   * work during the wait and answers the instant it paints. Restarting the clock there stores a
   * 7.3s dwell as 0.3s, under the type's floor, and invents the very `rapid_completion` this
   * change exists to stop inventing. `resetActiveTime()` would go with it, discarding the only
   * evidence that contradicts the reading.
   *
   * So each gate is applied exactly where the render applies it, and both feed the controls below
   * from here rather than being recomputed beside them — the clock and the buttons have to read
   * the same thing. When #632 gates the five auto-submit types, add them to
   * ANSWER_FIELDSET_TYPES in the same commit and this follows automatically.
   */
  const questionHasOptionMedia =
    !!currentQuestion?.option_media && Object.keys(currentQuestion.option_media).length > 0;
  const optionMediaSettled = optionMediaTimedOut || loadedOptionMediaCount >= totalOptionMedia;
  const questionMediaIsQuestionLevel =
    !!currentQuestion?.media_urls?.length &&
    (currentQuestion.media_display_mode || DEFAULT_MEDIA_DISPLAY_MODE) ===
      DEFAULT_MEDIA_DISPLAY_MODE;
  // Every input is inside `<fieldset disabled={!answerControlsUnlocked}>` — both gates lock.
  const inputsInGatedFieldset =
    !!currentQuestion &&
    ANSWER_FIELDSET_TYPES.includes(currentQuestion.type) &&
    questionMediaIsQuestionLevel;
  // Media tiles lock individually, so they cover the question only when every choice has one.
  const inputsGatedByOptionMedia =
    !!currentQuestion &&
    ANSWER_FIELDSET_TYPES.includes(currentQuestion.type) &&
    !questionMediaIsQuestionLevel &&
    optionMediaCoversEveryChoice;
  const answerControlsUnlocked =
    (!inputsInGatedFieldset || (stimulusReady && optionMediaSettled)) &&
    (!inputsGatedByOptionMedia || optionMediaSettled);

  /**
   * Identity of the thing on screen right now, or null when nothing is. MaxDiff sets and
   * conjoint scenarios are included because each one submits its own response row and therefore
   * needs its own clock.
   *
   * `answerControlsUnlocked` is part of the identity rather than a gate on the whole key, which
   * is what makes the locked window safe: the question is stamped when it is PRESENTED, and
   * stamped again when it becomes ANSWERABLE. So a submit that somehow escapes the gates still
   * carries this question's presentation time (never the previous question's), while an answer
   * given normally is measured from the moment the respondent could first give it.
   *
   * The VISIT leads the key because the guard below compares it BY VALUE, and every other term is
   * identical on both sides of a jump that lands on the question it left (see QuestionVisit). Read
   * without it, a second presentation is indistinguishable from the first one never having left —
   * which is the case the guard exists to suppress — so the re-answer was billed the first answer's
   * dwell plus everything in between.
   */
  const presentedQuestionKey = questionIsPresented
    ? `${questionVisit.visit}:${currentQuestionIndex}:${currentQuestion.id}:${currentMaxDiffSetIndex}:${currentConjointScenarioIndex}:${answerControlsUnlocked}`
    : null;

  /**
   * Start the per-question clock the moment the question is PRESENTED — not when the survey data
   * arrives. Q1 used to be stamped on load, so it carried the welcome and consent reading time,
   * and the first question of a rapid-fire block was stamped while the intro screen was still up.
   * Restart it when the media gates unlock, for the same reason: until they do, the answer the
   * clock is timing cannot be given. A 2 MB stimulus on a slow connection otherwise buys up to
   * the full 8s fail-open timeout of padding, and an answer given the instant the buttons unlock
   * is banked as considered when it was rushed — `single_choice`'s 2.0s rapid-completion floor
   * cannot fire under it.
   *
   * The two instruments are reset together, on purpose: `question_start_time` and the active-time
   * accumulator describe the same window, and every downstream check reconciles one against the
   * other. Anything that restarts one must restart the other.
   *
   * Stamped once per UNIT, not once per transition into it. The spinner terms in
   * `questionIsPresented` take the question off screen and put the SAME one back — a submit in
   * flight, the redundancy look-ahead, an AI analysis that returns no follow-up — and re-stamping
   * on the way back would trim that wait off the row that finally lands. That is exactly the
   * mid-flight re-stamp the conjoint fix removed, and frontend#633 (which owns the in-flight wait
   * itself) says in as many words that it must not be "fixed" that way.
   */
  const lastStampedQuestionKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (presentedQuestionKey === null) return;
    if (presentedQuestionKey === lastStampedQuestionKeyRef.current) return;
    lastStampedQuestionKeyRef.current = presentedQuestionKey;
    questionStartTimeRef.current = new Date().toISOString();
    resetActiveTime();
  }, [presentedQuestionKey, resetActiveTime]);

  // Evaluate display conditions and skip questions that don't meet conditions
  useEffect(() => {
    const skipConditionalQuestion = async () => {
      if (!shouldSkipCurrentQuestion || isSubmitting || isSkippingConditional) return;
      // Past the server's buzzer this chain writes nothing and cannot advance, and it re-arms
      // itself the moment `isSkippingConditional` clears — so without this it would re-submit the
      // same discarded skip for as long as the tab stayed open, once per round trip, on the
      // unauthenticated respondent path.
      if (timerExpiredByServer) return;

      // Don't re-skip a question the backend already wrote a response for (e.g. ai_skipped).
      // Backend-written responses are authoritative; acting here would double-submit.
      const alreadyHandled = (session?.responses || []).some(
        (r) => r.question_id === currentQuestion?.id,
      );
      if (alreadyHandled) return;
      // The synchronous half of the guard the two respondent-driven write paths already hold.
      // `isSubmitting` above is STATE, and the ref exists precisely for the window where state
      // has not committed yet — so relying on the state alone let this chain take a flag another
      // write was holding and then clear it in its own `finally`, under a write still in flight.
      // That flag is what `submitToSettle` polls, so clearing it early is what lets the hard stop
      // complete the session on top of that write.
      //
      // Declines rather than waits: nothing here needs doing this pass, and the effect re-arms on
      // the next dep change — which the holder's own `isSubmitting` produces when it finishes.
      // Must come BEFORE `isSkippingConditional` is set, or the decline would pin the skip
      // spinner on a flag nothing gives back.
      if (submitInFlightRef.current) return;

      setIsSkippingConditional(true);

      // Preview mode: just skip locally without API calls
      if (isPreviewMode) {
        if (currentQuestionIndex + 1 < survey!.questions.length) {
          setCurrentQuestionIndex(currentQuestionIndex + 1);
        } else {
          setIsTerminated(true);
          setTerminationReason('completed');
        }
        setIsSkippingConditional(false);
        return;
      }

      // This chain WRITES — a real response, and on the last question a completion — so the hard
      // stop has to be able to see it. It set only `isSkippingConditional`, which the nudge slot
      // reads but `submitToSettle` does not, so a stop landing mid-chain resolved immediately and
      // completed the session out from under a write that was still going.
      submitInFlightRef.current = true;
      try {
        // A conditionally skipped question was never put in front of anyone, so it has no
        // duration — and BOTH available answers are lies. `questionStartTimeRef` still holds the
        // PREVIOUS question's start (reaching this line requires `shouldSkipCurrentQuestion`,
        // which `questionIsPresented` excludes, so the presentation effect provably did not stamp
        // for this one), which charges the skip that question's dwell time. Stamping "now"
        // instead reports ~0.05s — under every floor in the backend's rapid-completion table
        // (models_postgres/survey_sessions.py::_detect_quality_issues, 1.5-8s by type), which
        // reads response_time_seconds without ever looking at the response's state. Since
        // SessionResponses.tsx renders quality_flags on every row, that puts an amber "Rapid
        // Completion" warning in front of a researcher for a question nobody was shown.
        //
        // So send neither instrument. With no question_start_time the backend leaves
        // response_time_seconds NULL and derives no flags — "not measured", which is the truth.
        // The accumulator needs no reset here either: the next question to be PRESENTED resets
        // both, and this row carries nothing from it.
        const result = await submitResponse(
          session!.id,
          currentQuestion.id,
          { skipped_reason: 'conditionally_skipped' },
          currentQuestion.type,
          undefined,
          true,
          null,
          null,
        );

        // Accepted, recorded nothing: the hard stop had already passed. Advancing here would walk
        // the respondent through the rest of the survey with none of it being saved.
        if (isTimerExpiredWrite(result)) {
          setTimerExpiredByServer(true);
          setError(UI_TEXT.HUMAN_SURVEY.SESSION.ERRORS.TIME_UP);
          return;
        }

        setSession(result.session_data);

        if (currentQuestionIndex + 1 < survey!.questions.length) {
          setCurrentQuestionIndex(currentQuestionIndex + 1);
        } else {
          await handleCompleteSession();
        }
      } catch (error) {
        console.error('Failed to skip conditional question:', error);
        // Surface the failure like every other submit path so the respondent isn't left
        // stuck on a bare skip spinner with no feedback.
        setError('Failed to load the next question. Please try again.');
      } finally {
        submitInFlightRef.current = false;
        setIsSkippingConditional(false);
      }
    };

    skipConditionalQuestion();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    shouldSkipCurrentQuestion,
    currentQuestionIndex,
    isSubmitting,
    isSkippingConditional,
    timerExpiredByServer,
  ]);

  // ------------------------------------------------------------------ survey-wide clock (spec 35)

  /**
   * Resolve once nothing is being submitted.
   *
   * Polls the ref rather than holding the submit's own promise: `submitInFlightRef` is cleared
   * from half a dozen `finally` blocks and early returns, and a second handle beside it would
   * fall out of step with one of them. Bounded by the same budget the hard stop gives its other
   * last write, so a submit that hangs cannot leave this polling for the rest of the session.
   */
  const submitToSettle = useCallback(
    () =>
      new Promise<void>((resolve) => {
        if (!submitInFlightRef.current) {
          resolve();
          return;
        }
        const startedAtMs = Date.now();
        const id = window.setInterval(() => {
          if (submitInFlightRef.current && Date.now() - startedAtMs < BEFORE_HARD_STOP_TIMEOUT_MS) {
            return;
          }
          window.clearInterval(id);
          resolve();
        }, SUBMIT_SETTLE_POLL_MS);
      }),
    [],
  );

  // Declared here, below the conditional-skip memo it reads, rather than beside the rapid-fire
  // code it also reads. Everything that can stand in for the question body has to be visible to
  // it, and a nudge slot that knows about only some of them is the defect this keeps re-having.

  /**
   * Whether there is anywhere on screen to put a nudge right now.
   *
   * Every state below replaces the question body, and the nudge is rendered inside that body and
   * nowhere else. A mark raised with no slot used to be recorded as seen and then dismissed
   * unseen — the five-minute warning crossed inside a four-minute rapid-fire block was gone for
   * good — so the hook holds it until this turns back on.
   */
  const canShowNudge = nudgeSlotAvailable({
    // Deliberately WITHOUT the AI spinner, the skip check, the thinking delay and the rapid-fire
    // intro, all four of which used to be here. The live region has been hoisted above every one
    // of them, so a mark crossed while one is up has somewhere to go — and holding it anyway is
    // the warning lost, through the door the hoist opened.
    rapidFireActive,
    // A chain of conditionally-skipped questions spanning a threshold ate the warning outright:
    // the runtime early-returns a bare spinner for the whole chain, however long the submits take.
    skipping: shouldSkipCurrentQuestion || isSkippingConditional,
    // The CAPTCHA and the geo soft-gate each replace the entire survey, not just its body.
    gated: showChallenge || geoGateMessage !== null,
    // `currentQuestion` is null for the whole of a conversational survey — it has no authored
    // questions to index into — and the flow renders the nudge itself. Keyed on that alone, every
    // mark on such a survey was raised with `raisedAtMs: null` and held for ever, so the
    // respondent got the readout and not one warning before the hard stop. That is the silent
    // stop this design exists to prevent, reintroduced by the fix for the opposite failure.
    hasQuestion: Boolean(currentQuestion) || isConversationalRuntime,
  });

  const deadline = useSurveyDeadline({
    sessionId: session?.id ?? null,
    config: isPreviewMode ? null : surveyTimerConfig,
    // The clock starts at the first question, never on page load: time spent reading the welcome
    // and consent screens is not the respondent's answering time.
    started: Boolean(session && !showWelcomeScreen && !showConsentScreen && !isTerminated),
    canShowNudge,
    // Recording a voice answer fires no input events at all, so without this a respondent
    // talking for three minutes reads to the server as three minutes of absence. BOTH bodies
    // count: `isRecording` is the traditional question's microphone, and the conversational flow
    // keeps its own — reading only the first left the voice-first runtime, the one this matters
    // most on, reporting every answer it collected as an absence of exactly that length.
    engaged: isRecording || isRecordingConversational,
    // The server has already begun discarding this respondent's answers, so its deadline is a
    // fact and our interpolation is an estimate — one that can be a long way behind it, since the
    // server falls back to a clock derived from the session's created_at. Waiting for our own
    // clock to agree would leave them on a "time is up" notice with nothing to end the session.
    serverSaysExpired: timerExpiredByServer,
    // /timer/expire COMPLETES the session, and every other session write then refuses it — so
    // this is the last moment anything belonging to this session can be recorded, and everything
    // still owed to it goes here.
    beforeHardStop: async ({ grantable }) => {
      // The answer already on its way, first. A respondent who pressed Next at 0:01 has a submit
      // in flight when the tick reaches zero; without this the session was completed underneath
      // it, the backend refused the write, and their last answer was discarded with "Failed to
      // submit response…" flashing past on the way to the redirect.
      //
      // This and the block close below SHARE the hook's single ten-second budget rather than
      // getting one each, and the answer goes first deliberately: it is the respondent's own
      // words, where the block close is bookkeeping about questions they never reached.
      //
      // Waited for on EVERY ask, including one that cannot be granted: it finishes a write that is
      // already in the air rather than starting one, and the ask behind it is the same ask either
      // way.
      await submitToSettle();

      // The block close is the opposite kind of write, and only belongs on an ask that can
      // actually end the session. It is irreversible — `close_rapid_fire` stamps `closed_at` and
      // advances the session past the block — while the FIRST ask of every hard stop is refused by
      // construction (the client asks at its own zero; the server grants only past its five-second
      // grace). Run unconditionally, it shut the section the respondent was sitting in and then
      // left them in it: five more seconds of grace inside a closed block on the ordinary path,
      // and on a terminal refusal — the researcher switching to guidance seconds earlier — for the
      // whole rest of the survey, with the block's remaining questions unreachable and its own
      // timer unable to close it a second time.
      //
      // Not deferred to after the grant instead: /timer/expire COMPLETES the session, and
      // `close_rapid_fire` refuses anything that is not in progress. This is the last moment the
      // block can be recorded at all — it is simply not every ask's moment.
      //
      // The residual case, weighed and accepted: if the server ever GRANTS a first ask — its
      // clock more than the grace ahead of our interpolation, which needs a device-clock anomaly,
      // since ours is derived from its own last reading — this block is never recorded. That
      // costs the researcher one block's "not reached" bookkeeping on a session that is ending
      // anyway. Closing on every ask cost the RESPONDENT the rest of the section, every time.
      if (!grantable) return;

      const rf = rapidFireRef.current;
      if (!session || isPreviewMode || !rf?.started) return;
      if (rapidFireClosedRef.current[rf.blockId]) return;
      rapidFireClosedRef.current[rf.blockId] = true;
      try {
        await closeRapidFireBlock(session.id, rf.blockId, 'expired');
      } catch (e) {
        console.error('Failed to close rapid-fire block on hard stop:', e);
        rapidFireClosedRef.current[rf.blockId] = false;
      }
    },
    // A hard stop ends the session server-side, so it has to finish the same way a normal
    // completion does: drop the saved session, and honour the panel redirect the server returned
    // with it. A timed-out respondent answered in good faith and still has to be credited.
    onHardStop: (result) => {
      if (!session) return;
      clearSavedSession(session.survey_id);
      if (result.redirect_url) {
        window.location.href = result.redirect_url;
        return;
      }
      // The flag is what tells that page this session ended on the buzzer. Built from the same
      // pair the page reads it back with, because for twelve rounds it was written here and read
      // nowhere — so a respondent stopped at question 12 of 40 was congratulated on completing.
      //
      // And whether the stop was actually PAID for travels with it. That page tells the
      // respondent their answers have been saved, which is a claim about the database: false for
      // a session the server retired unpaid (`credited: false`, no redirect, nothing recorded)
      // and false for one the ask could never reach at all, which is still `in_progress` and
      // waiting for the abandonment sweep.
      navigate(timedOutCompletionPath(session.id, isCreditedCompletion(result)));
    },
  });

  // Auto-dismiss the AI-skip acknowledgement toast after a few seconds.
  useEffect(() => {
    if (!aiSkipNotice) return;
    const t = setTimeout(() => setAiSkipNotice(null), 3000);
    return () => clearTimeout(t);
  }, [aiSkipNotice]);

  // Start (or resume) the respondent session, running the backend fraud gate.
  // Re-invokable so the geo soft-gate can retry and the bot challenge can re-submit
  // with a Turnstile token. Reads survey/tracking context captured at load time.
  const attemptStartSession = useCallback(
    async (turnstileToken?: string) => {
      const ctx = sessionStartCtxRef.current;
      if (!ctx) return;

      try {
        const signals = await collectClientSignals();
        const sessionResult = await startSurveySession(
          ctx.surveyId,
          ctx.trackingId,
          signals,
          turnstileToken,
        );

        if (!sessionResult) {
          setError('Failed to start survey. Please try again.');
          return;
        }

        // Fraud-gate outcomes (returned before a session exists)
        if (sessionResult.status === 'blocked') {
          setShowChallenge(false);
          setGeoGateMessage(null);
          setIsTerminated(true);
          setTerminationReason('automation_blocked');
          return;
        }
        if (sessionResult.status === 'challenge_required') {
          setGeoGateMessage(null);
          // Backend requires a Turnstile challenge but no site key is configured to
          // render one — surface an error instead of looping on a token-less retry.
          if (!import.meta.env.VITE_TURNSTILE_SITE_KEY) {
            setError(UI_TEXT.HUMAN_SURVEY.FRAUD_GATE.CHALLENGE.UNAVAILABLE);
            return;
          }
          setShowChallenge(true);
          return;
        }
        if (sessionResult.status === 'geo_blocked') {
          setShowChallenge(false);
          setGeoGateMessage(sessionResult.message || '');
          return;
        }

        // Passed the gate (or gate disabled): clear any gate UI before continuing
        setShowChallenge(false);
        setGeoGateMessage(null);

        if (sessionResult.status === 'quota_full') {
          setIsTerminated(true);
          setTerminationReason('quota_full');
          setTerminationRedirectUrl(sessionResult.redirect_url || null);
          clearSavedSession(ctx.surveyId);
          return;
        }

        if (sessionResult.status === 'already_completed') {
          setIsTerminated(true);
          setTerminationReason('completed');
          setTerminationRedirectUrl(sessionResult.redirect_url || null);
          clearSavedSession(ctx.surveyId);
          return;
        }

        if (sessionResult.status === 'screened_out') {
          setIsTerminated(true);
          setTerminationReason('screened_out');
          setTerminationRedirectUrl(sessionResult.redirect_url || null);
          clearSavedSession(ctx.surveyId);
          return;
        }

        if (!sessionResult.session_data) {
          setError('Failed to start survey. Please try again.');
          return;
        }

        // Persist session to localStorage so progress survives a page reload
        // (covers both resumed and newly created sessions).
        saveSessionToStorage(ctx.surveyId, sessionResult.session_data.id);

        // For resumed sessions, restore question index and skip welcome screen
        if (sessionResult.status === 'resumed') {
          setSession(sessionResult.session_data);
          if (sessionResult.session_data.current_question_index > 0) {
            setCurrentQuestionIndex(sessionResult.session_data.current_question_index);
            setShowWelcomeScreen(false);
          }
          return;
        }

        // New session created
        setSession(sessionResult.session_data);
      } catch (error) {
        console.error('Failed to start session:', error);
        setError('Failed to start survey. Please try again.');
      }
    },
    [saveSessionToStorage, clearSavedSession, setCurrentQuestionIndex],
  );

  // Soft-gate retry (e.g. after the respondent disables a VPN).
  const handleAccessRetry = useCallback(async () => {
    setIsRetryingAccess(true);
    try {
      await attemptStartSession();
    } finally {
      setIsRetryingAccess(false);
    }
  }, [attemptStartSession]);

  // Load survey and create session
  useEffect(() => {
    const loadSurveyAndCreateSession = async () => {
      if (!shareId || session) return;
      if (startedForShareIdRef.current === shareId) return;
      startedForShareIdRef.current = shareId;

      try {
        setIsLoading(true);
        setError(null);

        const surveyId = shareIdToSurveyId(shareId);

        // Preview mode: load survey via authenticated endpoint, no session creation
        if (isPreviewMode) {
          const { getSurvey } = await import('../../utils/surveyApi');
          const surveyResult = await getSurvey(surveyId);
          setSurvey(surveyResult.survey);
          // A deep-link past the last question would otherwise leave the preview on a spinner.
          const previewIndex = clampQuestionIndex(
            startFromIndex,
            surveyResult.survey?.questions?.length ?? 0,
          );
          setCurrentQuestionIndex(previewIndex);
          // Create a fake local session for preview
          setSession({
            id: -1,
            state: 'in_progress',
            current_question_index: previewIndex,
            survey_id: surveyId,
            responses: [],
            responses_count: 0,
            question_order: null,
          });
          setIsLoading(false);
          return;
        }

        const surveyResult = await getPublicSurvey(surveyId);
        setSurvey(surveyResult.survey);

        // Quota reached: block everyone, including a resuming/reloading in-progress respondent,
        // before any saved-session resume below. The backend enforces this on submit; this shows
        // the Survey Closed screen at load instead of after a wasted answer.
        if (surveyResult.survey.quota_full) {
          setIsTerminated(true);
          setTerminationReason('quota_full');
          clearSavedSession(surveyId);
          return;
        }

        // Resolve who this respondent is BEFORE the saved-session check below. The URL is the
        // durable copy of that identity, so writing it only on the cold path would leave a resumed
        // respondent with nothing to fall back on the moment localStorage is cleared.
        const trackingParam = surveyResult.survey.redirect_config?.tracking_param as
          | Record<string, string>
          | null
          | undefined;
        let trackingId: string | null = null;

        for (const paramName of Object.keys(trackingParam ?? {})) {
          const paramValue = searchParams.get(paramName);
          if (paramValue) {
            trackingId = paramValue;
            break;
          }
        }

        // No panel provider owns this survey, so we mint and carry the id ourselves. Panel surveys
        // are deliberately left alone: the backend substitutes this value into the provider's
        // completion URL, and a fabricated iid/psid would corrupt their reconciliation.
        if (!trackingId && shouldMintRespondentId(trackingParam)) {
          const respondentId = resolveRespondentId(surveyId, searchParams.get(RESPONDENT_ID_PARAM));
          trackingId = respondentId;

          if (searchParams.get(RESPONDENT_ID_PARAM) !== respondentId) {
            setSearchParams(
              (prev) => {
                const next = new URLSearchParams(prev);
                next.set(RESPONDENT_ID_PARAM, respondentId);
                return next;
              },
              { replace: true },
            );
          }
        }

        // A LinkedIn-verified respondent arrives with the session their callback already created
        // named on the URL, and nothing in THIS browser's localStorage — so with the id ignored the
        // branch below finds nothing and `attemptStartSession` POSTs a SECOND session, orphaning
        // the verified one (BE #840 now refuses that outright, which is honest but unusable).
        //
        // A session that does not resolve deliberately falls THROUGH to the branches below rather
        // than surfacing an error: a stale, replayed or tampered id must not cost the respondent
        // their saved progress or their ability to take the survey at all. `linkedin_verified` is
        // not read — it carries no information this needs, since only a verified callback is given
        // a session id and the server re-checks ownership on every call.
        const namedSessionId = parseResumeSessionId(searchParams.get(RESUME_SESSION_ID_PARAM));
        if (namedSessionId !== null) {
          const namedSession = await getSessionById(namedSessionId);
          if (namedSession) {
            // Persist it here, which the branch below never has to do for its own id: a reload
            // that drops the query string would otherwise start over.
            saveSessionToStorage(surveyId, namedSession.id);
            setSession(namedSession);
            if (namedSession.current_question_index > 0) {
              setCurrentQuestionIndex(namedSession.current_question_index);
              setShowWelcomeScreen(false);
            }
            setIsLoading(false);
            return;
          }
          console.warn(
            `[SessionResume] URL session ${namedSessionId} is not resumable — starting normally`,
          );
        }

        // Check localStorage for an existing session (prevents losing progress on page reload)
        const savedSession = getSavedSession(surveyId);
        if (savedSession) {
          console.log(
            `[SessionPersist] Found saved session ${savedSession.sessionId}, attempting to resume...`,
          );
          const existingSession = await getSessionById(savedSession.sessionId);

          if (existingSession) {
            console.log(
              `[SessionPersist] Successfully resumed session ${existingSession.id} at question ${existingSession.current_question_index}`,
            );
            setSession(existingSession);

            // Restore question index if session has progressed
            if (existingSession.current_question_index > 0) {
              setCurrentQuestionIndex(existingSession.current_question_index);
              // Skip welcome screen for resumed sessions
              setShowWelcomeScreen(false);
            }
            setIsLoading(false);
            return;
          } else {
            // Saved session is no longer valid (completed, expired, or deleted)
            console.log(
              `[SessionPersist] Saved session ${savedSession.sessionId} is no longer valid, clearing...`,
            );
            clearSavedSession(surveyId);
          }
        }

        // Capture context so the session start can be retried (geo gate) or
        // re-submitted with a Turnstile token (bot challenge), then run the gate.
        sessionStartCtxRef.current = { surveyId, trackingId: trackingId || undefined };
        await attemptStartSession();
      } catch (error) {
        console.error('Failed to load survey or create session:', error);
        // A 403 from getPublicSurvey means the survey is no longer active (closed/
        // archived) — show the Survey Closed screen rather than a retry prompt. This
        // is the first call on load, so it also covers reloading/resuming respondents.
        const status = (error as { status?: number })?.status;
        if (status === 403) {
          setIsTerminated(true);
          setTerminationReason('closed');
          clearSavedSession(surveyId);
        } else {
          setError('Failed to start survey. Please try again.');
        }
      } finally {
        setIsLoading(false);
      }
    };

    loadSurveyAndCreateSession();
  }, [shareId, searchParams, setSearchParams, attemptStartSession, setCurrentQuestionIndex]);

  // Session heartbeat: ping every 60s to keep the session marked as active.
  //
  // Stands down while the survey-wide timer is running: that hook beats the SAME endpoint every
  // 60s and carries the activity the server needs to decide whether to pause the clock. Sending
  // this one alongside it doubles the respondent's request rate, and it used to carry no activity
  // — so the server read it as "the tab is busy" and resumed a clock the timer just had paused.
  // The unload ping stays either way: it is a different job (a last mark before the tab closes)
  // and the timer hook has no equivalent.
  //
  // Both of those are exemptions from the exclusivity rule, so both have to carry an activity
  // report of their own or they re-enter the very hazard that rule was written for: a heartbeat
  // with no body reaches `apply_activity` as `idle_ms=None, hidden=False`, which takes its RESUME
  // branch. Both therefore send what the activity tracker actually knows.
  //
  // The unload one sent a hardcoded `hidden: true` for a while, on the reasoning that the tab is
  // going away. `beforeunload` cannot support that claim: it fires identically for a close, a
  // RELOAD and a same-tab navigation, and `apply_activity` pauses on `hidden` instantly, with no
  // idle threshold to cross. So every reload of a timed survey paused the server's clock on the
  // way out — and the reloaded page has no leading beat of its own to un-pause it, leaving the
  // readout on "Timer paused" until the first interval beat a minute later, which then credits
  // that minute onto the deadline and greets a respondent who never went away with "Welcome
  // back". Repeated, it banks real deadline up to MAX_TOTAL_PAUSED_SECONDS. Replacing a wrong
  // claim of presence with a wrong claim of absence is not a fix; what is TRUE at unload is
  // whatever the tracker already knows, and it reports `hidden` for a tab that really is hidden.
  useEffect(() => {
    if (!session?.id || isPreviewMode) return;
    const intervalId = deadline.ownsHeartbeat
      ? null
      : setInterval(() => {
          sendSessionHeartbeat(session.id, deadline.activityRef.current());
        }, 60_000);

    // Past the questionnaire, a closing tab needs the completion mark, not a heartbeat. Ref: this closure is fixed at setup.
    const handleBeforeUnload = () => {
      if (completionPhaseRef.current !== 'idle') {
        sendCompletionBeacon(session.id);
        return;
      }
      sendSessionHeartbeat(session.id, deadline.activityRef.current());
    };
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      if (intervalId !== null) clearInterval(intervalId);
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [session?.id, deadline.ownsHeartbeat]);

  const handleResponseChange = useCallback((value: any) => {
    setCurrentResponse(value);
  }, []);

  const handleStartSurvey = useCallback(() => {
    setShowWelcomeScreen(false);
    setShowConsentScreen(true);
  }, []);

  const handleAcceptConsent = useCallback(() => {
    setShowConsentScreen(false);
  }, []);

  const handlePreviewSkip = useCallback(() => {
    if (!survey?.questions) return;
    const nextIndex = currentQuestionIndex + 1;
    if (nextIndex >= survey.questions.length) {
      setIsTerminated(true);
      setTerminationReason('completed');
    } else {
      setCurrentResponse('');
      setFollowupQuestion(null);
      setCurrentQuestionIndex(nextIndex);
    }
  }, [currentQuestionIndex, survey, setCurrentQuestionIndex]);

  /**
   * After an answer lands, has the block hit its question cap? Returns true if it closed, so the
   * caller stops its normal advance. The block's shape (id, size, cap) is read from the ref
   * because it is fixed for the block; the COUNT comes from the session the server just returned,
   * so it includes the answer that triggered this check.
   */
  const maybeCloseRapidFireOnCap = useCallback(
    async (
      sessionData: { responses?: { question_id: string; state?: string }[] } | undefined,
    ): Promise<boolean> => {
      const rf = rapidFireRef.current;
      if (!rf?.started || !survey?.questions) return false;

      const { close, reason } = shouldCloseRapidFire({
        remaining: null, // expiry is the timer's job; this path only ever decides the cap
        answeredInBlock: answeredCountInBlock(
          survey.questions,
          rf.blockId,
          sessionData?.responses ?? [],
        ),
        blockSize: rf.range.size,
        maxQuestions: rf.config.max_questions,
      });
      if (!close || !reason) return false;

      await handleCloseRapidFire(reason);
      return true;
    },
    [survey, handleCloseRapidFire],
  );

  /**
   * Deliberate skip inside a rapid-fire block. Recorded as a real `skipped` answer-state, not a
   * blank, so the researcher can see the skip rate rather than discovering it in clean-looking
   * data. Advances one question; it does NOT end the block.
   */
  const handleRapidFireSkip = useCallback(async () => {
    if (!rapidFire || !session || !survey?.questions || !currentQuestion) return;
    if (timerExpiredByServer) return;
    if (submitInFlightRef.current) return;
    submitInFlightRef.current = true;

    try {
      setIsSubmitting(true);
      const result = await submitResponse(
        session.id,
        currentQuestion.id,
        { skipped_reason: 'rapid_fire_skipped' },
        currentQuestion.type,
        questionStartTimeRef.current,
        true,
        null,
        getActiveTimings(),
      );
      // Same contract as every other write: past the hard stop the skip is accepted and not
      // recorded, so advancing would move them on through a block nothing is being saved from.
      if (isTimerExpiredWrite(result)) {
        setTimerExpiredByServer(true);
        setError(UI_TEXT.HUMAN_SURVEY.SESSION.ERRORS.TIME_UP);
        return;
      }
      setSession(result.session_data);
      setCurrentResponse('');
      setCurrentQuestionIndex((i) => i + 1);
    } catch (e) {
      console.error('Failed to skip rapid-fire question:', e);
      setError('Could not skip that question. Please try again.');
    } finally {
      setIsSubmitting(false);
      submitInFlightRef.current = false;
    }
    // No `questionStartTime` dependency: it is a ref now (see questionStartTimeRef), read at the
    // moment of the click rather than through whichever closure this callback was last rebuilt
    // with. Every other dependency either branch listed is kept.
  }, [
    rapidFire,
    session,
    survey,
    currentQuestion,
    getActiveTimings,
    setCurrentQuestionIndex,
    timerExpiredByServer,
  ]);

  /**
   * Undo a spoken rapid-fire answer's auto-advance (spec 02). Moves the respondent back one
   * question through the SAME `setCurrentQuestionIndex` mechanism every other navigation in this
   * file uses. It does not call the API at all, so the response already written for the question
   * being left never gets touched — nothing is lost if the respondent chooses not to re-record.
   */
  /** Patches a spoken answer's row once transcription finishes — one path for BOTH spoken
   *  surfaces. Three outcomes: ANSWERED, the true empty answer, or `failed` (ADR-0005 §3). */
  const deliverSpokenTranscript = useCallback(
    (delivery: {
      questionId: string;
      questionType: string;
      transcript: string | null;
      noAudioCaptured?: boolean;
      attemptId?: number;
    }) => {
      if (!session) return;
      const { questionId, questionType, transcript, noAudioCaptured, attemptId } = delivery;
      const settle = (settled: string | null) => () =>
        resolveTranscript(questionId, attemptId, settled);

      // A take the respondent re-recorded: writing it would put the words they undid back over the
      // ones they kept, whichever transcription happens to finish last. It still gives its marker
      // back, or the gate would wait on an attempt that is never coming.
      if (transcriptAttemptSuperseded(questionId, attemptId)) {
        resolveTranscript(questionId, attemptId);
        return;
      }

      if (transcript !== null) {
        submitResponse(session.id, questionId, transcript, questionType, undefined, false)
          .catch((e) => console.error('Failed to patch spoken transcript:', e))
          .finally(settle(transcript));
        return;
      }
      if (noAudioCaptured) {
        submitResponse(session.id, questionId, '', questionType, undefined, false)
          .catch((e) => console.error('Failed to patch spoken empty answer:', e))
          .finally(settle(''));
        return;
      }
      submitResponse(
        session.id,
        questionId,
        '',
        questionType,
        undefined,
        false,
        undefined,
        undefined,
        'failed',
      )
        .catch((e) => console.error('Failed to mark spoken transcription failed:', e))
        .finally(settle(null));
    },
    [session, resolveTranscript, transcriptAttemptSuperseded],
  );

  /** Back to the question a spoken answer advanced from; re-recording arms a marker (ADR-0005). */
  const handleSpokenUndo = useCallback(() => {
    setSpokenUndo((armed) => {
      if (armed) {
        setCurrentQuestionIndex(armed.fromIndex);
        setCurrentResponse('');
      }
      return null;
    });
  }, [setCurrentQuestionIndex]);

  const handleSubmitResponse = useCallback(
    async (
      responseValue?: any,
      // Rapid-fire voice only: 'pending' for the immediate advance write, before its transcript
      // (if any) is back. See RapidFireVoiceRunner's onAdvance below.
      transcriptionStatus?: 'pending' | 'failed',
    ) => {
      if (
        !session ||
        !survey ||
        !survey.questions ||
        currentQuestionIndex >= survey.questions.length
      )
        return;
      // Past the server's buzzer this write is accepted and discarded, and the question it was
      // written from stays interactive — nothing here disables it — so an impatient respondent
      // presses Next again and issues another one, per press, on the unauthenticated respondent
      // path. Each also re-takes `submitInFlightRef`, which is exactly what `beforeHardStop`'s
      // `submitToSettle` waits on, so those presses push the hard stop out towards its ten-second
      // budget again and again. The conditional-skip chain has had this guard since it started
      // re-arming itself; the two paths the RESPONDENT drives never got it.
      if (timerExpiredByServer) return;
      // The auto-submit types (single_choice, yes_no, rating, likert, dropdown, nps) submit straight
      // from onClick and are not disabled while a submit runs, so this is the only thing standing
      // between a double-tap and a silently skipped question. A ref, not `isSubmitting`, because
      // state is async: both taps would read the old `false` before React commits.
      if (submitInFlightRef.current) return;
      submitInFlightRef.current = true;

      const actualQuestionIndex =
        session.question_order && session.question_order.length > currentQuestionIndex
          ? session.question_order[currentQuestionIndex]
          : currentQuestionIndex;
      const questionToSubmit = survey.questions[actualQuestionIndex];
      const responseToSubmit = responseValue !== undefined ? responseValue : currentResponse;

      // Last line of defence before the answer is persisted. canProceed only guards the Next
      // button, and several choice inputs auto-submit without consulting it. Refusing here is what
      // makes "a photo object can never be stored as someone's prose" true rather than likely.
      if (!followupQuestion && !isValidAnswerFor(questionToSubmit.type, responseToSubmit)) {
        console.error(
          `Refusing to submit: ${describeAnswerMismatch(questionToSubmit.type, responseToSubmit)}`,
        );
        setCurrentResponse('');
        setError(UI_TEXT.HUMAN_SURVEY.SESSION.ERRORS.ANSWER_NOT_CAPTURED);
        submitInFlightRef.current = false;
        return;
      }

      // Preview mode: store response locally, skip all API calls and AI followup
      if (isPreviewMode) {
        const newResponse: QuestionResponse = {
          question_id: questionToSubmit.id,
          question_type: questionToSubmit.type,
          response_data: [{ type: 'original_response', value: responseToSubmit }],
          state: 'answered',
          created_at: new Date().toISOString(),
        };
        previewResponsesRef.current = [
          ...previewResponsesRef.current.filter((r) => r.question_id !== questionToSubmit.id),
          newResponse,
        ];
        setSession((prev) =>
          prev
            ? {
                ...prev,
                responses: [
                  ...prev.responses.filter((r) => r.question_id !== questionToSubmit.id),
                  newResponse,
                ],
              }
            : prev,
        );

        const nextIndex = currentQuestionIndex + 1;
        if (nextIndex >= survey.questions.length) {
          setIsTerminated(true);
          setTerminationReason('completed');
        } else {
          setCurrentResponse('');
          setFollowupQuestion(null);
          setCurrentQuestionIndex(nextIndex);
        }
        submitInFlightRef.current = false;
        return;
      }

      try {
        setIsSubmitting(true);
        setError(null);

        let result;
        let shouldAdvanceQuestion = true; // Default to advancing question
        // Set by the MaxDiff / conjoint branches to move to the next set once the answer is safely
        // persisted. Deliberately not invoked on the failure path, so a failed submit leaves the
        // respondent on the set they just answered instead of silently dropping it.
        let advanceMultiStep: (() => void) | null = null;

        if (followupQuestion) {
          // This is a follow-up response. Timing is the DELTA accrued since the previous
          // submission for this question (main answer or prior follow-up), so follow-up
          // answering time counts without double-counting the already-reported buckets.
          result = await submitFollowupResponse(
            session.id,
            questionToSubmit.id,
            followupQuestion,
            responseToSubmit,
            collectActiveTimeDelta(),
          );
          // A follow-up is an extra question not in the authored array — count it so the progress
          // estimate grows by one rather than overshooting.
          setFollowupsAnswered((n) => n + 1);
        } else {
          // Determine if this should advance to next question
          let actualResponseData = responseToSubmit;

          // Handle MaxDiff progression logic
          if (
            questionToSubmit.type === 'max_diff' &&
            session &&
            !isBestWorstQuestion(questionToSubmit)
          ) {
            // Best/worst mode (spec 36-02) skips this whole branch: one screen, all options,
            // `responseToSubmit` (the {best, worst} value BestWorstCard produced) IS the final
            // answer as-is — `actualResponseData` already defaults to `responseToSubmit` above, so
            // there is no per-set bookkeeping, no set_index wrapper, and no multi-step deferral to
            // do here. See `isBestWorst` at the render site for the same check.

            // The design ON SCREEN, not a second one built here. `questionToSubmit` is
            // `survey.questions[getActualQuestionIndex(currentQuestionIndex)]` — the same object
            // `currentQuestion` resolves to — and the generator is deterministic on
            // (choices, sessionId), so this rebuilt an identical design on EVERY set submit and
            // re-serialised `evaluateMaxDiffDesign`'s multi-KB result to the console with it. The
            // memo above exists to pay that once per question; a second copy of the call outside it
            // is the same defect wearing a different hat, and `sets.length` is all that was wanted.
            const sets = maxDiffSets;

            const currentSetIndex = currentMaxDiffSetIndex;

            // Store the response for this set
            const newMaxDiffResponses = { ...maxDiffResponses };
            newMaxDiffResponses[currentSetIndex] = responseToSubmit;
            setMaxDiffResponses(newMaxDiffResponses);

            // Format response data for backend with set index
            actualResponseData = {
              most: responseToSubmit.most_appealing || responseToSubmit.most,
              least: responseToSubmit.least_appealing || responseToSubmit.least,
              set_index: currentSetIndex,
            };

            // Check if there are more sets to show
            if (currentSetIndex < sets.length - 1) {
              shouldAdvanceQuestion = false;
              // Queued, NOT scheduled: this used to be a setTimeout fired before `await
              // submitResponse` below, so a failed submit still advanced the respondent and cleared
              // their answer — the set was silently discarded. Runs only after the submit succeeds.
              advanceMultiStep = () => {
                // Written WITH the visit it counts for, so it can never be read against another —
                // not another question, and not another pass through this one.
                setMaxDiffSetCursor({
                  visitKey: makeQuestionVisitKey(questionToSubmit.id, questionVisit.visit),
                  setIndex: currentSetIndex + 1,
                });
                setCurrentResponse(''); // Clear response for next set
              };
            }
            // No `else`: the last set does NOT reset here. Nothing resets it anywhere — the next
            // question reads its own set index because the cursor carries the question it was
            // written for (see MaxDiffSetCursor). Resetting here ran BEFORE `await submitResponse`
            // — the very ordering the intermediate branch above was fixed to avoid — so a failed
            // final submit, which keeps the respondent on the question, sent the per-image scope key
            // `${questionId}#${setIndex}` BACKWARDS onto keys an earlier mount had already marked
            // painted (they are scoped rather than cleared precisely because a reset effect lands
            // one render late), and set 1 came back unlocked over images that were still in flight.
            // It also wiped every stored set, so the retry re-submitted the final set's claims as
            // `set_index: 0` and restarted an eight-set question.
            //
            // The invariant this buys: within one question the set index only ever INCREASES — and
            // the answer clock depends on it too, since the set index is a term of
            // `presentedQuestionKey`: moving it backwards mid-flight re-stamped the clock for a set
            // nobody was looking at, so the row the retry finally wrote was charged the failed
            // request's duration.
          }

          // Handle Conjoint Analysis progression logic
          if (
            questionToSubmit.type === 'conjoint_analysis' &&
            questionToSubmit.conjoint_attributes
          ) {
            const currentScenarioIndex = currentConjointScenarioIndex;

            // Store the response for this scenario
            const newConjointResponses = { ...conjointResponses };
            newConjointResponses[currentScenarioIndex] = responseToSubmit;
            setConjointResponses(newConjointResponses);

            // Find the selected profile details. Use the SAME balanced design the render path
            // shows the respondent so the stored profile_details can't drift from what was seen.
            const attributes = questionToSubmit.conjoint_attributes || [];
            const allProfiles = generateConjointProfiles(attributes);

            // Find the selected profile
            const selectedProfile = allProfiles.find((p) => p.id === responseToSubmit);

            // Format response data for backend with scenario index and profile details
            actualResponseData = {
              choice: responseToSubmit,
              profile_details: selectedProfile ? selectedProfile.attributes : null,
              scenario_index: currentScenarioIndex,
            };

            // Calculate optimal number of scenarios based on conjoint design principles
            // Standard practice: minimum 8-12 choice tasks for reliable utility estimation
            // Can be made configurable via question properties in the future
            const totalScenarios =
              questionToSubmit.conjoint_scenarios ||
              Math.min(12, Math.max(8, attributes.length * 2));
            if (currentScenarioIndex < totalScenarios - 1) {
              shouldAdvanceQuestion = false;
              // Same deferral as MaxDiff above — never advance before the answer is persisted.
              advanceMultiStep = () => {
                setCurrentConjointScenarioIndex(currentScenarioIndex + 1);
                setCurrentResponse(''); // Clear response for next scenario
              };
            } else {
              // The last scenario resets for the next question — but QUEUED, exactly like the
              // intermediate branch above, and for a stricter version of the same reason. Run
              // eagerly it committed `currentConjointScenarioIndex` 0 before `await submitResponse`,
              // so a failed final submit dropped the respondent back to scenario 1 with every stored
              // answer cleared, to redo a task the backend then deduped down to the last re-answer.
              // It also moved `presentedQuestionKey` while the request was in flight, which
              // re-stamped the clock for a scenario nobody was looking at — so the row the retry
              // eventually wrote was charged the failed request's duration.
              advanceMultiStep = () => {
                setCurrentConjointScenarioIndex(0);
                setConjointResponses({});
              };
            }
          }

          const isOpenText =
            questionToSubmit.type === 'long_text' || questionToSubmit.type === 'short_text';

          // How the answer was given, for the mic-vs-text analytics on the responses screen. Only
          // the open-text types offer that choice; a tap on a choice question is neither, and
          // labelling it from the (deliberately sticky) channel toggle counted taps as typing.
          // Read through the ref so it is the toggle state at the moment of submit.
          const inputMode = isOpenText ? (showTextInputRef.current ? 'text' : 'mic') : null;

          // This is an original response. The backend runs the synchronous redundancy look-ahead
          // ONLY after a qualitative (open-text) answer — so only then do we show the brief
          // "checking your answers" state (mirrors the follow-up analyzing screen). Structured
          // answers submit instantly with no spinner. Skip in preview (no backend call).
          const willCheckSkip =
            !isPreviewMode &&
            shouldAdvanceQuestion &&
            !!survey?.ai_redundancy_skip_enabled &&
            isOpenText;
          if (willCheckSkip) setIsCheckingSkip(true);
          try {
            result = await submitResponse(
              session.id,
              questionToSubmit.id,
              actualResponseData,
              questionToSubmit.type,
              questionStartTimeRef.current,
              shouldAdvanceQuestion,
              inputMode,
              getActiveTimings(),
              transcriptionStatus,
            );
          } finally {
            if (willCheckSkip) setIsCheckingSkip(false);
          }
        }

        // `status: success` with NOTHING written. Past the deadline plus its five-second grace the
        // backend drops the answer and says so only here — deliberately, so the client stops
        // retrying rather than looping on an error. Nothing read the flag, so the runtime called
        // setSession, advanced, showed no error, and the respondent went on answering into a void
        // all the way to the completion endpoint. Their words are gone either way; what changes is
        // that they are told, they are not walked forward, and the session is actually ended.
        if (isTimerExpiredWrite(result)) {
          setTimerExpiredByServer(true);
          setError(UI_TEXT.HUMAN_SURVEY.SESSION.ERRORS.TIME_UP);
          return;
        }

        setSession(result.session_data);

        // The answer is persisted — only now is it safe to move to the next set/scenario.
        advanceMultiStep?.();

        // Adaptive question scoping: the backend may return a narrowed version of the NEXT question
        // (keyed by its id). Stash it so the runner shows the adapted text when it reaches that
        // question, instead of the authored text. Absent/disabled -> unchanged behavior.
        if (result.adapted_question?.question_id && result.adapted_question.adapted_question) {
          const aq = result.adapted_question;
          setAdaptedQuestions((prev) => ({ ...prev, [aq.question_id]: aq.adapted_question }));
        }

        // Check if session was terminated
        if (result.status === 'terminated') {
          setIsTerminated(true);
          setTerminationReason(result.termination_reason || null);
          setTerminationRedirectUrl(result.redirect_url || null);
          if (session) clearSavedSession(session.survey_id);
          setIsSubmitting(false);
          return; // Stop processing, show termination screen
        }

        // Check if skip logic was applied - defer until after follow-ups complete
        let skipTarget: number | null = pendingSkipTarget; // From previous follow-up cycle
        if (result.skip_applied && result.next_question_index !== undefined) {
          console.log(
            `Skip logic detected: will jump to question index ${result.next_question_index} after follow-ups`,
          );
          skipTarget = result.next_question_index;
          setPendingSkipTarget(result.next_question_index);
          // AI redundancy skip: acknowledge to the respondent that we skipped questions they
          // already answered (so the progress jump doesn't read as a glitch).
          if (result.skip_reason === 'ai_skipped') {
            // next_question_index is a CANONICAL index; count the skip in DISPLAY-index space
            // so the notice is correct under question_order randomization (where canonical and
            // display order differ). Falls back to 0 if the target isn't in the order map.
            const order = session?.question_order;
            const targetDisplayPos =
              order && order.length
                ? order.indexOf(result.next_question_index)
                : result.next_question_index;
            const skippedCount =
              targetDisplayPos < 0 ? 0 : Math.max(0, targetDisplayPos - (currentQuestionIndex + 1));
            if (skippedCount > 0) {
              setAiSkipNotice({ count: skippedCount });
            }
          }
        }

        // Check if AI follow-up is enabled for this question
        // For multi-set MaxDiff, only trigger follow-up after the LAST set is completed. Best/worst
        // mode (spec 36-02) is one screen, not a set sequence, so it is always "on the last set" —
        // `maxDiffSets` is empty for this mode (see the memo's own mode guard), which would
        // otherwise make `currentMaxDiffSetIndex === maxDiffSets.length - 1` compare 0 to -1 and
        // silently disable an author's follow-up even though requirement 6 (spec-02) asks for it.
        // Never inside a rapid-fire block: a 5-20s analysis pause would eat most of a 60s section,
        // and the authoring rules force the flag off there anyway — this is the runtime's backstop
        // for surveys authored before those rules existed.
        // The predicate alone, exactly as every other call site reads it — never re-derived from
        // the mode field or the response shape (decisions-02).
        const isBestWorstSubmit = isBestWorstQuestion(questionToSubmit);
        const shouldTriggerFollowup =
          !rapidFireActive &&
          questionToSubmit.enable_ai_followup &&
          (followupQuestion || // Already in follow-up conversation, continue analyzing
            questionToSubmit.type !== 'max_diff' ||
            isBestWorstSubmit ||
            (questionToSubmit.type === 'max_diff' &&
              currentMaxDiffSetIndex === maxDiffSets.length - 1));

        /* ADR-0005's second consumer: a follow-up would otherwise probe the empty `pending`
           placeholder. Waits on the same marker; skipped rather than run on '' if nothing lands. */
        let spokenTranscript: string | null = null;
        if (shouldTriggerFollowup && transcriptionStatus === 'pending') {
          setIsAnalyzing(true);
          setAnalysisProgress(UI_TEXT.HUMAN_SURVEY.SESSION.MESSAGES.AWAITING_TRANSCRIPT);
          try {
            spokenTranscript = await awaitSpokenTranscript(String(questionToSubmit.id));
          } finally {
            setIsAnalyzing(false);
            setAnalysisProgress('');
          }
        }
        const followupHasSomethingToProbe =
          transcriptionStatus !== 'pending' || !!spokenTranscript?.trim();

        if (shouldTriggerFollowup && followupHasSomethingToProbe) {
          setIsAnalyzing(true);
          setAnalysisProgress('Starting analysis...');

          try {
            // Analyze response for potential follow-up with progress tracking
            // Use responseToSubmit to ensure we get the actual user's choice/response — or, for a
            // spoken answer, the transcript that arrived after it, since the row itself was
            // written empty and `responseToSubmit` is that same empty string.
            let userResponseForAnalysis =
              spokenTranscript !== null
                ? spokenTranscript
                : typeof responseToSubmit === 'object'
                  ? JSON.stringify(responseToSubmit)
                  : String(responseToSubmit);

            // The picks under the researcher's own anchor words, and the options that went
            // unpicked. Both modes are built in followupContext.ts — see its header for why.
            const followupAnchors = resolveAnchors(questionToSubmit.max_diff_config);
            let followupContext: string | undefined = undefined;
            if (isBestWorstSubmit) {
              // One screen: `responseToSubmit` already IS the complete {best, worst} answer, in
              // rank order, with no accumulation across sets to do. Decoded through the shared
              // parser, so a value of some other shape refuses here as it does everywhere else.
              const answer = parseBestWorstAnswer(responseToSubmit);
              if (answer) {
                followupContext = buildBestWorstFollowupContext(
                  answer,
                  followupAnchors,
                  questionToSubmit.choices || [],
                );
              }
            } else if (questionToSubmit.type === 'max_diff') {
              // Use the complete maxDiffResponses state which has all sets
              const completedResponses = { ...maxDiffResponses };
              completedResponses[currentMaxDiffSetIndex] = responseToSubmit;

              // Override userResponseForAnalysis to include ALL MaxDiff responses
              userResponseForAnalysis = JSON.stringify(completedResponses);

              const claims = calculateMaxDiffRankedClaims(completedResponses, 3);
              console.log('MaxDiff claims calculated:', claims);

              // SHOWN is the sets the respondent saw, not the claim list: a design need not show
              // every claim, and calling an unseen one "passed over" is a fabricated finding.
              followupContext = buildMaxDiffFollowupContext(
                claims,
                followupAnchors,
                maxDiffSets.flat(),
                chosenMaxDiffOptions(completedResponses),
              );
            }

            // Extract image URLs for photo_upload questions (for vision-based followup)
            let imageUrlsForFollowup: string[] | undefined;
            if (questionToSubmit.type === 'photo_upload' && responseToSubmit?.images) {
              imageUrlsForFollowup = responseToSubmit.images
                .filter((img: { url?: string; extraction_status?: string }) => img.url)
                .map((img: { url: string }) => img.url);
              console.log('Photo upload followup with images:', imageUrlsForFollowup);
            }

            const analysis = await analyzeResponseForFollowup(
              session.id,
              questionToSubmit.id,
              questionToSubmit.text,
              userResponseForAnalysis,
              questionToSubmit.type,
              {
                timeout:
                  currentQuestionIndex + 1 >= survey.questions.length
                    ? ASYNC_ANALYSIS_CONFIG.FINAL_QUESTION_POLL_TIMEOUT_MS
                    : ASYNC_ANALYSIS_CONFIG.POLL_TIMEOUT_MS,
                pollInterval: ASYNC_ANALYSIS_CONFIG.POLL_INTERVAL_MS,
                context: followupContext,
                imageUrls: imageUrlsForFollowup,
                onProgress: (status: string, message: string) => {
                  console.log(`Analysis progress: ${status} - ${message}`);
                  // Update progress message based on status
                  switch (status) {
                    case ANALYSIS_TASK_STATES.PENDING:
                      setAnalysisProgress('Analysis task queued...');
                      break;
                    case ANALYSIS_TASK_STATES.PROCESSING:
                      setAnalysisProgress('Analyzing your response...');
                      break;
                    default:
                      setAnalysisProgress(message || 'Processing...');
                  }
                },
              },
            );

            if (analysis.analysis.needs_followup && analysis.analysis.followup_question) {
              // Show follow-up question
              setFollowupQuestion(analysis.analysis.followup_question);
              // Save the original response for display during follow-up (e.g., image hotspot clicks)
              setOriginalResponse(responseToSubmit);
              // Clear current response for the follow-up text input
              setCurrentResponse('');
              setIsAnalyzing(false);
              setIsSubmitting(false);
              setAnalysisProgress('');
              return; // Don't proceed to next question yet
            }
          } catch (followupError) {
            console.error('Failed to analyze response for follow-up:', followupError);
            setAnalysisProgress('Analysis failed, continuing...');
            // Continue normally if follow-up analysis fails
          } finally {
            setIsAnalyzing(false);
            setAnalysisProgress('');
          }
        }

        // Clear follow-up state and proceed normally
        setFollowupQuestion(null);
        setOriginalResponse(null);

        // Apply pending skip logic (deferred until after follow-ups complete)
        if (skipTarget !== null) {
          console.log(`Applying deferred skip logic: jumping to question index ${skipTarget}`);
          setCurrentResponse('');
          setCurrentQuestionIndex(skipTarget);
          setPendingSkipTarget(null);
          setIsSubmitting(false);
          return;
        }

        // Rapid-fire cap: checked off the answer that was just persisted rather than waiting for the
        // next 1s tick, so the respondent doesn't sit on a dead question after their final answer.
        if (shouldAdvanceQuestion && (await maybeCloseRapidFireOnCap(result.session_data))) return;

        // Only advance to next question if shouldAdvanceQuestion was true
        // For MaxDiff/Conjoint intermediate sets, we stay on the current question
        if (shouldAdvanceQuestion) {
          setCurrentResponse(''); // Clear response when advancing

          // Move to next question or complete survey
          if (currentQuestionIndex + 1 < survey.questions.length) {
            setCurrentQuestionIndex(currentQuestionIndex + 1);
          } else {
            // All questions answered, complete the survey
            await handleCompleteSession();
          }
        } else {
          // Staying on current question for intermediate sets/scenarios
          // Response is cleared by the setTimeout logic above
        }
      } catch (error) {
        console.error('Failed to submit response:', error);
        setError(respondentFacingSubmitError(error));
      } finally {
        setIsSubmitting(false);
        submitInFlightRef.current = false;
      }
      // `questionVisit` rather than `currentQuestionIndex`: the cursor written above is stamped with
      // the visit, and a re-entry that lands on the index it came from would not rebuild this closure.
    },
    [
      session,
      survey,
      questionVisit,
      maxDiffSets,
      currentResponse,
      followupQuestion,
      originalResponse,
      pendingSkipTarget,
      getActiveTimings,
      collectActiveTimeDelta,
      rapidFireActive,
      maybeCloseRapidFireOnCap,
      timerExpiredByServer,
      awaitSpokenTranscript,
    ],
  );

  /** Stopping the mic IS the submit: rapid fire's mechanism from an explicit stop. The marker is
   *  armed BEFORE the write, or it would arrive too late to hold the follow-up. */
  const handleSpokenAnswerCaptured = useCallback(
    (question: { id: string; type: string }, fromIndex: number, clip: Blob) => {
      if (!session) return;
      const questionId = String(question.id);
      const questionType = question.type;
      const attemptId = markTranscriptOutstanding(questionId);
      setSpokenUndo({ fromIndex, armedAt: Date.now() });
      void handleSubmitResponse('', 'pending');
      transcribeRapidFireClip(session.id, question.id, clip)
        .then((transcript) =>
          deliverSpokenTranscript({ questionId, questionType, transcript, attemptId }),
        )
        // The clip WAS long enough to be speech (the capture hook's own guard refused to deliver
        // anything shorter), so this is a transcription failure, never silence.
        .catch(() =>
          deliverSpokenTranscript({ questionId, questionType, transcript: null, attemptId }),
        );
    },
    [session, markTranscriptOutstanding, deliverSpokenTranscript, handleSubmitResponse],
  );

  // Remember which blocks the server says are already closed, so a resumed session never tries to
  // close one twice. Read-only bookkeeping — the deadline itself is merged in the rapidFire memo.
  useEffect(() => {
    const state = (
      session as { rapid_fire_state?: Record<string, { closed_at?: string | null }> } | null
    )?.rapid_fire_state;
    if (!state) return;
    for (const [blockId, entry] of Object.entries(state)) {
      if (entry?.closed_at) rapidFireClosedRef.current[blockId] = true;
    }
  }, [session]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">{UI_TEXT.HUMAN_SURVEY.SESSION.DESCRIPTIONS.LOADING}</p>
        </div>
      </div>
    );
  }

  // Must precede every guard below, isTerminated included: once non-idle there is no question left.
  if (completionPhase !== 'idle') {
    return (
      <SurveyFinishing failed={completionPhase === 'failed'} onRetry={handleCompleteSession} />
    );
  }

  // Show termination screen if session was terminated. This MUST precede the conditional-skip
  // spinner guard below: a skip of the last question sets terminate while `shouldSkipCurrentQuestion`
  // can remain true for the still-current question, which would otherwise mask the termination
  // screen behind the bare spinner indefinitely. Terminated always wins.
  if (isTerminated) {
    return (
      <SurveyTermination
        reason={terminationReason || undefined}
        redirectUrl={terminationRedirectUrl || undefined}
      />
    );
  }

  // Don't render question content while skipping conditional questions (prevents flash).
  // If a skip submit failed, surface the error here so the respondent isn't stuck on a bare spinner.
  if (shouldSkipCurrentQuestion || isSkippingConditional) {
    // Running out of time mid-chain is not a broken survey. The chain sets `error` and returns
    // without writing a response, so `shouldSkipCurrentQuestion` is still true and this guard is
    // what renders — and with the generic card below it told a respondent whose survey was about
    // to complete normally that it was "Survey Unavailable", and offered them a Go Home button.
    // The hard stop is seconds behind, carrying the panel `redirect_url` they are credited by, so
    // that button is a way to lose the completion they had earned. There is nothing to decide
    // here: the wait IS the remedy, and it always ends — /timer/expire either grants the stop or
    // is refused for good, and both now finish the session.
    if (timerExpiredByServer) {
      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="max-w-md mx-auto text-center p-6">
            <div className="bg-white rounded-xl shadow-sm p-8">
              <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <AlertCircle className="w-8 h-8 text-amber-600" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">
                {UI_TEXT.HUMAN_SURVEY.SESSION.TITLES.TIME_UP}
              </h2>
              <p className="text-gray-600 mb-6">{error}</p>
              <div
                className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto"
                role="status"
                aria-label={UI_TEXT.HUMAN_SURVEY.SESSION.DESCRIPTIONS.CLOSING_OFF}
              />
            </div>
          </div>
        </div>
      );
    }
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          {error ? (
            <div className="max-w-md mx-auto p-6">
              <div className="bg-white rounded-xl shadow-sm p-8">
                <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <AlertCircle className="w-8 h-8 text-yellow-600" />
                </div>
                <h2 className="text-xl font-semibold text-gray-900 mb-3">Survey Unavailable</h2>
                <p className="text-gray-600 mb-6">{error}</p>
                <Button onClick={() => navigate('/')} variant="primary">
                  Go Home
                </Button>
              </div>
            </div>
          ) : (
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          )}
        </div>
      </div>
    );
  }

  // Fraud gate: borderline respondents must pass a CAPTCHA before a session starts.
  if (showChallenge) {
    return (
      <TurnstileChallenge
        onVerified={(token) => attemptStartSession(token)}
        // If the widget can't load, surface an error rather than re-submitting
        // token-less (which the backend would just re-challenge, looping).
        onError={() => {
          setShowChallenge(false);
          setError(UI_TEXT.HUMAN_SURVEY.FRAUD_GATE.CHALLENGE.LOAD_ERROR);
        }}
      />
    );
  }

  // Fraud gate: non-US IPs see a retryable soft gate (e.g. to disable a VPN).
  if (geoGateMessage !== null) {
    return (
      <SurveyAccessGate
        message={geoGateMessage || undefined}
        onRetry={handleAccessRetry}
        retrying={isRetryingAccess}
      />
    );
  }

  if (!session || !survey || !survey.questions) {
    // If there's an error message (e.g., quota reached), show that instead of "Survey Not Found"
    if (error) {
      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="max-w-md mx-auto text-center p-6">
            <div className="bg-white rounded-xl shadow-sm p-8">
              <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <AlertCircle className="w-8 h-8 text-yellow-600" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">Survey Unavailable</h2>
              <p className="text-gray-600 mb-6">{error}</p>
              <Button onClick={() => navigate('/')} variant="primary">
                Go Home
              </Button>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <MessageSquare className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            {UI_TEXT.HUMAN_SURVEY.SESSION.TITLES.SURVEY_NOT_FOUND}
          </h2>
          <p className="text-gray-600 mb-4">
            {UI_TEXT.HUMAN_SURVEY.SESSION.DESCRIPTIONS.NOT_FOUND}
          </p>
          <Button onClick={() => navigate('/')}>
            {UI_TEXT.HUMAN_SURVEY.SESSION.BUTTONS.GO_HOME}
          </Button>
        </div>
      </div>
    );
  }

  // Show welcome screen if it hasn't been dismissed yet
  if (showWelcomeScreen) {
    const welcomeData = {
      title: survey.title || survey.name || 'Survey',
      description: survey.description || UI_TEXT.HUMAN_SURVEY.SESSION.WELCOME.DESCRIPTION,
      instructions: survey.instructions || UI_TEXT.HUMAN_SURVEY.SESSION.WELCOME.INSTRUCTIONS,
    };

    return <SurveyWelcomeScreen welcomeData={welcomeData} onStart={handleStartSurvey} />;
  }

  if (showConsentScreen) {
    return <SurveyConsentScreen onAccept={handleAcceptConsent} />;
  }

  // Experimental: once past the welcome screen AND the consent screen, a conversational /
  // discussion-guide survey hands the entire respondent experience to the voice moderator flow.
  // This MUST come after the consent check — the session is created on load (id > 0 before
  // consent), so checking conversational first would skip consent entirely. Traditional surveys
  // (the default, survey_style absent or 'traditional') never reach this branch and are completely
  // unaffected. Requires a real persisted session (the moderator endpoints need it), so preview
  // mode (id < 0) falls through.
  // The clock comes with it. useSurveyDeadline is called above every early return, so a timed
  // conversational survey was already ON a hard stop — but SurveyCountdown and TimerNudge are
  // mounted only in the traditional body below, so the respondent saw no readout, got no warning
  // at any threshold, and was then navigated away mid-conversation with no notice at all. Every
  // mark was spent against a screen that could not show it. A silent hard stop is precisely the
  // outcome this whole design exists to prevent, so the timer is rendered rather than disabled:
  // the researcher's limit stays real, and the respondent can see it coming.
  if (isConversationalRuntime && survey && session) {
    return (
      <ConversationalSurveyFlow
        survey={survey}
        sessionId={session.id}
        // The clock's own "is a write in the air" flag. This flow tracks its submits in local
        // state the hook cannot see, so without sharing the ref a moderator turn already sent was
        // not awaited: /timer/expire completed the session and the backend discarded the answer.
        submitInFlightRef={submitInFlightRef}
        // Only the server knows its buzzer went — a discarded turn is the one signal of it.
        onTimerExpired={() => setTimerExpiredByServer(true)}
        // And handed straight back, so the flow locks its own controls off the SAME fact the
        // hard stop is acting on rather than a second copy of it.
        timerExpired={timerExpiredByServer}
        // A live microphone is the respondent being there. Nothing else in this flow says so:
        // it fires none of the events the clock's activity tracking watches for.
        onRecordingChange={setIsRecordingConversational}
        timer={
          <>
            {deadline.showReadout && (
              <SurveyCountdown
                remaining={deadline.remaining}
                isFinal={deadline.isFinal}
                paused={deadline.paused}
              />
            )}
            <TimerNudge message={deadline.nudge} urgent={isNudgeUrgent(deadline.remaining)} />
          </>
        }
      />
    );
  }

  // currentQuestion is computed via useMemo with question_order support
  // If currentQuestion is null, show loading or error state
  if (!currentQuestion) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600">Loading question...</p>
        </div>
      </div>
    );
  }

  const isLastQuestion = currentQuestionIndex === survey.questions.length - 1;

  // Enhanced validation logic for all question types
  const canProceed = (() => {
    if (currentResponse === '' || currentResponse === null || currentResponse === undefined) {
      return false;
    }

    // For follow-up questions, only check if there's a non-empty text response
    if (followupQuestion) {
      return typeof currentResponse === 'string' && currentResponse.trim().length > 0;
    }

    // Shape gate before the per-type completeness checks below. Catches an answer belonging to a
    // DIFFERENT question type sitting in the shared state — previously nothing did, because
    // long_text/short_text have no case in this switch and fell through to `default: return true`.
    if (!isValidAnswerFor(currentQuestion.type, currentResponse)) {
      return false;
    }

    switch (currentQuestion.type) {
      case 'matrix_rating':
        // Matrix questions require all items to have ratings
        if (typeof currentResponse !== 'object' || currentResponse === null) return false;
        const matrixItems = currentQuestion.matrix_rows || currentQuestion.choices || [];
        return matrixItems.every(
          (item: string) => currentResponse[item] !== undefined && currentResponse[item] !== null,
        );

      case 'max_diff': {
        if (typeof currentResponse !== 'object' || currentResponse === null) return false;
        // Best/worst mode (spec 36-02): BestWorstCard owns its own Next button, gated on
        // `isComplete` internally, and the shared bottom button is hidden for this mode (see the
        // Navigation Buttons block) — this branch is never actually consulted to enable a click,
        // only to stop a stray legacy {most_appealing, least_appealing} shape from reading as
        // valid if this path is ever reached some other way.
        if (isBestWorst) {
          return Array.isArray(currentResponse.best) && Array.isArray(currentResponse.worst);
        }
        // MaxDiff (multi-set) requires both most and least appealing selections
        return currentResponse.most_appealing && currentResponse.least_appealing;
      }

      case 'image_hotspot':
        // Image hotspot requires clicks array with required number of selections
        if (typeof currentResponse !== 'object' || currentResponse === null) return false;
        const clicks = currentResponse.clicks;
        if (!Array.isArray(clicks)) return false;
        return clicks.length >= (currentQuestion.ranking_count || 3);

      case 'info_screen':
        // Info screens always have a valid response (no input required)
        return true;

      case 'conjoint_analysis':
        // Conjoint analysis requires a profile selection
        return typeof currentResponse === 'string' && currentResponse.startsWith('profile_');

      case 'multiple_choice': {
        // The cap is an UPPER bound alongside the existing "at least one" lower bound. It matters
        // here and not only on the option buttons because an answer can arrive from outside them —
        // a restored/persisted answer, or one collected before the author lowered the cap.
        const withinCap = isWithinSelectionCap(currentResponse, multiSelectCap);
        // Asks the SHARED predicate what the renderer will draw rather than re-testing
        // `media_display_mode === 'choices'` here. The renderer never tested for that constant —
        // it tiles on any stored mode that is not "question", and goes back to the choice list
        // when `option_media` is present — so the strict test gated two real shapes on an answer
        // the respondent could not produce and Next never enabled on either. `single_choice`
        // below is deliberately left alone: its fallback is `currentResponse != null`, which the
        // `{selected_indices}` object already satisfies, so the same looseness costs it nothing.
        if (rendersImageTiles(currentQuestion)) {
          return (
            currentResponse?.selected_indices &&
            currentResponse.selected_indices.length > 0 &&
            withinCap
          );
        }
        // Multiple choice requires at least one selection (handles both question-level media and no media)
        return Array.isArray(currentResponse) && currentResponse.length > 0 && withinCap;
      }

      case 'single_choice':
        // If has media URLs in EXPLICIT choice-level mode (legacy), it's an image/video choice question
        if (
          currentQuestion.media_urls &&
          currentQuestion.media_urls.length > 0 &&
          currentQuestion.media_display_mode === 'choices'
        ) {
          return currentResponse?.selected_indices && currentResponse.selected_indices.length > 0;
        }
        // Single choice requires a selection (handles both question-level media and no media)
        return currentResponse != null;

      case 'ranking':
        // Ranking requires the minimum number of selections
        const rankingCount = currentQuestion.ranking_count || 3;
        return Array.isArray(currentResponse) && currentResponse.length >= rankingCount;

      case 'percentage_allocation':
        // Percentage allocation requires total to equal target
        if (typeof currentResponse !== 'object' || currentResponse === null) return false;
        const target = currentQuestion.allocation_target || 100;
        const allocOpts = currentQuestion.allocation_options || currentQuestion.choices || [];
        const total = Object.values(currentResponse as Record<string, number>).reduce(
          (sum: number, val) => sum + (val || 0),
          0,
        );
        // Check allow_zero constraint - if allow_zero is false, all options must have value > 0
        const allowZeroCheck = currentQuestion.allow_zero !== false; // Default true
        if (!allowZeroCheck && allocOpts.length > 0) {
          const hasZeroValue = allocOpts.some((opt: string) => {
            const val = (currentResponse as Record<string, number>)[opt];
            return val === 0 || val === undefined || val === null;
          });
          if (hasZeroValue) return false;
        }
        return total === target;

      case 'photo_upload':
        return isPhotoUploadResponseValid(currentResponse);

      default:
        // For all other question types, basic non-empty validation
        return true;
    }
  })();

  /** Only on the question the answer advanced TO: the index comparison stops it appearing on the
   *  one it came from when a submit failed and left the respondent there. */
  const spokenUndoAvailable =
    !!spokenUndo &&
    !isPreviewMode &&
    !rapidFire?.started &&
    currentQuestionIndex > spokenUndo.fromIndex;

  const renderQuestionInput = () => {
    // Delegation only — every piece of ai_dynamic_section state and logic lives in
    // AiDynamicSectionRunner (and its sibling files), never in this component. See spec-01 +
    // the task-9 brief: this function is 6,000+ lines already and must not grow further.
    if (currentQuestion.type === 'ai_dynamic_section') {
      // Never generate from an answer whose transcript has not landed — ADR-0005.
      if (
        contextTranscriptsOutstanding(
          dynamicSectionConfigFromRow(currentQuestion),
          outstandingTranscripts,
          survey?.questions ?? [],
        )
      ) {
        return (
          <div
            className="text-center py-12"
            role="status"
            data-testid="awaiting-context-transcript"
          >
            <p className="text-gray-700">One moment — just getting your last answer.</p>
          </div>
        );
      }
      return (
        <AiDynamicSectionRunner
          question={currentQuestion as unknown as AiDynamicSectionQuestionWithVoiceFlags}
          sessionId={session.id}
          // A dynamic section that is the LAST question must COMPLETE the survey, not walk the
          // index off the end. Incrementing past the last question left the respondent on a
          // permanent loading screen with the section already stopped server-side — reported
          // from a live session that ended on stop_reason='timeout'.
          onSectionDone={() => {
            if (isLastQuestion) {
              void handleCompleteSession();
              return;
            }
            setCurrentQuestionIndex(currentQuestionIndex + 1);
          }}
        />
      );
    }

    /** The running "Selected: 2 of 3" count. Renders nothing when the question has no cap. */
    const renderSelectionCounter = () => {
      const counter = selectionCounterText(currentResponse, multiSelectCap);
      if (!counter) return null;
      return (
        <p className="text-xs text-gray-500" role="status" aria-live="polite">
          {counter}
        </p>
      );
    };

    /**
     * Toggling the "Other" box goes through the SAME cap-aware helper as every other option:
     * "Other" is a selection, so letting it slip past the ceiling would put the respondent one
     * over and the server would reject the submission they cannot see a way to fix.
     *
     * The OTHER shape keeps it distinct from an ordinary option that happens to be labelled
     * "Other" — those are two different selections and must not cancel each other out.
     */
    const toggleOtherSelection = () =>
      toggleMultiSelect(currentResponse, OTHER_CHOICE_VALUE, multiSelectCap, SELECTION_SHAPE.OTHER);

    /** Whether the "Other" box is locked by the cap (never because a same-named option is on). */
    const isOtherLocked = () =>
      isChoiceLocked(currentResponse, OTHER_CHOICE_VALUE, multiSelectCap, SELECTION_SHAPE.OTHER);

    // Helper to render question-level media for question types that don't have special media handling
    const renderSimpleQuestionMedia = () => {
      if (!orderedQuestionMediaUrls || orderedQuestionMediaUrls.length === 0) return null;

      const mediaCount = orderedQuestionMediaUrls.length;

      return (
        <div className="mb-6">
          <div
            className={`grid gap-4 ${
              mediaCount === 1 ? 'grid-cols-1 max-w-2xl mx-auto' : 'grid-cols-1 md:grid-cols-2'
            }`}
          >
            {orderedQuestionMediaUrls.map((url: string, index: number) => {
              const isVideo = isVideoUrl(url);
              const fullUrl = url.startsWith('/api/')
                ? `${import.meta.env.VITE_API_URL || ''}${url}`
                : url;
              // Scope the key/state to the question id: on navigation React remounts the node
              // (no stale decoded frame) and the image starts unloaded (skeleton, not the
              // previous question's image showing through).
              const mediaKey = `${currentQuestion?.id ?? 'q'}-media-${index}`;
              const isLoaded = questionMediaLoaded[mediaKey] === true;
              const hasErrored = questionMediaErrored[mediaKey] === true;

              return (
                <div
                  key={mediaKey}
                  className="relative border border-gray-200 rounded-lg overflow-hidden bg-gray-50 min-h-[200px] flex items-center justify-center"
                >
                  {/* Loading skeleton — covers the frame until this question's media paints,
                      so a question switch never flashes the previous question's images.
                      Hidden once errored too: the skeleton is `absolute inset-0 z-10`, so
                      leaving it up would cover the failure message (and, on a <video>, would
                      swallow clicks on the native controls). */}
                  {!isLoaded && !hasErrored && (
                    <div className="absolute inset-0 z-10 flex items-center justify-center bg-gray-100 animate-pulse">
                      <div className="w-6 h-6 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
                    </div>
                  )}
                  {hasErrored && (
                    <div className="absolute inset-0 z-20 flex items-center justify-center bg-gray-100 p-4">
                      <span className="text-sm text-gray-500 text-center">
                        {isVideo ? 'Failed to load video' : 'Failed to load image'}
                      </span>
                    </div>
                  )}
                  {isVideo ? (
                    url.includes('youtube.com') || url.includes('youtu.be') ? (
                      <div className="aspect-video w-full">
                        <iframe
                          src={
                            url.includes('youtube.com')
                              ? url.replace('watch?v=', 'embed/')
                              : url.replace('youtu.be/', 'youtube.com/embed/')
                          }
                          className={`w-full h-full transition-opacity duration-300 ${isLoaded ? 'opacity-100' : 'opacity-0'}`}
                          allowFullScreen
                          title={`Video ${index + 1}`}
                          onLoad={() => markQuestionMediaLoaded(mediaKey)}
                        />
                      </div>
                    ) : (
                      <video
                        src={fullUrl}
                        className={`w-full h-auto max-h-[400px] transition-opacity duration-300 ${isLoaded ? 'opacity-100' : 'opacity-0'}`}
                        controls
                        // `loadeddata` only fires once a frame has actually decoded, which
                        // never happens under preload="none" (iOS Safari / data-saver) until
                        // the user hits play — but the skeleton overlay sits above the video
                        // and swallows that click, so the respondent gets permanently stuck.
                        // Clear on metadata/canplay too, which fire without decoding a frame.
                        preload="metadata"
                        onLoadedMetadata={() => markQuestionMediaLoaded(mediaKey)}
                        onCanPlay={() => markQuestionMediaLoaded(mediaKey)}
                        onLoadedData={() => markQuestionMediaLoaded(mediaKey)}
                        onError={() => {
                          markQuestionMediaLoaded(mediaKey);
                          markQuestionMediaErrored(mediaKey);
                        }}
                      />
                    )
                  ) : (
                    <img
                      src={fullUrl}
                      alt={`Question media ${index + 1}`}
                      className={`w-full h-auto max-h-[400px] object-contain transition-opacity duration-300 ${isLoaded ? 'opacity-100' : 'opacity-0'}`}
                      // Mark paint (hides skeleton, reveals image) AND report to the stimulus gate
                      // so the answer UI unlocks on paint, not just the 8s fail-open timeout.
                      // A cached image can complete before onLoad attaches, hence the ref check —
                      // but `complete` is also true for a fetch that settled BROKEN, whose error
                      // event has already fired and will not fire again. A settled node always
                      // resolves the gate (so it fails open exactly as onError below); whether it
                      // is also a FAILURE is a question the node's size cannot answer on its own,
                      // so an undecidable one is put to decode(). See readImageSettleState.
                      ref={(el) => {
                        const settle = readImageSettleState(el);
                        if (settle === IMAGE_SETTLE.PENDING) return;
                        markQuestionMediaLoaded(mediaKey);
                        markStimulusResolved(url);
                        if (settle === IMAGE_SETTLE.UNDECIDABLE) {
                          probeImageDecode(
                            el as HTMLImageElement,
                            () => markQuestionMediaLoaded(mediaKey),
                            () => markQuestionMediaErrored(mediaKey),
                          );
                        }
                      }}
                      onLoad={() => {
                        markQuestionMediaLoaded(mediaKey);
                        markStimulusResolved(url);
                      }}
                      // Still resolve the stimulus gate on error — failing open keeps the
                      // answer UI usable rather than stranding the respondent on a dead image.
                      onError={() => {
                        markQuestionMediaLoaded(mediaKey);
                        markQuestionMediaErrored(mediaKey);
                        markStimulusResolved(url);
                      }}
                    />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      );
    };

    // If there's a follow-up question, always show text input interface
    if (followupQuestion) {
      // Check if the original question has media to display
      // For text questions: always show media if available
      // For single/multiple choice: only show media if NOT in explicit 'choices' mode (legacy)
      // For image_hotspot: always show the reference image
      const isTextQuestion =
        currentQuestion.type === 'short_text' || currentQuestion.type === 'long_text';
      const isChoiceQuestion =
        currentQuestion.type === 'single_choice' || currentQuestion.type === 'multiple_choice';
      const isImageHotspot = currentQuestion.type === 'image_hotspot';
      const displayMode = currentQuestion.media_display_mode || 'question'; // Default to question-level for new system
      const isQuestionLevelMedia = displayMode !== 'choices'; // Question-level unless explicitly 'choices'

      // Build image hotspot URL if applicable
      const hotspotImageUrl =
        isImageHotspot && currentQuestion.image_url
          ? currentQuestion.image_url.startsWith('http')
            ? currentQuestion.image_url
            : `${import.meta.env.VITE_API_URL || ''}/api/question-images/public/${survey.id}/${currentQuestion.image_url.split('/').pop()}`
          : null;

      const hasMedia =
        currentQuestion.media_urls &&
        currentQuestion.media_urls.length > 0 &&
        (isTextQuestion || (isChoiceQuestion && isQuestionLevelMedia));

      // Get user's selections from originalResponse for image hotspot
      // originalResponse stores the user's response data (e.g., { clicks: [...] } for image_hotspot)
      const hotspotClicks =
        isImageHotspot &&
        originalResponse &&
        typeof originalResponse === 'object' &&
        originalResponse.clicks
          ? (originalResponse.clicks as Array<{
              rank: number;
              x: number;
              y: number;
              product_id: string;
              product_label: string;
              timestamp_ms: number;
            }>)
          : [];
      const boundingBoxes = isImageHotspot ? currentQuestion.bounding_boxes || [] : [];

      // Get rank color for visual feedback
      const getRankColor = (rank: number): string => {
        const colors = [
          'bg-teal-500', // Rank 1
          'bg-blue-500', // Rank 2
          'bg-purple-500', // Rank 3
          'bg-orange-500', // Rank 4
          'bg-pink-500', // Rank 5
        ];
        return colors[(rank - 1) % colors.length];
      };

      // Image hotspot reference image content with selections overlay
      const hotspotContent = hotspotImageUrl ? (
        <div className="space-y-4 mb-6">
          {/* Reference Image Badge */}
          <div className="flex items-center justify-between">
            <span className="inline-flex items-center rounded-md bg-teal-100 px-2 py-1 text-xs font-medium text-teal-700">
              Reference Image
            </span>
            <span className="text-xs text-gray-500">Your selections are highlighted</span>
          </div>

          {/* Reference Image with Selections Overlay */}
          <div className="border border-gray-200 rounded-lg p-4 shadow-sm flex flex-col items-center">
            <div className="relative inline-block rounded-md overflow-hidden">
              <img
                src={hotspotImageUrl}
                alt="Reference image for follow-up question"
                className="max-w-full h-auto max-h-[400px] object-contain rounded-md"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.style.display = 'none';
                  const parent = target.parentElement;
                  if (parent) {
                    parent.innerHTML =
                      '<div class="w-full h-48 flex items-center justify-center text-sm text-gray-500">Failed to load image</div>';
                  }
                }}
              />

              {/* Bounding boxes overlay - show selected products */}
              {boundingBoxes.map(
                (box: {
                  id: string;
                  label: string;
                  x: number;
                  y: number;
                  width: number;
                  height: number;
                }) => {
                  const isSelected = hotspotClicks.some((click) => click.product_id === box.id);
                  const clickForBox = hotspotClicks.find((click) => click.product_id === box.id);

                  if (!isSelected) return null; // Only show selected boxes

                  return (
                    <div
                      key={box.id}
                      className="absolute border-2 border-teal-500 bg-teal-500/20 pointer-events-none"
                      style={{
                        left: `${box.x * 100}%`,
                        top: `${box.y * 100}%`,
                        width: `${box.width * 100}%`,
                        height: `${box.height * 100}%`,
                      }}
                    >
                      {/* Rank indicator */}
                      {clickForBox && (
                        <div
                          className={`absolute -top-3 -left-3 w-6 h-6 ${getRankColor(clickForBox.rank)} text-white text-xs font-bold rounded-full flex items-center justify-center shadow-md border-2 border-white`}
                        >
                          {clickForBox.rank}
                        </div>
                      )}
                      {/* Product label */}
                      <div className="absolute -bottom-6 left-0 bg-gray-800 text-white text-xs px-2 py-0.5 rounded whitespace-nowrap">
                        {box.label}
                      </div>
                    </div>
                  );
                },
              )}

              {/* Free click mode markers - show click positions */}
              {currentQuestion.free_click_mode &&
                hotspotClicks.map((click) => (
                  <div
                    key={click.rank}
                    className="absolute pointer-events-none"
                    style={{
                      left: `${click.x * 100}%`,
                      top: `${click.y * 100}%`,
                      transform: 'translate(-50%, -50%)',
                    }}
                  >
                    <div
                      className={`w-7 h-7 ${getRankColor(click.rank)} text-white text-xs font-bold rounded-full flex items-center justify-center shadow-lg border-2 border-white`}
                    >
                      {click.rank}
                    </div>
                  </div>
                ))}
            </div>
          </div>

          {/* Selection Summary */}
          {hotspotClicks.length > 0 && (
            <div className="bg-gray-50 rounded-lg p-3 border border-gray-200 mt-4 w-full max-w-md">
              <h4 className="text-sm font-medium text-gray-700 mb-2">Your Selections:</h4>
              <div className="space-y-1">
                {hotspotClicks.map((click) => (
                  <div key={click.rank} className="flex items-center gap-2 text-sm">
                    <span
                      className={`w-5 h-5 ${getRankColor(click.rank)} text-white text-xs font-bold rounded-full flex items-center justify-center`}
                    >
                      {click.rank}
                    </span>
                    <span className="text-gray-700">
                      {currentQuestion.free_click_mode || !click.product_label
                        ? `Location (${(click.x * 100).toFixed(0)}%, ${(click.y * 100).toFixed(0)}%)`
                        : click.product_label}
                    </span>
                    <span className="text-gray-400 text-xs">
                      ({(click.timestamp_ms / 1000).toFixed(1)}s)
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      ) : null;

      const mediaContent = hasMedia ? (
        <div className="space-y-4 mb-6">
          {/* Media Type Badge */}
          <div className="flex items-center justify-between">
            <span className="inline-flex items-center rounded-md bg-gray-100 px-2 py-1 text-xs font-medium text-gray-600">
              Follow-up question
            </span>
          </div>

          {/* Media Grid */}
          <div className="grid gap-6 grid-cols-1 max-w-4xl mx-auto">
            {orderedQuestionMediaUrls.map((url: string, index: number) => {
              // Determine if it's image or video
              const isVideo = isVideoUrl(url);

              return (
                <div
                  key={`${currentQuestion.id}-media-${index}`}
                  className="border border-gray-200 rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow"
                >
                  <div className="relative rounded-md overflow-hidden">
                    {isVideo ? (
                      url.includes('youtube.com') || url.includes('youtu.be') ? (
                        // YouTube Embed
                        <div className="aspect-video">
                          <iframe
                            src={
                              url.includes('youtube.com')
                                ? url.replace('watch?v=', 'embed/')
                                : url.replace('youtu.be/', 'youtube.com/embed/')
                            }
                            className="w-full h-full rounded-md"
                            allowFullScreen
                            title={`Video ${index + 1}`}
                          />
                        </div>
                      ) : (
                        // Regular Video
                        <video
                          src={getFullMediaUrl(url)}
                          className="w-full h-auto max-h-[600px] rounded-md"
                          controls
                          onError={(e) => {
                            const target = e.target as HTMLVideoElement;
                            target.style.display = 'none';
                            const parent = target.parentElement;
                            if (parent) {
                              parent.innerHTML =
                                '<div class="w-full h-48 flex items-center justify-center text-sm text-gray-500">Failed to load video</div>';
                            }
                          }}
                        />
                      )
                    ) : (
                      // Image - show full image without cropping
                      <img
                        src={getFullMediaUrl(url)}
                        alt={`Image ${index + 1} for survey question`}
                        className="w-full h-auto max-h-[500px] object-contain rounded-md"
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.style.display = 'none';
                          const parent = target.parentElement;
                          if (parent) {
                            parent.innerHTML =
                              '<div class="w-full h-48 flex items-center justify-center text-sm text-gray-500">Failed to load image</div>';
                          }
                        }}
                      />
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ) : null;

      return (
        <div className="space-y-6">
          {/* Image Hotspot Reference - shown above text input for follow-up questions */}
          {hotspotContent}

          {/* Media Content - shown above text input for follow-up questions */}
          {mediaContent}

          <VoiceOrTextInput
            value={typeof currentResponse === 'string' ? currentResponse : ''}
            onChange={handleResponseChange}
            showTextInput={showTextInput}
            onShowTextInputChange={setShowTextInput}
            transcriptionUnavailable={transcriptionUnavailable}
            onTranscriptionUnavailable={() => setTranscriptionUnavailable(true)}
            disableVoiceInput={!!currentQuestion?.disable_voice_input}
            disableTextInput={!!currentQuestion?.disable_text_input}
            slotKey={answerSlotKey}
            transcribeContext={{
              sessionId: session?.id,
              surveyId: session?.survey_id,
              questionId: currentQuestion?.id,
              trackingId: sessionStartCtxRef.current?.trackingId,
            }}
            placeholder={UI_TEXT.HUMAN_SURVEY.SESSION.PLACEHOLDERS.OPEN_ENDED}
            onRecordingChange={setIsRecording}
            onError={setError}
          />
        </div>
      );
    }

    switch (currentQuestion.type) {
      case 'long_text':
      case 'short_text':
        // Spec 02: a spoken open-text question inside a rapid-fire block advances on silence
        // instead of a tap. RapidFireVoiceRunner owns its own mic loop and never writes into
        // `currentResponse` — the transcript is patched onto the response already written on
        // advance, by question id (decisions-02.md 2026-08-28T18:12; spec-30 regression guard).
        if (rapidFireActive && session) {
          return (
            <RapidFireVoiceRunner
              key={currentQuestion.id}
              question={{
                id: currentQuestion.id,
                type: currentQuestion.type,
                text: currentQuestion.text,
              }}
              sessionId={session.id}
              // Written with transcription_status='pending' (schemas/survey.py
              // SubmitResponseRequest.transcription_status), not a plain '', so a researcher
              // reading this answer later can tell "we don't have the text yet" apart from
              // "they said nothing" — both used to land as state=ANSWERED value='' (spec-30
              // follow-up; backend fix "a lost transcript is not an empty answer").
              onAdvance={() => {
                // Outstanding from the moment the pending row is written (ADR-0005), and bounded
                // from that same moment — see markTranscriptOutstanding. The attempt id goes back
                // to the runner, which carries it into the delivery below.
                const attemptId = markTranscriptOutstanding(String(currentQuestion.id));
                void handleSubmitResponse('', 'pending');
                return attemptId;
              }}
              // The one delivery path both spoken surfaces share — see deliverSpokenTranscript
              // for the three outcomes and why the release is a `.finally`.
              onTranscript={({ questionId, transcript, noAudioCaptured, attemptId }) =>
                deliverSpokenTranscript({
                  questionId: String(questionId),
                  questionType: currentQuestion.type,
                  transcript,
                  noAudioCaptured,
                  attemptId,
                })
              }
              // The author's window, not the module constant — a block saved before
              // `silence_seconds` existed resolves to the 2s default (spec: silence window).
              silenceAdvanceMs={silenceAdvanceMsFor(rapidFire?.config)}
              transcribe={(blob) => transcribeRapidFireClip(session.id, currentQuestion.id, blob)}
            />
          );
        }
        return (
          <div className="space-y-6">
            {/* Question-level media — info-screen-style responsive grid, shown above text input */}
            {renderSimpleQuestionMedia()}

            <VoiceOrTextInput
              value={typeof currentResponse === 'string' ? currentResponse : ''}
              onChange={handleResponseChange}
              showTextInput={showTextInput}
              onShowTextInputChange={setShowTextInput}
              transcriptionUnavailable={transcriptionUnavailable}
              onTranscriptionUnavailable={() => setTranscriptionUnavailable(true)}
              disableVoiceInput={!!currentQuestion?.disable_voice_input}
              disableTextInput={!!currentQuestion?.disable_text_input}
              slotKey={answerSlotKey}
              transcribeContext={{
                sessionId: session?.id,
                surveyId: session?.survey_id,
                questionId: currentQuestion?.id,
                trackingId: sessionStartCtxRef.current?.trackingId,
              }}
              placeholder={
                currentQuestion.type === 'short_text'
                  ? 'Enter your answer...'
                  : UI_TEXT.HUMAN_SURVEY.SESSION.PLACEHOLDERS.OPEN_ENDED
              }
              // Stopping the mic IS the submit here (Problem 1). Absent in preview, which writes
              // nothing to the server and so has no row to patch a transcript into — and absent
              // for a typed answer by construction, since there is no mic to stop.
              onSpokenAnswerCaptured={
                session && !isPreviewMode
                  ? (clip) =>
                      handleSpokenAnswerCaptured(
                        { id: String(currentQuestion.id), type: currentQuestion.type },
                        currentQuestionIndex,
                        clip,
                      )
                  : undefined
              }
              onRecordingChange={setIsRecording}
              onError={setError}
            />
          </div>
        );

      case 'single_choice':
        // Check if this is a media question and determine display mode
        const hasMediaChoice = currentQuestion.media_urls && currentQuestion.media_urls.length > 0;
        // Only use choice-level mode if EXPLICITLY set to 'choices' (legacy surveys)
        // New system: media_urls = question-level, option_media = per-option
        // Same constant the timing gate reads (questionMediaIsQuestionLevel): the clock keys on
        // whether this branch is taken, so a default that drifted between them would gate one and
        // not the other.
        const displayMode = currentQuestion.media_display_mode || DEFAULT_MEDIA_DISPLAY_MODE;
        const isQuestionMode = displayMode === DEFAULT_MEDIA_DISPLAY_MODE;

        // Question-level media mode (media displayed below question text)
        if (hasMediaChoice && isQuestionMode) {
          return (
            <div className="space-y-4">
              {/* Question-level media — info-screen-style responsive grid. Wires the stimulus
                  gate so choices stay locked until the images paint. */}
              {renderSimpleQuestionMedia()}

              {/* Choices — with option_media if present */}
              {/* Answering is locked until the stimulus above AND this question's own option
                  images have actually painted: these buttons submit on click, so an early tap
                  would rate an image the respondent never saw — and when both kinds of media are
                  present the pack shots are the thing being chosen between. <fieldset disabled>
                  also blocks keyboard activation, which a pointer-events rule would not. Both
                  gates fail open on the same 8s backstop. */}
              <fieldset
                disabled={!answerControlsUnlocked}
                aria-busy={!answerControlsUnlocked}
                className={`space-y-3 border-0 p-0 m-0 transition-opacity duration-200 ${
                  answerControlsUnlocked ? 'opacity-100' : 'opacity-40 cursor-progress'
                }`}
              >
                {!answerControlsUnlocked && (
                  <p className="text-sm text-gray-500 italic" role="status">
                    {UI_TEXT.HUMAN_SURVEY.SESSION.LOADING_STIMULUS}
                  </p>
                )}
                {randomizedChoices.map((choice: string, index: number) => {
                  const isSelected = currentResponse === choice;
                  const origIdx = currentQuestion.choices?.indexOf(choice) ?? index;
                  const choiceMedia =
                    currentQuestion.option_media?.[String(origIdx)] ||
                    currentQuestion.option_media?.[origIdx] ||
                    [];

                  if (choiceMedia.length > 0) {
                    return (
                      <button
                        key={choice}
                        type="button"
                        disabled={isSubmitting}
                        onClick={async () => {
                          handleResponseChange(choice);
                          handleSubmitResponse(choice);
                        }}
                        className={`w-full text-left rounded-xl border-2 transition-all duration-200 overflow-hidden ${
                          isSelected
                            ? 'border-blue-500 bg-blue-50 shadow-md ring-2 ring-blue-200'
                            : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-md'
                        }`}
                      >
                        <div
                          className={`px-4 py-3 flex items-center justify-between ${isSelected ? 'bg-blue-100' : 'bg-gray-50'}`}
                        >
                          <span
                            className={`font-medium ${isSelected ? 'text-blue-900' : 'text-gray-900'}`}
                          >
                            {choice}
                          </span>
                          {isSelected && (
                            <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center">
                              <Check className="w-4 h-4 text-white" />
                            </div>
                          )}
                        </div>
                        <div className="p-3">
                          <div
                            className={`grid gap-2 ${choiceMedia.length === 1 ? 'grid-cols-1' : choiceMedia.length === 2 ? 'grid-cols-2' : 'grid-cols-3'}`}
                          >
                            {choiceMedia.map((url: string, mediaIdx: number) => {
                              // The SAME tile as the gated layout below. This block is a
                              // hand-copied twin of it, and every round that fixed the option
                              // artwork fixed one copy — so it kept the `onError` →
                              // `style.display = 'none'` the others dropped, and stayed the copy
                              // with the defect. Sharing the component is what stops that
                              // recurring. The marks it writes are LIVE here: the fieldset above
                              // gates on `answerControlsUnlocked`, which waits for the option
                              // artwork as well as the question-level stimulus, because when both
                              // kinds of media are present the pack shots are the thing being
                              // chosen between (frontend#425).
                              const mediaKey = choiceOptionMediaKey(
                                optionMediaScopeKey,
                                origIdx,
                                mediaIdx,
                              );
                              return (
                                <ChoiceOptionMediaTile
                                  key={mediaKey}
                                  url={url}
                                  alt={`${choice} media ${mediaIdx + 1}`}
                                  mediaKey={mediaKey}
                                  isLoaded={optionMediaLoaded[mediaKey] === true}
                                  hasFailed={optionMediaErrored[mediaKey] === true}
                                  onPainted={markOptionMediaPainted}
                                  onSettled={markOptionMediaLoaded}
                                  onFailed={markOptionMediaErrored}
                                />
                              );
                            })}
                          </div>
                        </div>
                      </button>
                    );
                  }

                  return (
                    <button
                      key={choice}
                      type="button"
                      disabled={isSubmitting}
                      onClick={async () => {
                        handleResponseChange(choice);
                        handleSubmitResponse(choice);
                      }}
                      className={`w-full text-left px-4 py-3 rounded-lg border-2 transition-all duration-200 ${
                        isSelected
                          ? 'border-blue-500 bg-blue-50 text-blue-900'
                          : 'border-gray-300 bg-white text-gray-900 hover:border-gray-400 hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span>{choice}</span>
                        {isSelected && (
                          <div className="w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center">
                            <Check className="w-3 h-3 text-white" />
                          </div>
                        )}
                      </div>
                    </button>
                  );
                })}

                {/* Other option if enabled */}
                {currentQuestion.allow_other && (
                  <div className="space-y-2">
                    <button
                      type="button"
                      onClick={() => {
                        handleResponseChange({ choice: 'Other', other_text: '' });
                      }}
                      className={`w-full text-left px-4 py-3 rounded-lg border-2 transition-all duration-200 ${
                        typeof currentResponse === 'object' && currentResponse?.choice === 'Other'
                          ? 'border-blue-500 bg-blue-50 text-blue-900'
                          : 'border-gray-300 bg-white text-gray-900 hover:border-gray-400 hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span>{UI_TEXT.HUMAN_SURVEY.SESSION.CHOICES.OTHER}</span>
                        {typeof currentResponse === 'object' &&
                          currentResponse?.choice === 'Other' && (
                            <div className="w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center">
                              <Check className="w-3 h-3 text-white" />
                            </div>
                          )}
                      </div>
                    </button>

                    {/* Text input for Other option */}
                    {typeof currentResponse === 'object' && currentResponse?.choice === 'Other' && (
                      <input
                        type="text"
                        placeholder={UI_TEXT.HUMAN_SURVEY.SESSION.PLACEHOLDERS.OTHER_SPECIFY}
                        value={currentResponse?.other_text || ''}
                        onChange={(e) => {
                          handleResponseChange({
                            choice: 'Other',
                            other_text: e.target.value,
                          });
                        }}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        autoFocus
                      />
                    )}
                  </div>
                )}
              </fieldset>
            </div>
          );
        }

        // Choice-level media mode (media displayed with each choice) - existing/legacy behavior
        // Only use this if option_media is NOT present (option_media is the new preferred approach)
        const hasNewOptionMedia =
          currentQuestion.option_media && Object.keys(currentQuestion.option_media).length > 0;
        if (hasMediaChoice && !isQuestionMode && !hasNewOptionMedia) {
          const displayUrls = orderedQuestionMediaUrls;
          const storageUrls = currentQuestion.media_urls || [];

          return (
            <div className="space-y-4">
              {/* Instructions */}
              <div className="flex items-center justify-between">
                <p className="text-sm text-gray-600">Click on an image to select it.</p>
              </div>

              {/* Image Grid */}
              <div
                className={`grid gap-6 ${
                  displayUrls.length === 1
                    ? 'grid-cols-1 max-w-2xl mx-auto'
                    : displayUrls.length === 2
                      ? 'grid-cols-1 lg:grid-cols-2 max-w-4xl mx-auto'
                      : 'grid-cols-1 lg:grid-cols-2 max-w-5xl mx-auto'
                }`}
              >
                {displayUrls.map((url: string, displayIdx: number) => {
                  const originalIdx = questionMediaOriginalIndices[displayIdx];
                  const isSelected =
                    currentResponse?.selected_indices?.includes(originalIdx) ||
                    currentResponse?.selected_index === originalIdx;

                  return (
                    <div
                      key={`${currentQuestion.id}-image-${originalIdx}`}
                      className={`cursor-pointer transition-all duration-200 hover:shadow-lg border-2 rounded-lg p-4 ${
                        isSelected
                          ? 'border-blue-500 bg-blue-50 shadow-md'
                          : 'border-gray-300 hover:border-gray-400 hover:shadow-md'
                      }`}
                      onClick={() => {
                        const response = {
                          selected_indices: [originalIdx],
                          selected_urls: [storageUrls[originalIdx]],
                          selected_index: originalIdx,
                        };
                        handleResponseChange(response);
                        handleSubmitResponse(response);
                      }}
                    >
                      {/* Selection Indicator */}
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-sm font-medium text-gray-700">
                          {currentQuestion.choices?.[originalIdx] || `Image ${originalIdx + 1}`}
                        </span>
                        {isSelected && <Check className="h-5 w-5 text-blue-500" />}
                      </div>

                      {/* Image Container */}
                      <div className="relative rounded-md overflow-hidden">
                        <img
                          src={getFullMediaUrl(url)}
                          alt={`Image ${originalIdx + 1} - Choice option`}
                          className="w-full h-auto max-h-80 object-contain rounded-md"
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.style.display = 'none';
                            const parent = target.parentElement;
                            if (parent) {
                              parent.innerHTML =
                                '<div class="w-full h-32 flex items-center justify-center text-sm text-gray-500">Failed to load image</div>';
                            }
                          }}
                        />

                        {/* Selection Overlay */}
                        {isSelected && (
                          <div className="absolute inset-0 bg-blue-500 bg-opacity-20 flex items-center justify-center">
                            <Check className="h-8 w-8 text-white drop-shadow-md" />
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        }

        // Check if any options have option_media. Read off the single derivation above, so what
        // disables these tiles is by construction the same fact the answer clock consults. Note
        // the tiles lock INDIVIDUALLY — a choice with no media stays clickable — which is why the
        // clock only waits on this gate when it covers every choice (see inputsGatedByOptionMedia).
        const hasOptionMedia = questionHasOptionMedia;
        const allOptionMediaLoaded = optionMediaSettled;

        // If we have option_media AND media_urls, render media_urls as question-level media
        const questionLevelMedia =
          hasOptionMedia && hasMediaChoice ? orderedQuestionMediaUrls : null;

        return (
          <div className="space-y-3">
            {/* Render question-level media if present */}
            {questionLevelMedia && questionLevelMedia.length > 0 && (
              <div className="mb-6">
                <div className="grid gap-4 grid-cols-1 max-w-3xl mx-auto">
                  {questionLevelMedia.map((url: string, mediaIdx: number) => {
                    const isVideo = isVideoUrl(url);
                    return (
                      <div
                        key={`q-media-${mediaIdx}`}
                        className="border border-gray-200 rounded-lg overflow-hidden shadow-sm"
                      >
                        {isVideo ? (
                          <video
                            src={getFullMediaUrl(url)}
                            className="w-full h-auto max-h-[400px]"
                            controls
                          />
                        ) : (
                          <img
                            src={getFullMediaUrl(url)}
                            alt={`Question media ${mediaIdx + 1}`}
                            className="w-full h-auto max-h-[400px] object-contain"
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            {randomizedChoices.map((choice: string, index: number) => {
              const isSelected = currentResponse === choice;
              // Get the original index for option_media lookup (before randomization)
              const originalIndex = currentQuestion.choices?.indexOf(choice) ?? index;
              const optionMediaUrls =
                currentQuestion.option_media?.[String(originalIndex)] ||
                currentQuestion.option_media?.[originalIndex] ||
                [];

              // Render differently based on whether option has media
              if (optionMediaUrls.length > 0) {
                // Card-style layout for options with media
                return (
                  <button
                    key={choice}
                    type="button"
                    disabled={isSubmitting || (hasOptionMedia && !allOptionMediaLoaded)}
                    onClick={async () => {
                      if (hasOptionMedia && !allOptionMediaLoaded) return;
                      handleResponseChange(choice);
                      handleSubmitResponse(choice);
                    }}
                    className={`w-full text-left rounded-xl border-2 transition-all duration-200 overflow-hidden ${
                      hasOptionMedia && !allOptionMediaLoaded
                        ? 'border-gray-200 bg-gray-50 text-gray-400 cursor-not-allowed'
                        : isSelected
                          ? 'border-blue-500 bg-blue-50 shadow-md ring-2 ring-blue-200'
                          : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-md'
                    }`}
                  >
                    {/* Option header with choice text */}
                    <div
                      className={`px-4 py-3 flex items-center justify-between ${
                        isSelected ? 'bg-blue-100' : 'bg-gray-50'
                      }`}
                    >
                      <span
                        className={`font-medium ${isSelected ? 'text-blue-900' : 'text-gray-900'}`}
                      >
                        {choice}
                      </span>
                      {isSelected && (
                        <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center">
                          <Check className="w-4 h-4 text-white" />
                        </div>
                      )}
                    </div>

                    {/* Media grid */}
                    <div className="p-3">
                      <div
                        className={`grid gap-2 ${
                          optionMediaUrls.length === 1
                            ? 'grid-cols-1'
                            : optionMediaUrls.length === 2
                              ? 'grid-cols-2'
                              : 'grid-cols-3'
                        }`}
                      >
                        {optionMediaUrls.map((url: string, mediaIndex: number) => {
                          const mediaKey = choiceOptionMediaKey(
                            optionMediaScopeKey,
                            originalIndex,
                            mediaIndex,
                          );
                          return (
                            <ChoiceOptionMediaTile
                              key={mediaKey}
                              url={url}
                              alt={`${choice} media ${mediaIndex + 1}`}
                              mediaKey={mediaKey}
                              isLoaded={optionMediaLoaded[mediaKey] === true}
                              hasFailed={optionMediaErrored[mediaKey] === true}
                              onPainted={markOptionMediaPainted}
                              onSettled={markOptionMediaLoaded}
                              onFailed={markOptionMediaErrored}
                            />
                          );
                        })}
                      </div>
                    </div>
                  </button>
                );
              }

              // Standard layout for options without media
              return (
                <button
                  key={choice}
                  type="button"
                  disabled={isSubmitting}
                  onClick={async () => {
                    handleResponseChange(choice);
                    handleSubmitResponse(choice);
                  }}
                  className={`w-full text-left px-4 py-3 rounded-lg border-2 transition-all duration-200 ${
                    isSelected
                      ? 'border-blue-500 bg-blue-50 text-blue-900'
                      : 'border-gray-300 bg-white text-gray-900 hover:border-gray-400 hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span>{choice}</span>
                    {isSelected && (
                      <div className="w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center">
                        <Check className="w-3 h-3 text-white" />
                      </div>
                    )}
                  </div>
                </button>
              );
            })}

            {/* Other option if enabled */}
            {currentQuestion.allow_other && (
              <div className="space-y-2">
                <button
                  type="button"
                  onClick={() => {
                    handleResponseChange({ choice: 'Other', other_text: '' });
                  }}
                  className={`w-full text-left px-4 py-3 rounded-lg border-2 transition-all duration-200 ${
                    typeof currentResponse === 'object' && currentResponse?.choice === 'Other'
                      ? 'border-blue-500 bg-blue-50 text-blue-900'
                      : 'border-gray-300 bg-white text-gray-900 hover:border-gray-400 hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span>{UI_TEXT.HUMAN_SURVEY.SESSION.CHOICES.OTHER}</span>
                    {typeof currentResponse === 'object' && currentResponse?.choice === 'Other' && (
                      <div className="w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center">
                        <Check className="w-3 h-3 text-white" />
                      </div>
                    )}
                  </div>
                </button>

                {/* Text input for Other option */}
                {typeof currentResponse === 'object' && currentResponse?.choice === 'Other' && (
                  <input
                    type="text"
                    placeholder={UI_TEXT.HUMAN_SURVEY.SESSION.PLACEHOLDERS.OTHER_SPECIFY}
                    value={currentResponse?.other_text || ''}
                    onChange={(e) => {
                      handleResponseChange({
                        choice: 'Other',
                        other_text: e.target.value,
                      });
                    }}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    autoFocus
                  />
                )}
              </div>
            )}
          </div>
        );

      case 'multiple_choice':
        // Check if this is a media question and determine display mode
        const hasMediaMultiple =
          currentQuestion.media_urls && currentQuestion.media_urls.length > 0;
        // Only use choice-level mode if EXPLICITLY set to 'choices' (legacy surveys)
        // New system: media_urls = question-level, option_media = per-option
        // Same constant the timing gate reads — see the single_choice branch above.
        const displayModeMultiple =
          currentQuestion.media_display_mode || DEFAULT_MEDIA_DISPLAY_MODE;
        const isQuestionModeMultiple = displayModeMultiple === DEFAULT_MEDIA_DISPLAY_MODE;

        // Question-level media mode (media displayed below question text)
        if (hasMediaMultiple && isQuestionModeMultiple) {
          return (
            <div className="space-y-4">
              {/* Question-level media — info-screen-style responsive grid. Wires the stimulus
                  gate so choices stay locked until the images paint. */}
              {renderSimpleQuestionMedia()}

              {/* Choices — with option_media if present */}
              {/* Selection is locked until the stimulus above AND this question's own option
                  images have actually painted, so the respondent cannot tick options for an image
                  they never saw. These buttons only TOGGLE (unlike single_choice, which submits on
                  click) — the submit is the Next button, which is gated on stimulusReady too.
                  <fieldset disabled> also blocks keyboard activation, which a pointer-events rule
                  would not. Both gates fail open on the same 8s backstop. */}
              <fieldset
                disabled={!answerControlsUnlocked}
                aria-busy={!answerControlsUnlocked}
                className={`space-y-3 border-0 p-0 m-0 transition-opacity duration-200 ${
                  answerControlsUnlocked ? 'opacity-100' : 'opacity-40 cursor-progress'
                }`}
              >
                {!answerControlsUnlocked && (
                  <p className="text-sm text-gray-500 italic" role="status">
                    {UI_TEXT.HUMAN_SURVEY.SESSION.LOADING_STIMULUS}
                  </p>
                )}
                {renderSelectionCounter()}
                {randomizedChoices.map((choice: string, index: number) => {
                  // Same predicate as the lock and the toggle, so display can never disagree with
                  // them. Matching on the choice VALUE alone did: with `[{choice:'Other'}]` stored
                  // and a literal "Other" option on the question, the option painted itself blue
                  // with a tick while `isChoiceSelected` said it was not selected — so clicking it
                  // appended a DUPLICATE instead of deselecting, and at the cap it rendered as
                  // chosen while `disabled`, which is the stranded-respondent case this PR exists
                  // to prevent.
                  const isSelected = isChoiceSelected(currentResponse, choice);
                  const isLocked = isChoiceLocked(currentResponse, choice, multiSelectCap);
                  const origIdx = currentQuestion.choices?.indexOf(choice) ?? index;
                  const choiceMedia =
                    currentQuestion.option_media?.[String(origIdx)] ||
                    currentQuestion.option_media?.[origIdx] ||
                    [];

                  const toggleChoice = () => {
                    handleResponseChange(
                      toggleMultiSelect(currentResponse, choice, multiSelectCap),
                    );
                  };

                  if (choiceMedia.length > 0) {
                    return (
                      <button
                        key={choice}
                        type="button"
                        onClick={toggleChoice}
                        disabled={isLocked}
                        className={`w-full text-left rounded-xl border-2 transition-all duration-200 overflow-hidden ${
                          isSelected
                            ? 'border-blue-500 bg-blue-50 shadow-md ring-2 ring-blue-200'
                            : isLocked
                              ? CAPPED_OPTION_CLASS
                              : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-md'
                        }`}
                      >
                        <div
                          className={`px-4 py-3 flex items-center justify-between ${isSelected ? 'bg-blue-100' : 'bg-gray-50'}`}
                        >
                          <span
                            className={`font-medium ${isSelected ? 'text-blue-900' : 'text-gray-900'}`}
                          >
                            {choice}
                          </span>
                          {isSelected && (
                            <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center">
                              <Check className="w-4 h-4 text-white" />
                            </div>
                          )}
                        </div>
                        <div className="p-3">
                          <div
                            className={`grid gap-2 ${choiceMedia.length === 1 ? 'grid-cols-1' : choiceMedia.length === 2 ? 'grid-cols-2' : 'grid-cols-3'}`}
                          >
                            {choiceMedia.map((url: string, mediaIdx: number) => {
                              // The SAME tile as the gated layout below. This block is a
                              // hand-copied twin of it, and every round that fixed the option
                              // artwork fixed one copy — so it kept the `onError` →
                              // `style.display = 'none'` the others dropped, and stayed the copy
                              // with the defect. Sharing the component is what stops that
                              // recurring. The marks it writes are LIVE here: the fieldset above
                              // gates on `answerControlsUnlocked`, which waits for the option
                              // artwork as well as the question-level stimulus, because when both
                              // kinds of media are present the pack shots are the thing being
                              // chosen between (frontend#425).
                              const mediaKey = choiceOptionMediaKey(
                                optionMediaScopeKey,
                                origIdx,
                                mediaIdx,
                              );
                              return (
                                <ChoiceOptionMediaTile
                                  key={mediaKey}
                                  url={url}
                                  alt={`${choice} media ${mediaIdx + 1}`}
                                  mediaKey={mediaKey}
                                  isLoaded={optionMediaLoaded[mediaKey] === true}
                                  hasFailed={optionMediaErrored[mediaKey] === true}
                                  onPainted={markOptionMediaPainted}
                                  onSettled={markOptionMediaLoaded}
                                  onFailed={markOptionMediaErrored}
                                />
                              );
                            })}
                          </div>
                        </div>
                      </button>
                    );
                  }

                  return (
                    <button
                      key={choice}
                      type="button"
                      onClick={toggleChoice}
                      disabled={isLocked}
                      className={`w-full text-left px-4 py-3 rounded-lg border-2 transition-all duration-200 ${
                        isSelected
                          ? 'border-blue-500 bg-blue-50 text-blue-900'
                          : isLocked
                            ? CAPPED_OPTION_CLASS
                            : 'border-gray-300 bg-white text-gray-900 hover:border-gray-400 hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span>{choice}</span>
                        {isSelected && (
                          <div className="w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center">
                            <Check className="w-3 h-3 text-white" />
                          </div>
                        )}
                      </div>
                    </button>
                  );
                })}

                {/* Other option if enabled */}
                {currentQuestion.allow_other && (
                  <div className="space-y-2">
                    <button
                      type="button"
                      onClick={() => handleResponseChange(toggleOtherSelection())}
                      disabled={isOtherLocked()}
                      className={`w-full text-left px-4 py-3 rounded-lg border-2 transition-all duration-200 ${
                        Array.isArray(currentResponse) &&
                        currentResponse.some(
                          (item: any) => typeof item === 'object' && item?.choice === 'Other',
                        )
                          ? 'border-blue-500 bg-blue-50 text-blue-900'
                          : isOtherLocked()
                            ? CAPPED_OPTION_CLASS
                            : 'border-gray-300 bg-white text-gray-900 hover:border-gray-400 hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span>{UI_TEXT.HUMAN_SURVEY.SESSION.CHOICES.OTHER}</span>
                        {Array.isArray(currentResponse) &&
                          currentResponse.some(
                            (item: any) => typeof item === 'object' && item?.choice === 'Other',
                          ) && (
                            <div className="w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center">
                              <Check className="w-3 h-3 text-white" />
                            </div>
                          )}
                      </div>
                    </button>

                    {/* Text input for Other option */}
                    {Array.isArray(currentResponse) &&
                      currentResponse.some(
                        (item: any) => typeof item === 'object' && item?.choice === 'Other',
                      ) && (
                        <input
                          type="text"
                          placeholder={UI_TEXT.HUMAN_SURVEY.SESSION.PLACEHOLDERS.OTHER_SPECIFY}
                          value={(() => {
                            const otherItem = currentResponse.find(
                              (item: any) => typeof item === 'object' && item?.choice === 'Other',
                            );
                            return otherItem?.other_text || '';
                          })()}
                          onChange={(e) => {
                            const responses = Array.isArray(currentResponse) ? currentResponse : [];
                            const updatedResponses = responses.map((item: any) =>
                              typeof item === 'object' && item?.choice === 'Other'
                                ? { choice: 'Other', other_text: e.target.value }
                                : item,
                            );
                            handleResponseChange(updatedResponses);
                          }}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          autoFocus
                        />
                      )}
                  </div>
                )}
              </fieldset>
            </div>
          );
        }

        // Choice-level media mode (media displayed with each choice) - existing/legacy behavior
        // Only use this if option_media is NOT present (option_media is the new preferred approach)
        const hasNewOptionMediaMultiple =
          currentQuestion.option_media && Object.keys(currentQuestion.option_media).length > 0;
        if (hasMediaMultiple && !isQuestionModeMultiple && !hasNewOptionMediaMultiple) {
          const displayUrls = orderedQuestionMediaUrls;
          const storageUrls = currentQuestion.media_urls || [];
          const selectedIndices = currentResponse?.selected_indices || [];

          return (
            <div className="space-y-4">
              {/* Instructions */}
              <div className="flex items-center justify-between">
                <p className="text-sm text-gray-600">
                  You can select multiple images by clicking on them.
                </p>
                <span className="inline-flex items-center rounded-md bg-gray-100 px-2 py-1 text-xs font-medium text-gray-600">
                  Select multiple
                </span>
              </div>
              {renderSelectionCounter()}

              {/* Image Grid */}
              <div
                className={`grid gap-6 ${
                  displayUrls.length === 1
                    ? 'grid-cols-1 max-w-2xl mx-auto'
                    : displayUrls.length === 2
                      ? 'grid-cols-1 lg:grid-cols-2 max-w-4xl mx-auto'
                      : 'grid-cols-1 lg:grid-cols-2 max-w-5xl mx-auto'
                }`}
              >
                {displayUrls.map((url: string, displayIdx: number) => {
                  const originalIdx = questionMediaOriginalIndices[displayIdx];
                  const isSelected = selectedIndices.includes(originalIdx);
                  // Legacy image-choice mode stores selections as indices, not choice strings, so
                  // it uses the cap primitive directly rather than the string-keyed toggle. The
                  // rule is identical: at the cap only UNSELECTED tiles lock.
                  const isLocked =
                    !isSelected && isAddBlocked(selectedIndices.length, multiSelectCap);

                  return (
                    <div
                      key={`${currentQuestion.id}-image-${originalIdx}`}
                      aria-disabled={isLocked}
                      className={`transition-all duration-200 border-2 rounded-lg p-4 ${
                        isSelected
                          ? 'border-blue-500 bg-blue-50 shadow-md cursor-pointer hover:shadow-lg'
                          : isLocked
                            ? `${CAPPED_OPTION_CLASS} opacity-60`
                            : 'border-gray-300 cursor-pointer hover:border-gray-400 hover:shadow-lg'
                      }`}
                      onClick={() => {
                        if (isLocked) return;
                        let newSelectedIndices: number[];
                        if (isSelected) {
                          newSelectedIndices = selectedIndices.filter(
                            (i: number) => i !== originalIdx,
                          );
                        } else {
                          newSelectedIndices = [...selectedIndices, originalIdx];
                        }

                        const response = {
                          selected_indices: newSelectedIndices,
                          selected_urls: newSelectedIndices.map((i: number) => storageUrls[i]),
                        };
                        handleResponseChange(response);
                      }}
                    >
                      {/* Selection Indicator */}
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-sm font-medium text-gray-700">
                          {currentQuestion.choices?.[originalIdx] || `Image ${originalIdx + 1}`}
                        </span>
                        {isSelected && <Check className="h-5 w-5 text-blue-500" />}
                      </div>

                      {/* Image Container */}
                      <div className="relative rounded-md overflow-hidden">
                        <img
                          src={getFullMediaUrl(url)}
                          alt={`Image ${originalIdx + 1} - Choice option`}
                          className="w-full h-auto max-h-80 object-contain rounded-md"
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.style.display = 'none';
                            const parent = target.parentElement;
                            if (parent) {
                              parent.innerHTML =
                                '<div class="w-full h-32 flex items-center justify-center text-sm text-gray-500">Failed to load image</div>';
                            }
                          }}
                        />

                        {/* Selection Overlay */}
                        {isSelected && (
                          <div className="absolute inset-0 bg-blue-500 bg-opacity-20 flex items-center justify-center">
                            <Check className="h-8 w-8 text-white drop-shadow-md" />
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        }

        // Check if any options have option_media for multiple choice. Same single derivation as
        // the single-choice branch above.
        const hasOptionMediaMultiple = questionHasOptionMedia;
        const allOptionMediaLoadedMultiple = optionMediaSettled;

        // If we have option_media AND media_urls, render media_urls as question-level media
        const questionLevelMediaMultiple =
          hasOptionMediaMultiple && hasMediaMultiple ? orderedQuestionMediaUrls : null;

        return (
          <div className="space-y-3">
            {/* Render question-level media if present */}
            {questionLevelMediaMultiple && questionLevelMediaMultiple.length > 0 && (
              <div className="mb-6">
                <div className="grid gap-4 grid-cols-1 max-w-3xl mx-auto">
                  {questionLevelMediaMultiple.map((url: string, mediaIdx: number) => {
                    const isVideo = isVideoUrl(url);
                    return (
                      <div
                        key={`q-media-${mediaIdx}`}
                        className="border border-gray-200 rounded-lg overflow-hidden shadow-sm"
                      >
                        {isVideo ? (
                          <video
                            src={getFullMediaUrl(url)}
                            className="w-full h-auto max-h-[400px]"
                            controls
                          />
                        ) : (
                          <img
                            src={getFullMediaUrl(url)}
                            alt={`Question media ${mediaIdx + 1}`}
                            className="w-full h-auto max-h-[400px] object-contain"
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            {renderSelectionCounter()}
            {randomizedChoices.map((choice: string, index: number) => {
              // Shape + value, exactly as the lock and the toggle key it — see the identical
              // renderer above for why matching on the value alone strands the respondent.
              const isSelected = isChoiceSelected(currentResponse, choice);
              const isLocked = isChoiceLocked(currentResponse, choice, multiSelectCap);
              // Get the original index for option_media lookup (before randomization)
              const originalIndexMultiple = currentQuestion.choices?.indexOf(choice) ?? index;
              const optionMediaUrlsMultiple =
                currentQuestion.option_media?.[String(originalIndexMultiple)] ||
                currentQuestion.option_media?.[originalIndexMultiple] ||
                [];
              const mediaStillLoading = hasOptionMediaMultiple && !allOptionMediaLoadedMultiple;

              // Render card-style for options with media
              if (optionMediaUrlsMultiple.length > 0) {
                return (
                  <button
                    key={choice}
                    type="button"
                    disabled={mediaStillLoading || isLocked}
                    onClick={() => {
                      if (mediaStillLoading) return;
                      handleResponseChange(
                        toggleMultiSelect(currentResponse, choice, multiSelectCap),
                      );
                    }}
                    className={`w-full text-left rounded-xl border-2 transition-all duration-200 overflow-hidden ${
                      mediaStillLoading
                        ? 'border-gray-200 bg-gray-50 text-gray-400 cursor-not-allowed'
                        : isSelected
                          ? 'border-blue-500 bg-blue-50 shadow-md ring-2 ring-blue-200'
                          : isLocked
                            ? CAPPED_OPTION_CLASS
                            : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-md'
                    }`}
                  >
                    {/* Option header with choice text */}
                    <div
                      className={`px-4 py-3 flex items-center justify-between ${
                        isSelected ? 'bg-blue-100' : 'bg-gray-50'
                      }`}
                    >
                      <span
                        className={`font-medium ${isSelected ? 'text-blue-900' : 'text-gray-900'}`}
                      >
                        {choice}
                      </span>
                      <div
                        className={`w-6 h-6 rounded border-2 flex items-center justify-center ${
                          isSelected ? 'bg-blue-500 border-blue-500' : 'border-gray-300 bg-white'
                        }`}
                      >
                        {isSelected && <Check className="w-4 h-4 text-white" />}
                      </div>
                    </div>

                    {/* Media grid */}
                    <div className="p-3">
                      <div
                        className={`grid gap-2 ${
                          optionMediaUrlsMultiple.length === 1
                            ? 'grid-cols-1'
                            : optionMediaUrlsMultiple.length === 2
                              ? 'grid-cols-2'
                              : 'grid-cols-3'
                        }`}
                      >
                        {optionMediaUrlsMultiple.map((url: string, mediaIndex: number) => {
                          const mediaKey = choiceOptionMediaKey(
                            optionMediaScopeKey,
                            originalIndexMultiple,
                            mediaIndex,
                          );
                          return (
                            <ChoiceOptionMediaTile
                              key={mediaKey}
                              url={url}
                              alt={`${choice} media ${mediaIndex + 1}`}
                              mediaKey={mediaKey}
                              isLoaded={optionMediaLoaded[mediaKey] === true}
                              hasFailed={optionMediaErrored[mediaKey] === true}
                              onPainted={markOptionMediaPainted}
                              onSettled={markOptionMediaLoaded}
                              onFailed={markOptionMediaErrored}
                            />
                          );
                        })}
                      </div>
                    </div>
                  </button>
                );
              }

              // Standard layout for options without media
              return (
                <button
                  key={choice}
                  type="button"
                  onClick={() => {
                    handleResponseChange(
                      toggleMultiSelect(currentResponse, choice, multiSelectCap),
                    );
                  }}
                  disabled={isLocked}
                  className={`w-full text-left px-4 py-3 rounded-lg border-2 transition-all duration-200 ${
                    isSelected
                      ? 'border-blue-500 bg-blue-50 text-blue-900'
                      : isLocked
                        ? CAPPED_OPTION_CLASS
                        : 'border-gray-300 bg-white text-gray-900 hover:border-gray-400 hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span>{choice}</span>
                    <div
                      className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                        isSelected ? 'bg-blue-500 border-blue-500' : 'border-gray-300 bg-white'
                      }`}
                    >
                      {isSelected && <Check className="w-3 h-3 text-white" />}
                    </div>
                  </div>
                </button>
              );
            })}

            {/* Other option if enabled */}
            {currentQuestion.allow_other && (
              <div className="space-y-2">
                <button
                  type="button"
                  onClick={() => handleResponseChange(toggleOtherSelection())}
                  disabled={isOtherLocked()}
                  className={`w-full text-left px-4 py-3 rounded-lg border-2 transition-all duration-200 ${
                    Array.isArray(currentResponse) &&
                    currentResponse.some(
                      (item: any) => typeof item === 'object' && item?.choice === 'Other',
                    )
                      ? 'border-blue-500 bg-blue-50 text-blue-900'
                      : isOtherLocked()
                        ? CAPPED_OPTION_CLASS
                        : 'border-gray-300 bg-white text-gray-900 hover:border-gray-400 hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span>{UI_TEXT.HUMAN_SURVEY.SESSION.CHOICES.OTHER}</span>
                    {Array.isArray(currentResponse) &&
                      currentResponse.some(
                        (item: any) => typeof item === 'object' && item?.choice === 'Other',
                      ) && (
                        <div className="w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center">
                          <Check className="w-3 h-3 text-white" />
                        </div>
                      )}
                  </div>
                </button>

                {/* Text input for Other option */}
                {Array.isArray(currentResponse) &&
                  currentResponse.some(
                    (item: any) => typeof item === 'object' && item?.choice === 'Other',
                  ) && (
                    <input
                      type="text"
                      placeholder={UI_TEXT.HUMAN_SURVEY.SESSION.PLACEHOLDERS.OTHER_SPECIFY}
                      value={(() => {
                        const otherItem = currentResponse.find(
                          (item: any) => typeof item === 'object' && item?.choice === 'Other',
                        );
                        return otherItem?.other_text || '';
                      })()}
                      onChange={(e) => {
                        const responses = Array.isArray(currentResponse) ? currentResponse : [];
                        const updatedResponses = responses.map((item: any) =>
                          typeof item === 'object' && item?.choice === 'Other'
                            ? { choice: 'Other', other_text: e.target.value }
                            : item,
                        );
                        handleResponseChange(updatedResponses);
                      }}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      autoFocus
                    />
                  )}
              </div>
            )}
          </div>
        );

      case 'yes_no':
        return (
          <div className="space-y-3">
            {renderSimpleQuestionMedia()}
            {(currentQuestion.choices || ['Yes', 'No']).map((choice: string, index: number) => {
              const isSelected = currentResponse === choice;
              return (
                <button
                  key={index}
                  type="button"
                  disabled={isSubmitting}
                  onClick={async () => {
                    handleResponseChange(choice);
                    handleSubmitResponse(choice);
                  }}
                  className={`w-full text-left px-4 py-3 rounded-lg border-2 transition-all duration-200 ${
                    isSelected
                      ? 'border-blue-500 bg-blue-50 text-blue-900'
                      : 'border-gray-300 bg-white text-gray-900 hover:border-gray-400 hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span>{choice}</span>
                    {isSelected && (
                      <div className="w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center">
                        <Check className="w-3 h-3 text-white" />
                      </div>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        );

      case 'rating_scale':
        return (
          <div className="space-y-4">
            {renderSimpleQuestionMedia()}
            <div className="text-center">
              <div className="flex justify-center space-x-2">
                {Array.from(
                  {
                    length: (currentQuestion.scale_max || 5) - (currentQuestion.scale_min || 1) + 1,
                  },
                  (_, index) => {
                    const value = (currentQuestion.scale_min || 1) + index;
                    const isSelected = currentResponse === value;
                    return (
                      <button
                        key={value}
                        type="button"
                        disabled={isSubmitting}
                        onClick={() => {
                          handleResponseChange(value);
                          handleSubmitResponse(value);
                        }}
                        className={`w-12 h-12 rounded-full border-2 font-semibold transition-all duration-200 ${
                          isSelected
                            ? 'border-blue-500 bg-blue-500 text-white'
                            : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400 hover:bg-gray-50'
                        }`}
                      >
                        {value}
                      </button>
                    );
                  },
                )}
              </div>

              {/* Scale labels */}
              <div className="flex items-center justify-between text-xs text-gray-500 mt-3 max-w-xs mx-auto">
                <span className="text-left">Not at all</span>
                <span className="text-right">Extremely</span>
              </div>
            </div>
          </div>
        );

      case 'likert_scale':
        return (
          <div className="space-y-3">
            {renderSimpleQuestionMedia()}
            {['Strongly Disagree', 'Disagree', 'Neutral', 'Agree', 'Strongly Agree'].map(
              (choice: string, index: number) => {
                const isSelected = currentResponse === choice;
                return (
                  <button
                    key={index}
                    type="button"
                    disabled={isSubmitting}
                    onClick={async () => {
                      handleResponseChange(choice);
                      handleSubmitResponse(choice);
                    }}
                    className={`w-full text-left px-4 py-3 rounded-lg border-2 transition-all duration-200 ${
                      isSelected
                        ? 'border-blue-500 bg-blue-50 text-blue-900'
                        : 'border-gray-300 bg-white text-gray-900 hover:border-gray-400 hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span>{choice}</span>
                      {isSelected && (
                        <div className="w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center">
                          <Check className="w-3 h-3 text-white" />
                        </div>
                      )}
                    </div>
                  </button>
                );
              },
            )}
          </div>
        );

      case 'dropdown':
        return (
          <div className="space-y-4">
            {renderSimpleQuestionMedia()}
            <select
              value={currentResponse || ''}
              disabled={isSubmitting}
              onChange={(e) => {
                handleResponseChange(e.target.value);
                handleSubmitResponse(e.target.value);
              }}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              autoFocus
            >
              <option value="">Select an option...</option>
              {currentQuestion.choices?.map((choice: string, index: number) => (
                <option key={index} value={choice}>
                  {choice}
                </option>
              ))}
            </select>
          </div>
        );

      case 'nps':
        return (
          <div className="space-y-4">
            {renderSimpleQuestionMedia()}
            <div className="text-center">
              <div className="text-sm text-gray-600 mb-4">
                On a scale of 0-10, how likely are you to recommend this to a friend or colleague?
              </div>
              <div className="flex items-center justify-between text-xs text-gray-500 mb-2">
                <span>Not at all likely</span>
                <span>Extremely likely</span>
              </div>
              <div className="flex justify-center space-x-1">
                {Array.from({ length: 11 }, (_, index) => {
                  const value = index;
                  const isSelected = currentResponse === value;
                  return (
                    <button
                      key={value}
                      type="button"
                      disabled={isSubmitting}
                      onClick={() => {
                        handleResponseChange(value);
                        handleSubmitResponse(value);
                      }}
                      className={`w-10 h-10 rounded border-2 font-semibold text-sm transition-all duration-200 ${
                        isSelected
                          ? 'border-blue-500 bg-blue-500 text-white'
                          : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400 hover:bg-gray-50'
                      }`}
                    >
                      {value}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        );

      case 'numeric_input':
        return (
          <div className="space-y-4">
            {renderSimpleQuestionMedia()}
            <input
              type="number"
              value={currentResponse || ''}
              onChange={(e) => handleResponseChange(e.target.value)}
              placeholder="Enter a number..."
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              autoFocus
            />
          </div>
        );

      case 'ranking':
        return (
          <div className="space-y-4">
            {renderSimpleQuestionMedia()}
            <div className="text-sm text-gray-600 mb-4">
              Click to select, then drag to reorder your top {currentQuestion.ranking_count || 3}{' '}
              choices:
            </div>

            {/* Ranked items (draggable) */}
            <div className="mb-4">
              <div className="text-sm font-medium text-gray-700 mb-2">Your Rankings:</div>
              <div className="space-y-2 min-h-[60px] p-3 border-2 border-dashed border-gray-300 rounded-lg bg-gray-50">
                {Array.isArray(currentResponse) && currentResponse.length > 0 ? (
                  currentResponse.map((choice: string, index: number) => (
                    <div
                      key={choice}
                      draggable
                      onDragStart={(e) => {
                        e.dataTransfer.setData('text/plain', index.toString());
                        e.currentTarget.style.opacity = '0.5';
                      }}
                      onDragEnd={(e) => {
                        e.currentTarget.style.opacity = '1';
                      }}
                      onDragOver={(e) => {
                        e.preventDefault();
                      }}
                      onDrop={(e) => {
                        e.preventDefault();
                        const draggedIndex = parseInt(e.dataTransfer.getData('text/plain'));
                        const droppedIndex = index;

                        if (draggedIndex !== droppedIndex) {
                          const newRankings = Array.isArray(currentResponse)
                            ? [...currentResponse]
                            : [];
                          const draggedItem = newRankings[draggedIndex];
                          newRankings.splice(draggedIndex, 1);
                          newRankings.splice(droppedIndex, 0, draggedItem);
                          handleResponseChange(newRankings);
                        }
                      }}
                      className="flex items-center justify-between px-4 py-3 bg-blue-50 border-2 border-blue-200 rounded-lg cursor-move hover:bg-blue-100 transition-colors"
                    >
                      <div className="flex items-center space-x-3">
                        <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center text-white text-sm font-bold">
                          {index + 1}
                        </div>
                        <span className="text-blue-900 font-medium">{choice}</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <svg
                          className="w-5 h-5 text-gray-400"
                          fill="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path d="M3 18h18v-2H3v2zm0-5h18v-2H3v2zm0-7v2h18V6H3z" />
                        </svg>
                        <button
                          type="button"
                          onClick={() => {
                            const newRankings = Array.isArray(currentResponse)
                              ? [...currentResponse]
                              : [];
                            newRankings.splice(index, 1);
                            handleResponseChange(newRankings);
                          }}
                          className="text-red-500 hover:text-red-700 transition-colors"
                        >
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center text-gray-500 py-4">
                    Select items below to start ranking
                  </div>
                )}
              </div>
            </div>

            {/* Available choices */}
            <div>
              <div className="text-sm font-medium text-gray-700 mb-2">Available Choices:</div>
              <div className="space-y-2">
                {currentQuestion.choices?.map((choice: string, index: number) => {
                  const rankings = Array.isArray(currentResponse) ? currentResponse : [];
                  const isRanked = rankings.includes(choice);
                  const maxRank = currentQuestion.ranking_count || 3;
                  const canAdd = rankings.length < maxRank;

                  if (isRanked) return null; // Don't show already ranked items

                  return (
                    <button
                      key={index}
                      type="button"
                      onClick={() => {
                        if (canAdd) {
                          const newRankings = Array.isArray(currentResponse)
                            ? [...currentResponse]
                            : [];
                          newRankings.push(choice);
                          handleResponseChange(newRankings);
                        }
                      }}
                      disabled={!canAdd}
                      className={`w-full text-left px-4 py-3 rounded-lg border-2 transition-all duration-200 ${
                        canAdd
                          ? 'border-gray-300 bg-white text-gray-900 hover:border-gray-400 hover:bg-gray-50 cursor-pointer'
                          : 'border-gray-200 bg-gray-100 text-gray-400 cursor-not-allowed'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span>{choice}</span>
                        {canAdd && (
                          <svg
                            className="w-5 h-5 text-gray-400"
                            fill="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" />
                          </svg>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="text-xs text-gray-500 mt-2">
              Selected: {Array.isArray(currentResponse) ? currentResponse.length : 0} of{' '}
              {currentQuestion.ranking_count || 3}
            </div>
          </div>
        );

      case 'matrix_rating':
        // Use randomized rows if randomization is enabled, otherwise use original order
        const matrixRows =
          randomizedMatrixRows.length > 0
            ? randomizedMatrixRows
            : currentQuestion.matrix_rows || currentQuestion.choices || [];

        return (
          <div className="space-y-4">
            {renderSimpleQuestionMedia()}
            <div className="overflow-x-auto">
              <table className="w-full border-collapse border border-gray-300">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="text-left p-3 border border-gray-300 font-medium text-gray-700 min-w-[200px]">
                      {/* Empty header for row labels */}
                    </th>
                    {(currentQuestion.matrix_columns || ['1', '2', '3', '4', '5']).map(
                      (label, index) => (
                        <th
                          key={index}
                          className="text-center p-3 border border-gray-300 font-medium text-gray-700 min-w-[80px]"
                        >
                          {label}
                        </th>
                      ),
                    )}
                  </tr>
                </thead>
                <tbody>
                  {matrixRows.map((item, itemIndex) => (
                    <tr key={itemIndex} className="hover:bg-gray-50">
                      <td className="p-3 border border-gray-300 font-medium text-gray-900">
                        {item}
                      </td>
                      {(currentQuestion.matrix_columns || ['1', '2', '3', '4', '5']).map(
                        (_label, scaleIndex) => {
                          const scaleValue = currentQuestion.scale_min
                            ? currentQuestion.scale_min + scaleIndex
                            : scaleIndex + 1;
                          const matrixResponse = currentResponse || {};
                          const isSelected = matrixResponse[item] === scaleValue;

                          return (
                            <td key={scaleIndex} className="p-3 border border-gray-300 text-center">
                              <input
                                type="radio"
                                name={`matrix_${itemIndex}`}
                                value={scaleValue}
                                checked={isSelected}
                                onChange={() => {
                                  const newResponse = { ...matrixResponse };
                                  newResponse[item] = scaleValue;
                                  handleResponseChange(newResponse);
                                }}
                                className="w-4 h-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                              />
                            </td>
                          );
                        },
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );

      case 'max_diff': {
        // Best/worst mode (spec 36-02): one screen, every option, handled entirely by
        // BestWorstCard — a thin shell over bestWorst.ts's pure selection state machine. None
        // of the multi-set BIBD/image-gating machinery below applies (there are no "sets" —
        // `maxDiffSets` is empty for this mode, see its memo's own guard), so this returns
        // before any of it runs.
        if (isBestWorst) {
          const { n_best: nBest = 0, n_worst: nWorst = 0 } = currentQuestion.max_diff_config || {};

          // Gated like every option-media path, and keyed by the shared builder. ADR-0003 §3.
          const bestWorstImageUrl = (option: string): string | undefined => {
            const idx = currentQuestion.choices?.indexOf(option) ?? -1;
            const urls: string[] =
              idx < 0
                ? []
                : currentQuestion.option_media?.[String(idx)] ||
                  currentQuestion.option_media?.[idx] ||
                  [];
            // The first IMAGE, not the first url — a claim can hold a clip too.
            const url = urls.find((u) => u && !isVideoUrl(u));
            return url ? getFullMediaUrl(url) : undefined;
          };
          const bestWorstMedia = {
            imageUrlFor: bestWorstImageUrl,
            keyFor: (option: string) =>
              choiceOptionMediaKey(
                optionMediaScopeKey,
                currentQuestion.choices?.indexOf(option) ?? -1,
                0,
              ),
            loaded: optionMediaLoaded,
            errored: optionMediaErrored,
            timedOut: optionMediaTimedOut,
            onPainted: markOptionMediaPainted,
            onLoaded: markOptionMediaLoaded,
            onErrored: markOptionMediaErrored,
          };
          const hasBestWorstMedia = (currentQuestion.choices || []).some((o: string) =>
            bestWorstImageUrl(o),
          );

          return (
            <div className="space-y-6">
              {renderSimpleQuestionMedia()}
              <BestWorstCard
                /* The picks AND the stage are state INSIDE this card, so without an identity of
                   its own React reconciles one instance across a question boundary and carries
                   both over: two best/worst questions in a row opened the second one already
                   settled, with every control for its own options refused and its stored answer
                   made of the first question's option strings. Nothing else remounts the subtree
                   on a fast submit, and nothing does in preview mode at all. */
                key={currentQuestion.id}
                options={currentQuestion.choices || []}
                nBest={nBest}
                nWorst={nWorst}
                anchors={resolveAnchors(currentQuestion.max_diff_config)}
                media={hasBestWorstMedia ? bestWorstMedia : undefined}
                onSubmit={(value) => {
                  handleResponseChange(value);
                  handleSubmitResponse(value);
                }}
              />
            </div>
          );
        }

        // The balanced sets come from the memo at the top of the component, which builds them
        // from the same (question, session) pair. Rebuilding them here re-ran the BIBD heuristic
        // and serialised the multi-KB evaluation object on EVERY render — and this feature makes
        // a MaxDiff question re-render once per claim image that resolves, on the same phone
        // that is decoding those images.
        const multiSetAnchors = resolveAnchors(currentQuestion.max_diff_config);
        const currentSet = maxDiffSets[currentMaxDiffSetIndex] || [];
        const totalSets = maxDiffSets.length;

        // Per-item images. They live in the same `option_media` sidecar the choice types use,
        // keyed by the item's index in `choices` — maxDiffUtils shuffles the same strings, so
        // indexOf maps a displayed tile back to its storage slot. One image per tile: four items
        // are compared side by side and more than one picture each makes that unreadable.
        const currentSetItems = currentSet.map((item) => {
          const storageIndex = currentQuestion.choices?.indexOf(item) ?? -1;
          const urls: string[] =
            storageIndex < 0
              ? []
              : currentQuestion.option_media?.[String(storageIndex)] ||
                currentQuestion.option_media?.[storageIndex] ||
                [];
          // The first IMAGE, not the first url. `option_media` is shared with the choice types,
          // whose uploader accepts video and whose grid has no per-claim cap, so retyping a
          // single_choice question as max_diff can leave a claim holding a clip AND a pack shot
          // — very often the clip first, because that is the order they were uploaded in.
          // Taking urls[0] and then discarding it for being a video threw away the picture
          // stored right behind it, and the respondent ranked the claim from its text alone
          // while the builder was promising "only the first image is shown".
          //
          // A video is never rendered here in any form: it must not go through an <img> (that
          // 404s, resolves the gate and leaves an empty box), and a <video controls> is not the
          // answer either — the tile IS the ranking control, so a player inside it is
          // interactive content nested in a <button> and its play control would double as the
          // respondent's vote. Eight players (4 items × 2 columns) on one screen is a different
          // feature, not a bug fix. A claim holding NOTHING but video therefore still shows no
          // picture, and the builder flags exactly that case so it can be replaced.
          return {
            imageUrl: urls.find((url) => url && !isVideoUrl(url)),
            // The item coordinate of the paint slot: the STORAGE index, so a tile is identified
            // by its claim and not by where it sits on screen. The question and set coordinates
            // come from optionMediaScopeKey — the same scope the fail-open backstop derives, and
            // for the same reason — and the column is added per tile, since this item is about to
            // be rendered once in each. See MAX_DIFF_ITEM_MEDIA_KEY_PREFIX for the full model.
            // Scoped rather than cleared from an effect because a reset lands one render late and
            // that render is the failure.
            storageIndex,
          };
        });

        // Same reason the per-choice media path gates: an item image still in flight means a
        // respondent can rank packaging they never saw. Keyed per PAINT SLOT rather than reusing
        // the question-wide `loadedOptionMediaCount >= totalOptionMedia` check, since a set shows
        // only 4 of N items and shows each of them TWICE — that count could never be satisfied
        // here and the gate would fall through to its timeout on every set.
        //
        // EVERY slot on screen, which means both columns: the two copies of one claim are two
        // requests, and one of them having painted says nothing about the other. Waiting on the
        // item alone unlocked a tile that was still showing its spinner. Fails open.
        const maxDiffItemImagesReady =
          optionMediaTimedOut ||
          currentSetItems.every(
            (item) =>
              !item.imageUrl ||
              MAX_DIFF_COLUMNS.every(
                (column) =>
                  optionMediaLoaded[
                    maxDiffItemMediaKey(optionMediaScopeKey, item.storageIndex, column)
                  ],
              ),
          );

        // ...and the QUESTION-level stimulus gates as well. `max_diff` is in
        // MEDIA_SUPPORTED_TYPES, so a researcher can attach the pack shots as the question's
        // own `media_urls` (rendered by renderSimpleQuestionMedia below) instead of per item.
        // Gating only the per-item path would leave that researcher with the hole this feature
        // closes while believing it was closed. Same `stimulusReady` single_choice and
        // multiple_choice use, and it fails open on its own timeout.
        const maxDiffImagesReady = maxDiffItemImagesReady && stimulusReady;

        /**
         * One item tile. The same items are rendered in both columns, so `column` is what tells
         * this tile's <img> apart from the other column's copy of the same claim — its own
         * request, its own paint slot, its own verdict.
         */
        const renderMaxDiffTile = ({
          choice,
          index,
          isSelected,
          isTakenByOtherColumn,
          onSelect,
          column,
        }: {
          choice: string;
          index: number;
          isSelected: boolean;
          isTakenByOtherColumn: boolean;
          onSelect: () => void;
          column: MaxDiffColumn;
        }) => {
          const item = currentSetItems[index];
          const imageUrl = item?.imageUrl;
          const styles = MAX_DIFF_COLUMN_STYLES[column];
          const mediaKey = item
            ? maxDiffItemMediaKey(optionMediaScopeKey, item.storageIndex, column)
            : '';
          const isImageLoaded = optionMediaLoaded[mediaKey] === true;
          const hasImageFailed = optionMediaErrored[mediaKey] === true;

          return (
            <button
              key={index}
              type="button"
              disabled={isTakenByOtherColumn || !maxDiffImagesReady}
              onClick={onSelect}
              className={`w-full text-left px-4 py-3 rounded-lg border-2 transition-all duration-200 ${
                !maxDiffImagesReady
                  ? MAX_DIFF_TILE_LOCKED_CLASS
                  : isSelected
                    ? styles.selected
                    : isTakenByOtherColumn
                      ? MAX_DIFF_TILE_TAKEN_CLASS
                      : `border-gray-300 bg-white text-gray-900 ${styles.hover}`
              }`}
            >
              {imageUrl && (
                <div className={`${OPTION_MEDIA_CONTAINER_CLASS} mb-2`}>
                  {!isImageLoaded && !hasImageFailed && (
                    <div className="absolute inset-0 flex items-center justify-center bg-gray-100 animate-pulse">
                      <div className="w-6 h-6 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
                    </div>
                  )}
                  {/* Says so, instead of leaving a blank tile that reads as "this claim has no
                        picture". Sits above the broken image, same as the question-media path. */}
                  {hasImageFailed && (
                    <div className="absolute inset-0 z-20 flex items-center justify-center bg-gray-100 p-2">
                      <span className="text-xs text-gray-500 text-center">
                        {UI_TEXT.HUMAN_SURVEY.SESSION.ITEM_IMAGE_FAILED}
                      </span>
                    </div>
                  )}
                  <img
                    // The tile is keyed by display POSITION, so without an identity of its own
                    // this node is handed to a different claim on the next set and shows that
                    // claim's decoded frame until the new src arrives. Its identity is the
                    // media key, which is exactly what "which picture is this" means here.
                    key={mediaKey}
                    src={getFullMediaUrl(imageUrl)}
                    alt={`${choice} image`}
                    className={`${OPTION_MEDIA_IMG_CLASS} transition-opacity duration-300 ${isImageLoaded ? 'opacity-100' : 'opacity-0'}`}
                    // A cached image can settle before React attaches onLoad/onError, so the gate
                    // has to read the node itself — see readImageSettleState for what it can and
                    // cannot tell us. A settled node always resolves the gate; only a node that
                    // reported a size is PAINTED outright. One that reported none decides
                    // nothing by itself, so it is put to decode(), whose answer lands on THIS
                    // node's paint slot and so cannot speak for the other column's copy.
                    ref={(el) => {
                      const settle = readImageSettleState(el);
                      if (settle === IMAGE_SETTLE.PENDING) return;
                      if (settle === IMAGE_SETTLE.PAINTED) {
                        markOptionMediaPainted(mediaKey);
                        return;
                      }
                      markOptionMediaLoaded(mediaKey);
                      probeImageDecode(
                        el as HTMLImageElement,
                        () => markOptionMediaPainted(mediaKey),
                        () => markOptionMediaErrored(mediaKey),
                      );
                    }}
                    onLoad={() => markOptionMediaPainted(mediaKey)}
                    // Recorded against this node's paint slot rather than written onto the node,
                    // which an imperative hide would outlive. Still resolves the gate, so a dead
                    // image fails open instead of locking the respondent out.
                    onError={() => {
                      markOptionMediaErrored(mediaKey);
                      markOptionMediaLoaded(mediaKey);
                    }}
                  />
                </div>
              )}
              <div className="flex items-center justify-between">
                <span>{choice}</span>
                {isSelected && (
                  <div
                    className={`w-5 h-5 rounded-full flex items-center justify-center ${styles.check}`}
                  >
                    <Check className="w-3 h-3 text-white" />
                  </div>
                )}
              </div>
            </button>
          );
        };

        return (
          <div className="space-y-6">
            {renderSimpleQuestionMedia()}
            <div className="text-center mb-6">
              <div className="text-sm text-gray-500 mb-2">
                Set {currentMaxDiffSetIndex + 1} of {totalSets}
              </div>
              <div className="text-xs text-gray-600">
                Select the {multiSetAnchors.best.toLowerCase()} and{' '}
                {multiSetAnchors.worst.toLowerCase()} options • No right or wrong answers
              </div>
            </div>

            {!maxDiffImagesReady && (
              <p className="text-sm text-gray-500 italic text-center" role="status">
                {UI_TEXT.HUMAN_SURVEY.SESSION.LOADING_STIMULUS}
              </p>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Most Appealing Section */}
              <div className="border-2 border-green-200 rounded-lg p-4 bg-green-50">
                <h3 className="text-lg font-semibold text-green-800 mb-3 text-center">
                  {multiSetAnchors.best}
                </h3>
                <div className="space-y-2">
                  {currentSet.map((choice, index) => {
                    const response = currentResponse || {};
                    return renderMaxDiffTile({
                      choice,
                      index,
                      isSelected: response.most_appealing === choice,
                      isTakenByOtherColumn: response.least_appealing === choice,
                      onSelect: () => handleResponseChange({ ...response, most_appealing: choice }),
                      column: 'most',
                    });
                  })}
                </div>
              </div>

              {/* Least Appealing Section */}
              <div className="border-2 border-red-200 rounded-lg p-4 bg-red-50">
                <h3 className="text-lg font-semibold text-red-800 mb-3 text-center">
                  {multiSetAnchors.worst}
                </h3>
                <div className="space-y-2">
                  {currentSet.map((choice, index) => {
                    const response = currentResponse || {};
                    return renderMaxDiffTile({
                      choice,
                      index,
                      isSelected: response.least_appealing === choice,
                      isTakenByOtherColumn: response.most_appealing === choice,
                      onSelect: () =>
                        handleResponseChange({ ...response, least_appealing: choice }),
                      column: 'least',
                    });
                  })}
                </div>
              </div>
            </div>
          </div>
        );
      }

      case 'conjoint_analysis':
        return (
          <div className="space-y-6">
            {renderSimpleQuestionMedia()}
            {(() => {
              const attributes = currentQuestion.conjoint_attributes || [];

              if (attributes.length === 0) {
                return (
                  <div className="text-center text-gray-500 py-8">
                    No attributes configured for this conjoint analysis question.
                  </div>
                );
              }

              // Generate choice task (3 profiles per task is optimal). Shared helper so the
              // profiles shown here match the profile_details recovered on submit.
              const profilesPerTask = 3;
              const allProfiles = generateConjointProfiles(attributes);

              // Get current task index from state
              const currentTaskIndex = currentConjointScenarioIndex;

              // Select profiles for current choice task using systematic rotation
              const taskProfiles = [];
              for (let i = 0; i < profilesPerTask; i++) {
                const profileIndex = (currentTaskIndex * profilesPerTask + i) % allProfiles.length;
                taskProfiles.push(allProfiles[profileIndex]);
              }

              return (
                <div className="space-y-4">
                  {/* Choice task header */}
                  <div className="text-center">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      Which option would you be most likely to choose?
                    </h3>
                    <p className="text-sm text-gray-600">
                      Select the one option that best meets your preferences
                    </p>
                    <div className="text-sm text-gray-500 mt-2">
                      Scenario {currentConjointScenarioIndex + 1} of{' '}
                      {currentQuestion.conjoint_scenarios ||
                        Math.min(12, Math.max(8, attributes.length * 2))}
                    </div>
                  </div>

                  {/* Profile options */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {taskProfiles.map((profile, index) => {
                      const isSelected = currentResponse === profile.id;

                      return (
                        <div
                          key={profile.id}
                          className={`border-2 rounded-lg p-4 cursor-pointer transition-all duration-200 ${
                            isSelected
                              ? 'border-blue-500 bg-blue-50 shadow-md'
                              : 'border-gray-300 bg-white hover:border-blue-300 hover:shadow-sm'
                          }`}
                          onClick={() => handleResponseChange(profile.id)}
                        >
                          <div className="text-center mb-4">
                            <h4 className="font-semibold text-gray-900 text-lg">
                              Option {String.fromCharCode(65 + index)}
                            </h4>
                          </div>

                          <div className="space-y-3">
                            {attributes.map((attr) => (
                              <div
                                key={attr.name}
                                className="border-b border-gray-100 pb-2 last:border-b-0"
                              >
                                <div className="flex flex-col space-y-1">
                                  <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                                    {attr.name.replace(/_/g, ' ')}
                                  </span>
                                  <span className="text-sm font-semibold text-gray-900">
                                    {String(profile.attributes[attr.name]).replace(/_/g, ' ')}
                                  </span>
                                </div>
                              </div>
                            ))}
                          </div>

                          {isSelected && (
                            <div className="mt-4 flex justify-center">
                              <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center">
                                <Check className="w-5 h-5 text-white" />
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })()}
          </div>
        );

      case 'image_hotspot':
        // Build public image URL from question config
        const hotspotImageUrl = currentQuestion.image_url
          ? currentQuestion.image_url.startsWith('http')
            ? currentQuestion.image_url
            : `${import.meta.env.VITE_API_URL || ''}/api/question-images/public/${survey.id}/${currentQuestion.image_url.split('/').pop()}`
          : '';

        return (
          <div className="space-y-4">
            <ImageHotspotResponse
              imageUrl={hotspotImageUrl}
              boundingBoxes={currentQuestion.bounding_boxes || []}
              rankingCount={currentQuestion.ranking_count || 3}
              freeClickMode={currentQuestion.free_click_mode || false}
              fitToScreen={currentQuestion.fit_to_screen !== false}
              customSize={currentQuestion.custom_size || false}
              customWidthVw={currentQuestion.custom_width_vw}
              customHeightVh={currentQuestion.custom_height_vh}
              onComplete={(clicks) => {
                // Format response data for submission
                const responseData = { clicks };
                handleResponseChange(responseData);
              }}
              disabled={isSubmitting}
            />
          </div>
        );

      case 'info_screen':
        // Information display - no response needed, user just clicks continue
        // Use key to force remount when question changes, ensuring clean loading states
        return (
          <InfoScreenDisplay
            key={`info-screen-${currentQuestion.id || currentQuestionIndex}`}
            content={currentQuestion.text}
            description={currentQuestion.description}
            displayTimeSeconds={currentQuestion.display_time_seconds}
            mediaUrls={orderedQuestionMediaUrls}
            onContinue={() => {
              // Auto-submit with "viewed" response and move to next question.
              // Pass 'viewed' explicitly so the submitted payload doesn't depend on the
              // async currentResponse state update having committed (which captured a stale '').
              handleResponseChange('viewed');
              handleSubmitResponse('viewed');
            }}
            disabled={isSubmitting}
          />
        );

      case 'percentage_allocation':
        const allocOptions = currentQuestion.allocation_options || currentQuestion.choices || [];
        const allocTarget = currentQuestion.allocation_target || 100;
        const allocResponse = (currentResponse as Record<string, number>) || {};
        const autoFillLast = currentQuestion.auto_fill_last !== false; // Default true
        const allowZero = currentQuestion.allow_zero !== false; // Default true

        // Calculate current total
        const allocTotal = Object.values(allocResponse).reduce((sum, val) => sum + (val || 0), 0);

        // Get status color
        const getAllocStatusColor = () => {
          if (allocTotal > allocTarget) return 'text-red-600';
          if (allocTotal < allocTarget) return 'text-amber-600';
          return 'text-emerald-600';
        };

        const getAllocStatusBg = () => {
          if (allocTotal > allocTarget) return 'bg-red-50 border-red-200';
          if (allocTotal < allocTarget) return 'bg-amber-50 border-amber-200';
          return 'bg-emerald-50 border-emerald-200';
        };

        return (
          <div className="space-y-4">
            {renderSimpleQuestionMedia()}
            <div className="text-sm text-gray-600 mb-4">
              Allocate values across the options below.{' '}
              {autoFillLast
                ? 'The last option will be auto-calculated.'
                : `Total must equal ${allocTarget}.`}
            </div>

            <div className="space-y-3">
              {allocOptions.map((option: string, index: number) => {
                const isLastOption = autoFillLast && index === allocOptions.length - 1;
                const otherOptionsTotal = allocOptions
                  .slice(0, -1)
                  .reduce((sum: number, opt: string) => sum + (allocResponse[opt] || 0), 0);
                const autoValue = isLastOption
                  ? Math.max(0, allocTarget - otherOptionsTotal)
                  : null;
                const currentValue =
                  isLastOption && autoFillLast ? autoValue : allocResponse[option] || 0;

                return (
                  <div
                    key={index}
                    className="flex items-center justify-between gap-4 p-3 bg-white border border-gray-200 rounded-lg"
                  >
                    <span className="flex-1 text-gray-900 font-medium">{option}</span>
                    <div className="flex items-center gap-2">
                      {isLastOption ? (
                        <div className="flex items-center gap-2">
                          <span
                            className={`w-20 text-right font-semibold tabular-nums ${autoValue !== null && autoValue < 0 ? 'text-red-600' : 'text-gray-900'}`}
                          >
                            {autoValue !== null ? autoValue : 0}
                          </span>
                          <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-600 rounded">
                            Auto
                          </span>
                        </div>
                      ) : (
                        <>
                          <input
                            type="number"
                            min={allowZero ? 0 : 1}
                            max={allocTarget}
                            step={1}
                            value={currentValue || ''}
                            onChange={(e) => {
                              let val = parseInt(e.target.value) || 0;
                              // Enforce minimum of 1 when allow_zero is false
                              if (!allowZero && val < 1 && e.target.value !== '') {
                                val = 1;
                              }
                              // Initialize all options with 0 first, then spread existing values
                              const initializedResponse = allocOptions.reduce(
                                (acc: Record<string, number>, opt: string) => {
                                  acc[opt] = allocResponse[opt] || 0;
                                  return acc;
                                },
                                {} as Record<string, number>,
                              );
                              const newResponse = { ...initializedResponse, [option]: val };

                              // If auto_fill_last, calculate the last option's value
                              if (autoFillLast && allocOptions.length > 0) {
                                const lastOption = allocOptions[allocOptions.length - 1];
                                const otherTotal = allocOptions
                                  .slice(0, -1)
                                  .reduce((sum: number, opt: string) => {
                                    if (opt === option) return sum + val;
                                    return sum + (initializedResponse[opt] || 0);
                                  }, 0);
                                newResponse[lastOption] = Math.max(0, allocTarget - otherTotal);
                              }

                              handleResponseChange(newResponse);
                            }}
                            className="w-20 px-3 py-2 text-right border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent tabular-nums"
                            placeholder={allowZero ? '0' : '1'}
                          />
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Total indicator */}
            <div
              className={`flex items-center justify-between p-3 rounded-lg border ${getAllocStatusBg()}`}
            >
              <span className="font-medium text-gray-700">Total</span>
              <div className="flex items-center gap-2">
                <span className={`font-bold text-lg tabular-nums ${getAllocStatusColor()}`}>
                  {autoFillLast
                    ? allocOptions
                        .slice(0, -1)
                        .reduce((sum: number, opt: string) => sum + (allocResponse[opt] || 0), 0) +
                      Math.max(
                        0,
                        allocTarget -
                          allocOptions
                            .slice(0, -1)
                            .reduce(
                              (sum: number, opt: string) => sum + (allocResponse[opt] || 0),
                              0,
                            ),
                      )
                    : allocTotal}
                </span>
                <span className="text-gray-500">/ {allocTarget}</span>
                {allocTotal === allocTarget && (
                  <svg className="w-5 h-5 text-emerald-600" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                )}
              </div>
            </div>

            {/* Warning messages */}
            {allocTotal > allocTarget && (
              <p className="text-sm text-red-600">
                Total exceeds {allocTarget}. Please reduce some values.
              </p>
            )}
          </div>
        );

      case 'photo_upload':
        return (
          <PhotoUploadQuestion
            // Keyed so a new question REMOUNTS it. Same-type questions would otherwise reconcile
            // in place, keeping the previous question's images and its still-running poll alive.
            key={`photo-${currentQuestion?.id ?? currentQuestionIndex}`}
            questionId={currentQuestion.id}
            surveyId={survey.id}
            sessionId={session?.id || 0}
            questionText={currentQuestion.text}
            maxImages={currentQuestion.max_images || 3}
            onComplete={(response) => {
              handleResponseChange(response);
            }}
            disabled={isSubmitting}
          />
        );

      default:
        return null;
    }
  };

  return (
    <>
      <style>{`
        @keyframes gentleBounce {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-4px); }
        }
        @keyframes waveform {
          0%, 100% { 
            transform: scaleY(0.3);
            opacity: 0.6;
          }
          50% { 
            transform: scaleY(1);
            opacity: 1;
          }
        }
      `}</style>
      {/* AI redundancy-skip acknowledgement (auto-dismisses) */}
      {aiSkipNotice && aiSkipNotice.count > 0 && (
        <div
          role="status"
          aria-live="polite"
          className="fixed top-4 left-1/2 -translate-x-1/2 z-50 flex items-center gap-2 px-4 py-2 rounded-lg bg-[#E8FCFA] border border-[#07E5D1] text-[#1C1C1E] shadow-sm text-sm font-medium"
        >
          <Check className="w-4 h-4 text-[#06D4C2]" />
          {`We skipped ${aiSkipNotice.count} question${aiSkipNotice.count === 1 ? '' : 's'} you already answered.`}
        </div>
      )}
      <div className="min-h-screen bg-gray-50 py-8">
        <div
          className={`${currentQuestion?.type === 'image_hotspot' ? 'max-w-6xl' : 'max-w-4xl'} mx-auto px-4 transition-all duration-300`}
        >
          <div className="bg-white rounded-xl shadow-sm">
            {/* Preview Mode Banner */}
            {isPreviewMode && (
              <div className="px-6 py-2 bg-amber-50 border-b border-amber-200 text-center">
                <span className="text-xs font-medium text-amber-700">
                  Preview Mode — responses are not saved
                </span>
              </div>
            )}

            {/* Inside a rapid-fire block the countdown REPLACES the progress bar. Two competing
              progress indicators would fight for attention in the one section where attention is
              the whole point, and the survey-level percentage barely moves during it anyway. */}
            {/* Inside a rapid-fire block that block's own countdown owns the header, and the
              survey-level readout stands down — two clocks at once is noise. */}
            {!rapidFire?.started && deadline.showReadout && (
              <SurveyCountdown
                remaining={deadline.remaining}
                isFinal={deadline.isFinal}
                paused={deadline.paused}
              />
            )}
            {/* `!rapidFireOutro`: a cap close leaves time on the clock, and neither the deadline nor
              the question index is cleared — so without this the countdown ticks on behind the
              closing card and, on crossing the urgent threshold, MOUNTS RapidFireTimer's live
              region to announce a round that has already ended. */}
            {rapidFire?.started && !rapidFireOutro ? (
              <RapidFireTimer
                remaining={rapidFire.remaining}
                totalSeconds={rapidFire.durationSeconds}
                answered={rapidFire.answeredInBlock}
                label={rapidFireLabel}
              />
            ) : (
              !currentQuestion?.is_screener &&
              !showRapidFireIntro &&
              !showRapidFireBeat &&
              !rapidFireOutro && (
                <div className="px-6 pt-6 pb-4 border-b border-gray-200">
                  <div className="flex items-center justify-end mb-4">
                    <div className="text-sm font-medium text-gray-500">
                      {progress.percent}% Complete
                    </div>
                  </div>

                  {/* Progress Bar — percentage of the questions this respondent will actually be asked
                (estimated from conditional logic), so a branching survey never shows a scary raw total. */}
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${progress.percent}%` }}
                    />
                  </div>
                </div>
              )
            )}

            {/* Error Display */}
            {error && (
              <div className="mx-6 mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}

            {/* ABOVE everything that can stand in for the question body, and outside the rapid-fire
              intro, because the live region has to outlive all of them.
              It sat inside the body until now, and the failure was not the held-nudge case the
              hold logic covers — a held mark is released by an effect, so the region mounts one
              commit BEFORE its message either way. It was a nudge already SHOWING when a submit
              ran long: the spinner tore the region out from under a live announcement, and if the
              submit came home inside the eight-second window the region remounted with a non-null
              `message` in the SAME commit — the exact mount-with-its-first-message TimerNudge's
              own comment says is not reliably announced. Longer than eight seconds and the mark
              was spent behind the spinner and never seen at all.
              Nudges are still SILENCED inside a rapid-fire block — that section owns the screen
              and its whole point is undivided attention — but by having nothing to say, never by
              unmounting. */}
            <div className="px-6">
              <TimerNudge
                message={rapidFire?.started ? null : deadline.nudge}
                urgent={isNudgeUrgent(deadline.remaining)}
              />
            </div>

            {/* Rapid-fire intro: the clock starts on Start, never on arrival. */}
            {showRapidFireIntro && rapidFire && (
              <RapidFireIntro
                label={
                  (survey?.blocks || []).find((b) => String(b.id) === rapidFire.blockId)?.label ||
                  'Quick-fire round'
                }
                introText={rapidFireIntroText(rapidFire.config)}
                durationSeconds={rapidFire.durationSeconds}
                // Derived from the block the respondent is about to enter, so the card cannot
                // promise behaviour the block does not have.
                rules={rapidFireRules(
                  rapidFire.config,
                  (survey?.questions || [])
                    .filter(
                      (q: { block_id?: string | null }) =>
                        String(q.block_id ?? '') === rapidFire.blockId,
                    )
                    .map((q: { type?: string }) => q.type ?? ''),
                )}
                onStart={handleStartRapidFire}
                starting={isStartingRapidFire}
              />
            )}

            {/* The clock is still stopped here — `handleRapidFireBeatDone` is what starts it. */}
            {showRapidFireBeat && rapidFire && (
              <RapidFireBeat label={rapidFireLabel} onDone={handleRapidFireBeatDone} />
            )}

            {rapidFireOutro && (
              <RapidFireOutro
                label={rapidFireOutro.label}
                reason={rapidFireOutro.reason}
                answered={rapidFireOutro.answered}
                notReached={rapidFireOutro.notReached}
                onContinue={handleRapidFireContinue}
              />
            )}

            {/* Question Content */}
            {!showRapidFireIntro && !showRapidFireBeat && !rapidFireOutro && (
              <div className="px-6 py-8">
                {!isAnalyzing && !isCheckingSkip && !showThinking ? (
                  <>
                    <div className="mb-8">
                      {/* The parser never emits a section's stimulus as its own question, so this is
                      the only route it has to the respondent's screen. */}
                      <SurveySectionStimulus
                        context={currentQuestion.section_context}
                        title={currentQuestion.section_title}
                      />

                      {/* Hide question text header for info_screen since InfoScreenDisplay shows it */}
                      {/* `ai_dynamic_section` joins info_screen in owning its own heading: it has
                      no authored respondent text BY DESIGN (AiDynamicSectionSettings: "goal +
                      topics + rubric only"), so whatever sits in `text` is researcher framing —
                      and the runner writes the respondent's real question itself. Rendering it
                      here put the researcher's own words on a consumer's screen. */}
                      {currentQuestion.type !== 'info_screen' &&
                        currentQuestion.type !== 'ai_dynamic_section' && (
                          <QuestionText className="text-xl font-semibold text-gray-900 mb-6">
                            {/* The multi-select cap is surfaced HERE rather than written into the stored
                          text: it is editable, so baking "select up to 3" into question.text would
                          leave a stale hint the moment the author changed it — and would corrupt
                          the text every export and analysis reads. A follow-up prompt is its own
                          free-text question and carries no cap. */}
                            {followupQuestion ||
                              questionTextWithCap(
                                adaptedQuestions[currentQuestion.id] || currentQuestion.text,
                                multiSelectCap,
                              )}
                          </QuestionText>
                        )}

                      {/* Once the server's buzzer has gone, every control in here is a dead no-op:
                      `handleSubmitResponse` returns immediately on `timerExpiredByServer`. Nothing
                      SAID so — the red banner appeared above a form that still looked live, and a
                      respondent could press Next or an option as often as they liked with no
                      response of any kind, normally for the seconds before /timer/expire lands and
                      on a flaky connection for up to the five minutes the retry is bounded to.
                      `ConversationalSurveyFlow` has disabled its textarea, Send and mic off the
                      same fact since it was written; this is the traditional body matching it.

                      ONE fieldset rather than `|| timerExpiredByServer` on eleven controls: it
                      covers every question type, including the ones that auto-submit straight from
                      an option, and the ones added after this. `pointer-events-none` is for the
                      image-choice options, which are `<div onClick>` and so not form controls at
                      all. `min-w-0` cancels the `min-inline-size: min-content` a fieldset brings
                      with it, which is the one default Tailwind's preflight does not reset and
                      would otherwise stop wide content wrapping. */}
                      <fieldset
                        disabled={timerExpiredByServer}
                        className={`min-w-0 ${timerExpiredByServer ? 'pointer-events-none opacity-60' : ''}`}
                      >
                        {renderQuestionInput()}
                      </fieldset>
                    </div>

                    {/* Navigation Buttons - hidden for info_screen since it has its own Continue
                    button, and for best/worst mode (spec 36-02) since BestWorstCard renders its
                    OWN Next button gated on isComplete. Without this exclusion, canProceed's
                    max_diff case has nothing valid to key its old {most_appealing,
                    least_appealing} shape off in this mode, so this button would sit permanently
                    disabled right next to BestWorstCard's own — two Next affordances at once. */}
                    {/* An ai_dynamic_section owns its whole turn loop INCLUDING its own Continue
                    button, exactly as best/worst owns its own — so this outer nav would be a
                    second, competing button on the same screen. A respondent saw both. */}
                    {currentQuestion.type !== 'info_screen' &&
                      !isBestWorst &&
                      currentQuestion.type !== 'ai_dynamic_section' && (
                        <div className="flex justify-between">
                          {isPreviewMode ? (
                            <button
                              type="button"
                              onClick={handlePreviewSkip}
                              className="px-4 py-2 text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                            >
                              Skip
                            </button>
                          ) : rapidFire?.started && rapidFire.config.allow_skip ? (
                            /* Only when the author turned it on. In a timed block skip is an escape from
                         burning the clock, not from answering — the respondent moves on instead of
                         watching seconds drain on a question they can't answer. */
                            <button
                              type="button"
                              onClick={handleRapidFireSkip}
                              // `handleRapidFireSkip` returns silently past the buzzer, so leaving this
                              // live left a respondent tapping Skip at a block that had stopped moving.
                              disabled={isSubmitting || timerExpiredByServer}
                              className="px-4 py-2 text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors disabled:opacity-50"
                            >
                              Skip
                            </button>
                          ) : spokenUndoAvailable ? (
                            /* A spoken answer moved the respondent on by itself; for the length of
                               UNDO_WINDOW_MS they can go back and say it again. Without this the
                               stop-is-the-submit change would have taken away the only way to redo
                               a bad take — the mic re-tap they used to have while the transcript
                               came back. Same navigation as the rapid-fire Undo, and like it it
                               calls no API: the row already written is left exactly as it is. */
                            <button
                              type="button"
                              onClick={handleSpokenUndo}
                              className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                            >
                              <RotateCcw className="w-3.5 h-3.5" aria-hidden="true" />
                              <span>
                                {UI_TEXT.HUMAN_SURVEY.SESSION.MESSAGES.UNDO_SPOKEN_ANSWER}
                              </span>
                            </button>
                          ) : (
                            <div />
                          )}
                          <Button
                            variant="primary"
                            onClick={() => handleSubmitResponse()}
                            // Also gated on the stimulus: single_choice submits from the option button,
                            // but every other type (multiple_choice, rating, ranking, matrix, text...)
                            // submits from HERE, outside the option fieldset. Without this, a respondent
                            // carrying a non-empty currentResponse — e.g. a restored/persisted answer —
                            // could advance past a question whose image had not painted yet.
                            // ...and on the buzzer, which is the one condition here that is about the
                            // SESSION rather than about this question: past it the submit is a no-op, so
                            // a live Next button is an affordance with nothing behind it.
                            disabled={
                              !canProceed ||
                              isSubmitting ||
                              isAnalyzing ||
                              !stimulusReady ||
                              timerExpiredByServer
                            }
                            icon={
                              followupQuestion ? (
                                <ArrowRight className="w-4 h-4" />
                              ) : isLastQuestion ? (
                                <Check className="w-4 h-4" />
                              ) : (
                                <ArrowRight className="w-4 h-4" />
                              )
                            }
                          >
                            {isSubmitting
                              ? UI_TEXT.HUMAN_SURVEY.SESSION.BUTTONS.SUBMITTING
                              : followupQuestion
                                ? UI_TEXT.BUTTONS.CONTINUE
                                : UI_TEXT.HUMAN_SURVEY.SESSION.BUTTONS.NEXT}
                          </Button>
                        </div>
                      )}
                  </>
                ) : (
                  <SurveyAnalyzingIndicator
                    accent={isCheckingSkip ? 'teal' : 'blue'}
                    message={
                      isCheckingSkip
                        ? 'Checking your answers…'
                        : isAnalyzing
                          ? analyzingMessage
                          : thinkingMessage
                    }
                    detail={
                      showThinking && !isAnalyzing && !isCheckingSkip
                        ? 'Preparing your next question…'
                        : undefined
                    }
                  />
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default SurveySession;
